// The component tier of the Midnight Mint theme: one token block per component
// in the component packages, keyed by the component's own name.
//
// This is PrimeVue's third token tier, applied to components PrimeVue does not
// know about. PrimeVue's own `components` branch cannot hold them: that branch
// is PULL-based, emitted only when a widget's style loader asks for its block by
// name, and nothing would ever ask for `saleCard`. `extend` is push-based —
// emitted once, unconditionally, under `:root,:host` at theme registration.
//
// NO PLATFORM SEGMENT, deliberately. The theme describes the brand, not the
// build target: a desktop and a mobile SaleCard are one brand object and share
// one token block. Where their VALUES need to differ, that is a second preset
// layered on this one with `definePreset`, which inherits every token it does
// not restate — so the split costs nothing to defer and duplicates nothing.
//
// NO EXCEPTIONS IN HERE, and keeping it that way is the point. A component whose
// name is also a PrimeVue widget name emits the SAME custom property PrimeVue
// does — a variable name carries no delimiter between a widget's name and the
// rest of its path — at the same `:root,:host` specificity, so whichever
// stylesheet loads later silently wins. `Drawer` was the one such component and
// is now PrimeVue's own Drawer, brand-themed under `components.drawer` in
// index.mjs. `ComponentTokenContract.test.mjs` fails on the next one.
//
// EVERY TOKEN IN THE BLOCK BELOW SITS FLAT, and that is correct rather than an
// oversight. A `{…}` reference is emitted by PrimeVue as a live `var()` —
// `'{content.background}'` becomes
// `--p-sale-card-background: var(--p-content-background)` — so a token pointing
// at a scheme-aware semantic token is scheme-aware for free.
//
// A token that genuinely must differ per scheme goes in
// `componentTokensByColorScheme` at the foot of this file, NEVER in a
// `colorScheme` branch nested inside a component's block. PrimeVue splits
// `colorScheme` only where it sits directly under a preset branch; nested
// deeper it is flattened, so both values land in `:root,:host` together and the
// second one wins in BOTH schemes. Nothing fails — the variable is declared, it
// resolves, and the component is simply the wrong color in one scheme.
//
// Generated once from the components' own CSS, then owned by hand. The naming
// rule, the property set and the gates are in
// knowledge-base/standards/component-token-contract.md.

export const componentTokens = {
    // ActionList — .action-list
    //
    // Every value here was Sheet's until the list came out of it. They are
    // unchanged, because the rows did not change — only which component's name
    // keys them, which is exactly what the token contract promises costs
    // nothing when a component moves.
    actionList: {
        gap: '4px',
        row: {
            gap: '12px',
            padding: '12px 8px',
            background: 'transparent',
            borderRadius: '14px',
            color: '{text.color}',
            hover: {
                background: '{content.background}',
            },
            destructive: {
                color: '{status.danger}',
                rowIcon: {
                    background: 'color-mix(in srgb, var(--p-status-danger) 15%, transparent)',
                    color: '{status.danger}',
                },
            },
        },
        rowContent: {
            gap: '12px',
        },
        rowIcon: {
            borderRadius: '18px',
            background: '{content.hoverBackground}',
            icon: {
                fontSize: '14px',
            },
        },
        rowText: {
            gap: '2px',
        },
        rowLabel: {
            fontSize: '15px',
            fontWeight: '600',
        },
        rowSublabel: {
            fontSize: '12px',
            fontWeight: '400',
            color: '{text.hoverMutedColor}',
        },
        rowDisclosure: {
            color: '{text.mutedColor}',
            fontSize: '12px',
        },
        empty: {
            padding: '28px 0',
            color: '{text.mutedColor}',
            fontSize: '13px',
        },
    },

    // AnnouncementToast — .announcement-toast
    //
    // The toast that drops in over the top of a screen, from COMPONENTS.md,
    // whose numbers are the Chat Prototype's toast to the pixel: `10px 12px`
    // inside a 1px hairline, a 10px gap, the token map's locked 16px inline-card
    // corner, and a shadow in black at half strength, as Sheet's is. The ground
    // is the raised ground at 97%: the design's `rgba(28,28,30,0.97)` is the
    // token map's raised row, and near enough opaque that a bright stream behind
    // it never reaches the words.
    //
    // The kind carries the accent. The sale desk speaks in the danger hue — the
    // hairline at 50%, the title and "View" — and a person in the brand accent,
    // the hairline at 45%, the title and "Reply". The desk's 34px flag plate
    // takes the hue at 14% with the flag in the hue itself at 14px, the plate
    // recipe of AttentionItem and SaleDeskStrip, at the token map's locked 10px
    // corner for a 32 to 36px plate. A person's avatar is AvatarChip, which owns
    // its own tokens.
    //
    // The title is 12/800, the text 13.5px on a 1.35 line, and the action
    // 12.5/700. "Now" is 11px in the secondary ink, a step up from the design's
    // tertiary: on this raised ground the tertiary measures 4.50:1 in dark
    // (4.33:1 over a white stream) and 4.18:1 in light, under the 4.5:1 its
    // 11px needs, where the secondary measures 6.17:1 and 7.01:1. Under the
    // pointer the ground mixes 8% of the ink into itself, which lifts it in dark
    // and deepens it in light, as the notices sheet's bell does; keyboard focus
    // draws the library's 2px accent ring.
    announcementToast: {
        card: {
            gap: '10px',
            padding: '10px 12px',
            borderWidth: '1px',
            borderRadius: '16px',
            background: 'color-mix(in srgb, var(--p-content-hover-background) 97%, transparent)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
            color: '{text.color}',
            hover: {
                background: 'color-mix(in srgb, color-mix(in srgb, var(--p-content-hover-background), var(--p-text-color) 8%) 97%, transparent)',
            },
            focus: {
                outlineColor: '{primary.color}',
            },
        },
        desk: {
            card: {
                borderColor: 'color-mix(in srgb, var(--p-status-danger) 50%, transparent)',
            },
            title: {
                color: '{status.danger}',
            },
            action: {
                color: '{status.danger}',
            },
        },
        message: {
            card: {
                borderColor: 'color-mix(in srgb, var(--p-primary-color) 45%, transparent)',
            },
            title: {
                color: '{primary.color}',
            },
            action: {
                color: '{primary.color}',
            },
        },
        plate: {
            borderRadius: '10px',
            background: 'color-mix(in srgb, var(--p-status-danger) 14%, transparent)',
            color: '{status.danger}',
        },
        icon: {
            fontSize: '14px',
        },
        head: {
            gap: '8px',
        },
        title: {
            fontSize: '12px',
            fontWeight: '800',
        },
        age: {
            fontSize: '11px',
            color: '{text.hoverMutedColor}',
        },
        text: {
            fontSize: '13.5px',
            lineHeight: '1.35',
        },
        action: {
            fontSize: '12.5px',
            fontWeight: '700',
        },
    },
    assetRail: {
        gap: '10px',
        // How wide one asset is in the row. The rail states it rather than the
        // tile, because it is a decision about the ROW — how many assets a
        // dealer sees before scrolling — and the same tile is a full-width
        // card elsewhere.
        itemWidth: '208px',
        head: {
            gap: '10px',
        },
        titles: {
            gap: '3px',
        },
        eyebrow: {
            color: '{text.mutedColor}',
            fontSize: '9.5px',
            fontWeight: '800',
            letterSpacing: '0.07em',
        },
        title: {
            fontFamily: '{font.display}',
            fontSize: '15px',
            fontWeight: '{display.weight}',
            letterSpacing: '{display.tracking}',
            color: '{text.color}',
        },
        link: {
            gap: '4px',
            padding: '0',
            color: '{text.mutedColor}',
            fontSize: '10.5px',
            fontWeight: '600',
            icon: {
                fontSize: '8px',
            },
            hover: {
                color: '{primary.color}',
            },
        },
        controls: {
            gap: '5px',
        },
        arrow: {
            borderWidth: '1px',
            borderColor: '{content.borderColor}',
            borderRadius: '6px',
            background: '{content.background}',
            color: '{text.mutedColor}',
            fontSize: '9px',
            hover: {
                background: '{content.hoverBackground}',
            },
        },
        track: {
            gap: '10px',
        },
    },

    assetTile: {
        background: '{content.background}',
        color: '{text.color}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.row}',
        hover: {
            borderColor: '{primary.color}',
        },
        photo: {
            background: '{content.hoverBackground}',
            color: '{text.mutedColor}',
            glyph: {
                fontSize: '22px',
            },
        },
        run: {
            padding: '1px 4px',
            borderRadius: '3px',
            background: 'rgba(0, 0, 0, 0.72)',
            color: '{surface.0}',
            fontFamily: '{font.mono}',
            fontSize: '8px',
            fontWeight: '700',
        },
        body: {
            gap: '3px',
            padding: '9px 10px 4px',
        },
        name: {
            fontSize: '12px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
        },
        meta: {
            fontSize: '10px',
            color: '{text.mutedColor}',
        },
        foot: {
            gap: '8px',
            paddingTop: '6px',
        },
        price: {
            fontFamily: '{font.mono}',
            fontSize: '12.5px',
            fontWeight: '700',
        },
        // `auctionLight`, never `light` — see the hero's note above.
        auctionLight: {
            borderRadius: '50%',
            boxShadow: '0 0 0 0.5px rgba(0, 0, 0, 0.4) inset',
            red: { background: '{auctionLight.red}' },
            green: { background: '{auctionLight.green}' },
            yellow: { background: '{auctionLight.yellow}' },
            blue: { background: '{auctionLight.blue}' },
            white: { background: '{auctionLight.white}' },
        },
        track: {
            gap: '5px',
            padding: '0 10px 10px',
            color: '{text.mutedColor}',
            fontSize: '10px',
            fontWeight: '700',
            icon: {
                fontSize: '9px',
            },
            hover: {
                color: '{primary.color}',
            },
            on: {
                color: '{primary.color}',
            },
        },
    },


    // BuyerAssetRail — .buyer-asset-rail
    //
    // A titled row of BuyerAssetTiles that scrolls sideways, from COMPONENTS.md's
    // BuyerAssetRail and FeaturedRail and the handoff's two frames of them: the head
    // 10px over the tiles, as the BuyerAssetRail frame stacks them, and the tiles 10px
    // apart with the page's 20px gutter either side, which is also the inset
    // each tile snaps to. The head takes the same 20px, so its title stands over
    // the first tile.
    //
    // The title is COMPONENTS.md's 15/700 in the primary ink, where the BuyerAssetRail
    // frame sets 17/700 at -0.01em; the FeaturedRail frame draws no head at all.
    // The link is the frame's 13/700 in the brand accent, a step along the
    // accent's ramp under the pointer and a second step pressed. Those deltas
    // are open under MBP-16.
    buyerAssetRail: {
        gap: '10px',
        // The head is SectionHeader's, which brings its own type and ink; the
        // rail only insets it over the first tile.
        head: {
            padding: '0 20px',
        },
        tiles: {
            gap: '10px',
            padding: '0 20px',
        },
    },

    // BuyerAssetTile — .buyer-asset-tile
    //
    // One asset in a rail, from COMPONENTS.md and the handoff's FeaturedRail
    // frame: 150 wide (the width is the SFC's), the preset's card ground with the
    // 0.5px hairline and DESIGN_SYSTEM §4's 14px tile corner, `{radius.row}`. The
    // photo's box is the raised ground with the glyph in the tertiary ink, and
    // the token map's locked 8px thumbnail corner, where the frame draws the
    // photo square and lets the tile's corner clip it; that delta is open under
    // MBP-16. The body is `8px 10px`: the title 13/700 in the primary ink, and on
    // a featured tile the MMR at 11px in the info hue with its figure mono 700,
    // as the MMR is everywhere in the flow.
    //
    // Under the pointer the ground lifts one step, to the raised ground; keyboard
    // focus is a 2px ring in the accent.
    buyerAssetTile: {
        background: '{content.background}',
        borderWidth: '0.5px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.row}',
        color: '{text.color}',
        hover: {
            background: '{content.hoverBackground}',
        },
        focus: {
            outlineColor: '{primary.color}',
        },
        media: {
            // Josh lock (2026-09-25 via Flynn): square photo bottom against the
            // card body; top keeps the locked 8px thumbnail corner (tile clips
            // the top two to its own 14px).
            borderRadius: '8px 8px 0 0',
            background: '{content.hoverBackground}',
            color: '{text.mutedColor}',
        },
        body: {
            padding: '8px 10px',
        },
        title: {
            fontSize: '13px',
            fontWeight: '700',
        },
        mmr: {
            color: '{status.info}',
            fontSize: '11px',
        },
        mmrAmount: {
            fontFamily: '{font.mono}',
            fontWeight: '700',
        },
        // Browse variant (MBP-32 / COMPONENTS AssetTile): save glass, countdown
        // chip, where/how-far line, and current bid. Featured leaves these unread.
        save: {
            size: '32px',
            background: 'rgba(0, 0, 0, 0.42)',
            color: '{surface.0}',
            borderRadius: '999px',
            fontSize: '12px',
            focus: {
                outlineColor: '{primary.color}',
            },
            // Saved: a white heart on a red plate, as everywhere in the app.
            on: {
                background: '{saved.background}',
                color: '{saved.color}',
            },
        },
        countdown: {
            padding: '2px 6px',
            borderRadius: '6px',
            background: 'rgba(0, 0, 0, 0.6)',
            color: '{surface.0}',
            fontFamily: '{font.mono}',
            fontSize: '11px',
            fontWeight: '700',
        },
        where: {
            color: '{text.mutedColor}',
            fontSize: '11px',
        },
        bid: {
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '13px',
            fontWeight: '800',
        },
    },

    // DateBadge — .date-badge (a sale row's calendar tile)
    //
    // 40x40 at radius 10, the Dashboard's measurements: Home drew the same tile
    // at 36 and 8, which is the drift one component ends. The day is mono and
    // tabular so `8` lines up under `27` down a list.
    dateBadge: {
        borderRadius: '10px',
        background: '{content.hoverBackground}',
        live: {
            background: 'color-mix(in srgb, var(--p-primary-color) 18%, transparent)',
        },
        top: {
            color: '{primary.color}',
            fontSize: '9.5px',
            fontWeight: '800',
            letterSpacing: '0.06em',
        },
        day: {
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '16px',
            fontWeight: '800',
        },
    },

    // FollowToggle — .follow-toggle (the round + / heart at the end of a row)
    //
    // The glyph carries the treatment, because the two are different marks. The
    // `plus` pair is an outlined circle that fills with the brand when the dealer
    // follows a sale. The `heart` pair has no outline and turns RED when on,
    // which is what saved looks like on every other surface (QA H1) — a mint
    // plate there would make one screen's saved mark unlike all the others.
    followToggle: {
        borderRadius: '999px',
        background: '{content.hoverBackground}',
        plus: {
            borderWidth: '1px',
            borderColor: '{content.borderColor}',
            color: '{text.hoverMutedColor}',
            on: {
                borderColor: '{primary.color}',
                background: '{primary.color}',
                color: '{primary.contrastColor}',
            },
        },
        heart: {
            color: '{text.color}',
            // Saved: a white heart on a red plate, as everywhere in the app.
            on: {
                background: '{saved.background}',
                color: '{saved.color}',
            },
        },
        focus: {
            outlineColor: '{primary.color}',
        },
        icon: {
            fontSize: '15px',
        },
    },

    // InlineHint — .inline-hint (one line of guidance over a list)
    //
    // Two tones. `neutral` is the quiet aside under a search field and brings no
    // plate at all. `info` is the hint a screen does not work without, on a tint
    // of the brand rather than a surface step, so it reads as the app talking
    // rather than as another card.
    inlineHint: {
        gap: '8px',
        color: '{text.mutedColor}',
        fontSize: '12px',
        lineHeight: '1.45',
        icon: {
            color: '{primary.color}',
            fontSize: '13px',
        },
        strong: {
            color: '{text.hoverMutedColor}',
            fontWeight: '600',
        },
        info: {
            padding: '13px 14px',
            borderWidth: '1px',
            borderColor: 'color-mix(in srgb, var(--p-primary-color) 16%, transparent)',
            borderRadius: '{radius.card}',
            background: 'color-mix(in srgb, var(--p-primary-color) 8%, transparent)',
            color: '{text.hoverMutedColor}',
            fontSize: '14px',
            icon: {
                fontSize: '18px',
            },
            strong: {
                color: '{text.color}',
                fontWeight: '700',
            },
        },
    },

    // SegmentTabs — .segment-tabs (the row of segments over a screen's lists)
    //
    // Two shapes, and `scrollable` picks between them. `fitted` is one bordered
    // track with the segments sharing its width, which is what Purchases and Pay
    // & Finalize drew. `scrolling` is a strip of outlined pills that runs past
    // the screen's edges with a tally disc on each, which is what the board drew.
    // Neither takes PrimeVue's underline: the chosen segment's own fill says
    // which one is showing.
    segmentTabs: {
        gap: '10px',
        tab: {
            gap: '6px',
            fontSize: '13px',
            fontWeight: '700',
        },
        fitted: {
            bar: {
                padding: '3px',
                borderWidth: '1px',
                borderColor: '{formField.borderColor}',
                borderRadius: '{borderRadius.xl}',
                background: '{content.hoverBackground}',
            },
            list: {
                gap: '2px',
            },
            tab: {
                padding: '0 8px',
                borderRadius: '10px',
                color: '{text.hoverMutedColor}',
                hover: {
                    background: 'color-mix(in srgb, var(--p-text-color) 6%, transparent)',
                    color: '{text.color}',
                },
                active: {
                    background: '{primary.color}',
                    color: '{primary.contrastColor}',
                    focus: {
                        outlineColor: '{primary.contrastColor}',
                    },
                },
                focus: {
                    outlineColor: '{primary.color}',
                },
            },
        },
        scrolling: {
            list: {
                gap: '6px',
                padding: '4px 20px',
            },
            tab: {
                padding: '0 12px',
                borderWidth: '0.5px',
                borderColor: 'color-mix(in srgb, var(--p-text-color) 14%, transparent)',
                borderRadius: '999px',
                background: '{content.background}',
                color: '{text.hoverMutedColor}',
                hover: {
                    background: '{content.hoverBackground}',
                },
                active: {
                    background: '{primary.color}',
                    color: '{primary.contrastColor}',
                },
                focus: {
                    outlineColor: '{primary.color}',
                },
            },
        },
        count: {
            padding: '0 6px',
            borderRadius: '11px',
            background: 'color-mix(in srgb, var(--p-text-color) 16%, transparent)',
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '12.5px',
            fontWeight: '800',
            active: {
                background: '{primary.contrastColor}',
                color: '{primary.color}',
            },
        },
    },

    // StatusToast — .status-toast (the short message across a screen's top)
    //
    // The tone moves the RING and nothing else. A toast is never a coloured card:
    // the words carry the meaning, because a ring alone does not survive
    // grayscale.
    statusToast: {
        card: {
            gap: '10px',
            padding: '12px 12px 12px 14px',
            borderWidth: '1px',
            borderRadius: '{borderRadius.xl}',
            background: 'color-mix(in srgb, var(--p-content-background) 92%, transparent)',
            boxShadow: '0 8px 24px color-mix(in srgb, var(--p-surface-950) 50%, transparent)',
            neutral: {
                borderColor: '{content.borderColor}',
            },
            success: {
                borderColor: '{primary.color}',
            },
            error: {
                borderColor: '{status.danger}',
            },
        },
        message: {
            color: '{text.color}',
            fontSize: '14px',
            fontWeight: '600',
            // Read ONLY by the opt-in `wrap` variant: one line has no leading
            // to move, so the default toast never asks for this. An absolute
            // length rather than a ratio, because the ✕ and the action read it
            // too — they are offset by half the difference between the first
            // line's box and their own, which a unitless number cannot
            // express.
            wrap: {
                lineHeight: '20px',
            },
        },
        action: {
            padding: '0 16px',
            borderRadius: '999px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontSize: '13px',
            fontWeight: '800',
        },
        dismiss: {
            color: '{text.mutedColor}',
            fontSize: '13px',
            focus: {
                outlineColor: '{primary.color}',
            },
        },
    },

    // SectionHeader — .section-header (the line above a list)
    //
    // Ten screens wrote this by hand at six different type sizes. The two shapes
    // are the heading LEVEL: a section's own name is a title (level 2), a group
    // inside one is a small uppercase label (level 3), and the modifier is a word
    // rather than the digit because a token path may not carry one.
    sectionHeader: {
        gap: '12px',
        titles: {
            gap: '2px',
        },
        titleRow: {
            gap: '8px',
        },
        eyebrow: {
            color: '{text.mutedColor}',
            fontSize: '10.5px',
            fontWeight: '800',
            letterSpacing: '0.08em',
        },
        // The Home rails' head (BuyerAssetRail draws this header), so every
        // section on Home titles itself alike.
        title: {
            color: '{text.color}',
            fontSize: '15px',
            fontWeight: '700',
            letterSpacing: '0',
        },
        label: {
            color: '{text.mutedColor}',
            fontSize: '11px',
            fontWeight: '800',
            letterSpacing: '0.1em',
        },
        meta: {
            color: '{text.mutedColor}',
            fontFamily: '{font.mono}',
            fontSize: '11.5px',
        },
        link: {
            gap: '4px',
            color: '{primary.color}',
            fontSize: '13px',
            fontWeight: '700',
            hover: {
                color: '{primary.hoverColor}',
            },
            active: {
                color: '{primary.activeColor}',
            },
            focus: {
                outlineColor: '{primary.color}',
            },
            icon: {
                fontSize: '12px',
            },
        },
    },


    // DealershipPicker — .dealership-picker (who the dealer bids as)
    //
    // Two layouts: `strip` is BidSheet's horizontal chips (migrated from
    // bidSheet.dealership*), `list` is the Dashboard / VideoView sheet rows.
    dealershipPicker: {
        focus: {
            outlineColor: '{primary.color}',
        },
        name: {
            fontSize: '13px',
            fontWeight: '700',
        },
        meta: {
            fontSize: '11px',
            color: '{text.color}',
            fontFamily: '{font.mono}',
        },
        strip: {
            gap: '8px',
            marginBottom: '14px',
            item: {
                gap: '9px',
                maxWidth: '240px',
                padding: '8px 12px 8px 8px',
                borderRadius: '12px',
                background: '{content.hoverBackground}',
                borderColor: '{content.borderColor}',
                // The chosen dealership is the one a bid commits under, so it
                // must read at a glance: the chip inverts — text-color fill,
                // surface-color ink — which no other chip in the row is. Not
                // mint: mint stays on the bid control.
                selected: {
                    background: '{text.color}',
                    borderColor: '{text.color}',
                    color: '{content.background}',
                },
            },
        },
        list: {
            gap: '8px',
            item: {
                gap: '12px',
                padding: '12px 14px',
                borderRadius: '14px',
                background: '{content.background}',
                borderColor: '{content.borderColor}',
                selected: {
                    background: 'color-mix(in srgb, var(--p-primary-color) 10%, transparent)',
                    borderColor: 'color-mix(in srgb, var(--p-primary-color) 40%, transparent)',
                },
            },
            name: {
                fontSize: '15px',
            },
            meta: {
                color: '{text.mutedColor}',
            },
        },
        check: {
            borderColor: '{formField.borderColor}',
            on: {
                background: '{primary.color}',
                borderColor: '{primary.color}',
                color: '{primary.contrastColor}',
            },
        },
        item: {
            color: '{text.color}',
        },
    },

    // BiddingAsChip — .bidding-as-chip (trigger that opens DealershipPicker)
    biddingAsChip: {
        focus: {
            outlineColor: '{primary.color}',
        },
        chevron: {
            fontSize: '9px',
        },
        brief: {
            gap: '6px',
            padding: '0 10px',
            borderColor: '{formField.borderColor}',
            background: '{content.hoverBackground}',
            color: '{text.color}',
            fontSize: '11.5px',
            fontWeight: '700',
            icon: {
                fontSize: '10px',
            },
        },
        detail: {
            gap: '9px',
            padding: '9px 12px',
            borderRadius: '12px',
            borderColor: '{content.borderColor}',
            background: 'color-mix(in srgb, var(--p-surface-0) 6%, transparent)',
            color: '{text.color}',
            eyebrow: {
                fontSize: '10px',
                fontWeight: '700',
                color: '{text.color}',
            },
            name: {
                fontSize: '14px',
                fontWeight: '700',
            },
            meta: {
                color: '{text.mutedColor}',
            },
            chevron: {
                fontSize: '16px',
            },
        },
    },

    // OfferStatePill — .offer-state-pill (lifted from OfferCard's pill)
    offerStatePill: {
        padding: '3px 8px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: '700',
        info: {
            background: 'color-mix(in srgb, var(--p-status-info) 14%, transparent)',
            color: '{status.info}',
        },
        warning: {
            background: 'color-mix(in srgb, var(--p-status-warning) 14%, transparent)',
        },
        success: {
            background: 'color-mix(in srgb, var(--p-status-success) 14%, transparent)',
        },
        danger: {
            background: 'color-mix(in srgb, var(--p-status-danger) 14%, transparent)',
            color: '{status.danger}',
        },
        neutral: {
            background: '{content.hoverBackground}',
            color: '{text.hoverMutedColor}',
        },
    },

    // LiveBadge — .live-badge, the app's one live-now badge (Josh lock via
    // design-token-map). Plate stays #232a2f — do not snap to a nearby ramp
    // step.
    //
    // This block was brand-level while the badge was two spans every surface
    // drew for itself and three components read the trio directly. It is a
    // component block now: `-shared`'s LiveBadge is the only reader, and the
    // dot, the plate and the word moved into it together.
    liveBadge: {
        background: '#232a2f',
        color: '{surface.0}',
        dot: '{status.danger}',
        fontSize: '11px',
        fontWeight: '700',
        letterSpacing: '0.04em',
        padding: '3px 8px',
        borderRadius: '999px',
        gap: '6px',
        dotSize: '6px',
        // The badge over a thumbnail, where the plate has about a third of the
        // width it has beside a title.
        sm: {
            fontSize: '9px',
            padding: '2px 6px',
            gap: '4px',
            dotSize: '5px',
        },
        // No plate: a section eyebrow or a bare pip in a row of figures. Only
        // the hue is the badge's; the type belongs to the line it sits on.
        plain: {
            color: '{status.danger}',
        },
        // Over a photo or a video, where neither a light nor a dark ground can
        // be assumed. A scrim of the darkest surface rather than a literal
        // black, so the badge is still the brand's at the bottom of the ramp.
        onMedia: {
            background: 'color-mix(in srgb, {surface.950} 50%, transparent)',
            color: '{surface.0}',
            borderWidth: '0.5px',
            borderColor: 'color-mix(in srgb, {surface.0} 16%, transparent)',
        },
        // The glow under the breathing dot. A glow is not motion, so it stays
        // when the reduced-motion reset stops the animation.
        pulse: {
            dot: {
                boxShadow: '0 0 8px {status.danger}',
            },
        },
    },

    // LiveLaneCard — .live-lane-card (Home · Live now)
    //
    // COMPONENTS.md: radius 18, padding 14; live badge + sale·lane / run; asset
    // row thumb 52×40 + title + bid; Join lane 44px full-width primary.
    liveLaneCard: {
        gap: '12px',
        padding: '14px',
        background: '{content.background}',
        borderWidth: '0.5px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.card}',
        width: '300px',
        head: {
            gap: '8px',
        },
        sale: {
            color: '{text.color}',
            fontSize: '13px',
            fontWeight: '600',
        },
        run: {
            color: '{text.mutedColor}',
            fontFamily: '{font.mono}',
            fontSize: '12px',
        },
        asset: {
            gap: '10px',
        },
        // Room for the LIVE badge to sit on the photo and still show the car.
        thumb: {
            width: '80px',
            height: '60px',
            borderRadius: '10px',
            background: '{content.hoverBackground}',
            color: '{text.mutedColor}',
        },
        title: {
            color: '{text.color}',
            fontSize: '15px',
            fontWeight: '700',
        },
        bid: {
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '17px',
            fontWeight: '800',
        },
        join: {
            height: '44px',
            borderRadius: '12px',
            background: '{primary.color}',
            color: '{primary.contrast.color}',
            fontSize: '15px',
            fontWeight: '700',
            hover: {
                background: '{primary.hoverColor}',
            },
            active: {
                background: '{primary.activeColor}',
            },
            focus: {
                outlineColor: '{primary.color}',
            },
        },
    },

    // PreviewPanel — .preview-panel (Home calendar / locations / BidStack)
    //
    // COMPONENTS.md: card radius 18; head eyebrow 11/700 uppercase tertiary +
    // title 15/700 + mint link 13.5/700; body up to 3 rows via default slot.
    previewPanel: {
        background: '{content.background}',
        borderWidth: '0.5px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.card}',
        head: {
            gap: '8px',
            padding: '14px 14px 0',
        },
        eyebrow: {
            color: '{text.mutedColor}',
            fontSize: '11px',
            fontWeight: '700',
            letterSpacing: '0.08em',
        },
        title: {
            color: '{text.color}',
            fontSize: '15px',
            fontWeight: '700',
        },
        meta: {
            color: '{text.mutedColor}',
            fontSize: '12px',
        },
        link: {
            color: '{primary.color}',
            fontSize: '13.5px',
            fontWeight: '700',
            gap: '4px',
            iconFontSize: '11px',
            hover: {
                color: '{primary.hoverColor}',
            },
            active: {
                color: '{primary.activeColor}',
            },
            focus: {
                outlineColor: '{primary.color}',
            },
        },
        // 8px over and under the rows, whose own 6px makes 14: the head's own
        // inset, so the space under the title and under the last row match the
        // space above the title.
        body: {
            padding: '8px 0',
        },
    },

    // AttentionItem — .attention-item
    //
    // One row of the Dashboard's Needs-you list, from COMPONENTS.md and the two
    // Josh locks, laid out as the Dashboard screen draws it: the plate beside
    // the eyebrow and the title, then the reason and the action under the title.
    // The type is COMPONENTS.md's — title 15/700, reason 12.5, eyebrow 10.5/800
    // — where the screen sets 14.5, 12 and 11. The gaps, the tracking, the line
    // heights, the icon and the action's padding and label, none of which
    // COMPONENTS.md gives, are the screen's. Those deltas are open under MBP-10.
    //
    // The plate is the token map's icon plate: a 10px component-owned corner
    // (COMPONENTS.md says 9; locked at 10), tinted with the tone at the screen's
    // 16% as a `color-mix()` of the status token, the token map's rule for
    // tinted status fills. One ink per tone paints the glyph on the plate and
    // the eyebrow beside it. `money.color` is scheme-split — see
    // componentTokensByColorScheme below.
    //
    // The action is the locked small in-card button, 40px tall (the screen draws
    // 38) with a 10px corner, in the brand accent as the screen paints it. The
    // deadline is bold in the primary ink, as COMPONENTS.md has it, where the
    // older Proposed Components frame set it in the tone.
    attentionItem: {
        gap: '10px',
        padding: '12px 14px',
        borderBottomWidth: '0.5px',
        borderBottomColor: '{content.borderColor}',
        focus: {
            outlineColor: '{primary.color}',
        },
        head: {
            gap: '12px',
        },
        plate: {
            borderRadius: '10px',
        },
        icon: {
            fontSize: '14px',
        },
        eyebrow: {
            gap: '5px',
            fontSize: '10.5px',
            fontWeight: '800',
            letterSpacing: '0.04em',
        },
        title: {
            color: '{text.color}',
            fontFamily: '{font.body}',
            fontSize: '15px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
            lineHeight: '1.3',
        },
        // The plate's 36px and the head's 12px gap, so the reason starts under
        // the title.
        foot: {
            gap: '12px',
            paddingLeft: '48px',
        },
        reason: {
            color: '{text.hoverMutedColor}',
            fontSize: '12.5px',
            lineHeight: '1.35',
        },
        deadline: {
            color: '{text.color}',
            fontWeight: '700',
        },
        action: {
            padding: '0 14px',
            borderRadius: '10px',
            borderWidth: '1px',
            borderColor: '{primary.color}',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontFamily: '{font.body}',
            fontSize: '13px',
            fontWeight: '700',
            hover: {
                background: '{primary.hoverColor}',
                borderColor: '{primary.hoverColor}',
            },
        },
        live: {
            color: '{status.danger}',
            plate: {
                background: 'color-mix(in srgb, var(--p-status-danger) 16%, transparent)',
            },
        },
        soon: {
            color: '{status.warning}',
            plate: {
                background: 'color-mix(in srgb, var(--p-status-warning) 16%, transparent)',
            },
        },
        // `money.color` is scheme-split — see componentTokensByColorScheme below.
        money: {
            plate: {
                background: 'color-mix(in srgb, var(--p-status-success) 16%, transparent)',
            },
        },
        info: {
            color: '{status.info}',
            plate: {
                background: 'color-mix(in srgb, var(--p-status-info) 16%, transparent)',
            },
        },
    },

    // AuctionLights — .auction-lights
    // AssetPhoto — .asset-photo
    //
    // The one camera under every asset photo. The caller's box brings the
    // background and the radius; this brings the glyph, in three sizes.
    assetPhoto: {
        placeholder: {
            color: '{text.mutedColor}',
        },
        small: { placeholder: { fontSize: '16px' } },
        medium: { placeholder: { fontSize: '22px' } },
        // Large sits in a gallery or an expanded card, hundreds of pixels tall,
        // where 28px read as a thumbnail's glyph lost in the box.
        large: { placeholder: { fontSize: '40px' } },
        // Fill: a share of the box's shorter side, held between a row plate's
        // legible minimum and the large size's ceiling.
        fill: {
            placeholder: {
                minFontSize: '14px',
                share: '34cqmin',
                maxFontSize: '40px',
            },
        },
    },

    auctionLights: {
        gap: '4px',
        // The pip's size is the component's own literal 8px, not a token: the
        // letter has to fit inside a 13px circle, and a theme that moved it
        // overflowed the pip on a live BidCard.
        pip: {
            borderRadius: '50%',
            boxShadow: '0 0 0 0.5px rgba(0, 0, 0, 0.4) inset',
            fontFamily: '{font.mono}',
            fontWeight: '800',
            // Ink per fill, by that fill's luminance — white on this green is
            // 3.12:1, so green takes the dark ink with yellow and white.
            red: {
                background: '{auctionLight.red}',
                color: '{surface.0}',
            },
            green: {
                background: '{auctionLight.green}',
                color: '{surface.950}',
            },
            yellow: {
                background: '{auctionLight.yellow}',
                color: '{surface.950}',
            },
            blue: {
                background: '{auctionLight.blue}',
                color: '{surface.0}',
            },
            white: {
                background: '{auctionLight.white}',
                color: '{surface.950}',
            },
        },
    },

    // AvatarChip — .avatar-chip
    avatarChip: {
        color: '{primary.contrastColor}',
        fontFamily: '{font.display}',
        fontWeight: '700',
        lineHeight: '1',
        onlineIndicator: {
            borderRadius: '10px',
            background: '{primary.color}',
            borderWidth: '2px',
            borderColor: 'var(--surface-ground)',
        },
    },

    // BidCard — .bid-card
    //
    // The palette is spoken for, and that is why a new color here is never
    // free. Gold means the dealer has a stake, red means against them, mint is
    // the one thing to press, blue is the dealer's OWN input — the target flag
    // and the armed proxy — and neutral is reference data. Red already carries
    // three unrelated facts (the danger band, the price over target, the
    // countdown under ten seconds) and separates them by weight and position
    // rather than by hue. Nothing should be added to it, and nothing new gets a
    // color without taking one off something else first.
    //
    // `success` is deliberately absent: mint is the bid control's, and a green
    // header would compete with the only thing on the card that costs money.
    // The shared back control. Its three variants are three skins over one
    // geometry: `filled` is the header's disc, `glass` rides over media, and
    // `plain` is a text link. The sizes the variants override are the
    // component's own structure and stay literal there.
    backButton: {
        icon: {
            fontSize: '14px',
        },
        label: {
            fontSize: '15px',
            fontWeight: '500',
            letterSpacing: '-0.01em',
        },
        filled: {
            background: '{content.hoverBackground}',
            color: '{text.color}',
            borderRadius: '18px',
            icon: {
                fontSize: '14px',
            },
        },
        // White in both schemes, like the scrim it sits on: the control is over
        // a photograph, not over the page, so the page's ink would be wrong in
        // one scheme and invisible in the other.
        glass: {
            color: '#fff',
        },
        plain: {
            color: '{primary.color}',
        },
    },

    bidCard: {
        background: '{content.background}',
        // 1px on EVERY card, tinted or not. At 0.5px neutral against 1px tinted
        // the bordered cards measure 183px and the rest 182px, and both lanes
        // cache one card's height for every unmounted row.
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.card}',
        // The locked border only a finished asset gets. Nothing live or pending
        // has one, which is what keeps it readable as "finished" rather than as
        // decoration.
        final: {
            warn: {
                borderColor: 'color-mix(in srgb, var(--p-status-warning) 35%, transparent)',
            },
            danger: {
                borderColor: 'color-mix(in srgb, var(--p-status-danger) 50%, transparent)',
            },
        },
        // Ran, complete, no stake. Structural rather than `opacity`, which
        // multiplies against alpha-carrying text tokens and drops the outcome
        // label under AA.
        recede: {
            background: 'color-mix(in srgb, var(--p-content-background) 88%, var(--surface-ground))',
            title: {
                color: '{text.mutedColor}',
            },
        },

        // ── The header ──────────────────────────────────────────────────
        // A 33px tinted band, and the only color channel for state. Against
        // half a pixel of border around the card, this is the version you can
        // catch at scroll speed — which is the whole argument for it.
        //
        // The gap is the LEFT group's — run number, middot, clock. The headline
        // is pushed to the right edge by an auto margin, so the same number
        // never applies to it.
        header: {
            gap: '8px',
            padding: '0 14px',
            // Half a pixel, unlike the card's own border: this one divides two
            // regions of the same card rather than separating the card from the
            // lane, and it sits inside the header's fixed 33px either way.
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
        },
        // Not nothing: 3% of the text color composites to a 1.073:1 step
        // against the card, which makes the header read as its own row without
        // competing with the two tints that carry meaning.
        secondary: {
            header: {
                background: 'color-mix(in srgb, var(--p-text-color) 3%, transparent)',
            },
        },
        warn: {
            header: {
                background: 'color-mix(in srgb, var(--p-status-warning) 14%, transparent)',
                borderColor: 'color-mix(in srgb, var(--p-status-warning) 20%, transparent)',
            },
            // Reference data on an untinted band, identity on a tinted one: once
            // the header carries a standing the dealer is reading the whole row,
            // and a muted run number inside a gold band reads as switched off.
            run: {
                color: '{text.color}',
            },
            headline: {
                color: '{status.warning}',
                fontWeight: '800',
            },
        },
        danger: {
            header: {
                background: 'color-mix(in srgb, var(--p-status-danger) 12%, transparent)',
                borderColor: 'color-mix(in srgb, var(--p-status-danger) 20%, transparent)',
            },
            run: {
                color: '{text.color}',
            },
            headline: {
                color: '{status.danger}',
                fontWeight: '800',
            },
        },
        run: {
            fontFamily: '{font.mono}',
            fontSize: '11px',
            color: '{text.mutedColor}',
        },
        divider: {
            fontSize: '11px',
            color: '{text.mutedColor}',
        },
        // Half a pixel larger than the run number and a weight heavier, which is
        // what separates the figure that moves from the one that identifies.
        clock: {
            fontFamily: '{font.mono}',
            fontSize: '11.5px',
            fontWeight: '700',
            color: '{text.color}',
            // One threshold, not a gradient — and weight is the load-bearing
            // half, since it survives grayscale, color blindness and a phone in
            // sunlight.
            urgent: {
                color: '{status.danger}',
                fontWeight: '800',
            },
        },
        // The zero pulse. A card resting on zero is waiting on the runtime's
        // frame rather than stalled, and a motionless zero is indistinguishable
        // from a dead component.
        waiting: {
            borderRadius: '50%',
            background: '{status.danger}',
        },
        // Muted unless `severity` carries a standing: reference data must not
        // compete with the signal that replaces it the moment one exists. The
        // explicit line-height is what keeps a 9.5px word from setting a taller
        // line box than the 11px figures beside it and nudging the row.
        headline: {
            gap: '4px',
            fontSize: '9.5px',
            lineHeight: '11px',
            fontWeight: '700',
            letterSpacing: '0.07em',
            color: '{text.mutedColor}',
            icon: {
                fontSize: '9px',
            },
        },

        // ── The body ────────────────────────────────────────────────────
        body: {
            gap: '12px',
            padding: '12px 14px',
        },
        thumbnail: {
            borderRadius: '10px',
            background: 'color-mix(in srgb, var(--p-text-color) 6%, transparent)',
        },
        // The row's own line box, so its `1lh` floor is a known quantity rather
        // than whatever line-height the page around the card happens to set.
        marks: {
            gap: '6px',
            lineHeight: '13px',
        },
        // A drawn box rather than a tint: at 9px a fill alone is not enough edge
        // to read as a badge, and the hairline is what separates it from the
        // title's own weight one row below.
        mark: {
            gap: '3px',
            padding: '1px 6px',
            borderRadius: '4px',
            borderWidth: '0.5px',
            borderColor: 'color-mix(in srgb, var(--p-text-color) 14%, transparent)',
            background: '{content.hoverBackground}',
            color: '{text.hoverMutedColor}',
            fontSize: '9px',
            lineHeight: '13px',
            fontWeight: '800',
            letterSpacing: '0.05em',
            icon: {
                fontSize: '8px',
            },
            // A like is a RED heart everywhere in the app (QA H1 / S1), so the
            // liked mark takes the danger hue rather than the info blue the
            // target flag and the armed proxy chip keep. Solid fill under the
            // contrast ink: a 16 percent tint of a status color under its own
            // ink cannot clear AA, which is how the Tag block already paints.
            liked: {
                background: '{status.danger}',
                borderColor: '{status.danger}',
                color: '{status.dangerContrast}',
            },
        },
        title: {
            fontSize: '15px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
            color: '{text.color}',
        },
        money: {
            gap: '6px',
        },
        price: {
            fontFamily: '{font.mono}',
            fontSize: '17px',
            fontWeight: '700',
            color: '{text.color}',
            // Red is the PRICE, never the target: a constant that changes
            // color reads as though it moved.
            overTarget: {
                color: '{status.danger}',
            },
        },
        moneyLabel: {
            fontSize: '11px',
            color: '{text.mutedColor}',
        },
        target: {
            gap: '4px',
            fontFamily: '{font.mono}',
            fontSize: '11px',
            fontWeight: '700',
            color: '{status.info}',
            icon: {
                fontSize: '9.5px',
            },
        },

        // ── The action zone ─────────────────────────────────────────────
        actions: {
            gap: '8px',
            padding: '0 14px 12px',
        },
        control: {
            borderRadius: '12px',
            // A 48px control holds two lines by giving the label an 18px line
            // box and the dealer line 13px, with a pixel between: 18 + 1 + 13 =
            // 32, centered in 48. WITHOUT a dealer line the label goes back to
            // `normal` — a fixed 18px on a single line is what makes a control
            // sit a pixel or two high, and it is the drift that shows up first
            // beside a card that gets it right.
            stacked: {
                gap: '1px',
                label: {
                    lineHeight: '18px',
                },
            },
        },
        controlLabel: {
            gap: '8px',
            fontSize: '15px',
            fontWeight: '700',
        },
        // In the control's own color, so it reads as part of the label rather
        // than as an annotation on it — which is why only the mint bid control
        // declares one here, and every other control inherits.
        biddingAs: {
            gap: '4px',
            fontSize: '10px',
            lineHeight: '13px',
            fontWeight: '700',
            letterSpacing: '0.02em',
        },
        // One glyph whatever the reason, and the largest tap target on the card
        // — which is why it doubles as the way back to an armed proxy the block
        // stranded. The same box as the disabled pill, so a card that cannot be
        // bid on reads the same whether the sale closed it or the dealer's
        // account did.
        locked: {
            padding: '0 12px',
            background: '{content.hoverBackground}',
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
            color: '{text.hoverMutedColor}',
            // The reason at full strength inside a row that is not: the message
            // is the only thing here a dealer has to read.
            label: {
                color: '{text.color}',
            },
            icon: {
                fontSize: '14px',
            },
        },
        proxy: {
            padding: '0 8px',
            background: 'color-mix(in srgb, var(--p-status-info) 16%, transparent)',
            borderWidth: '1px',
            borderColor: 'color-mix(in srgb, var(--p-status-info) 40%, transparent)',
            color: '{status.info}',
            // Two lines inside 48px alongside the dealer line, so the chip sets
            // its own leading rather than inheriting the page's.
            lineHeight: '1.1',
            line: {
                gap: '4px',
            },
            label: {
                fontSize: '9px',
                fontWeight: '700',
                letterSpacing: '0.06em',
            },
            amount: {
                fontFamily: '{font.mono}',
                fontSize: '13px',
                fontWeight: '700',
            },
            // With the whole row to itself the chip stops being a chip: one text
            // run at the bid control's own size, because it IS the bid control
            // on a queued asset and a 9px label would read as a caption.
            wide: {
                line: {
                    fontSize: '15px',
                    fontWeight: '800',
                    lineHeight: '18px',
                },
            },
            // The armed chip's box exactly — same width, height and offset — so
            // the row does not shift under the dealer at the moment the proxy
            // stops being a control. Only the color and the label change.
            spent: {
                background: '{content.hoverBackground}',
                borderColor: '{content.borderColor}',
                color: '{text.hoverMutedColor}',
            },
        },
        bid: {
            padding: '0 10px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            // The heaviest label on the card, and the only one that is: it is
            // the one control that costs money.
            label: {
                fontWeight: '800',
            },
            // Disabled rather than absent while a live proxy is armed: offering
            // a bid there would raise the price the dealer's own ceiling is
            // measured against. It takes the pill every finalized and pending
            // control wears, not a dimmed mint — a faded accent reads as a
            // button that failed to load.
            disabled: {
                background: '{content.hoverBackground}',
                borderWidth: '0.5px',
                borderColor: '{content.borderColor}',
                color: '{text.hoverMutedColor}',
                label: {
                    fontWeight: '700',
                },
            },
        },
    },

    // BidSheet — .bid-sheet
    //
    // The tray is BidCard's other half, so where the two render the same fact
    // they reach for the same value: the header's measurements, the marks, the
    // clock's urgent weight and the auction lights are the card's, restated
    // here rather than shared, because a token block is per component and that
    // is what makes either one movable without the other.
    //
    // ONE NUMBER GOVERNS THE MIDDLE OF THIS BLOCK. `panel.height` is what the
    // live panel and the proxy panel both measure in every state, including
    // while the numeric field is open, and the four rows inside it add to
    // exactly that: 16 label + 8 gap + 48 chips + 8 gap + 64 control. Changing
    // any of the five means changing the height, and a panel that grows under
    // the thumb moves the control a dealer was reaching for mid-sale.
    bidSheet: {
        // ── The asset ───────────────────────────────────────────────────
        asset: {
            gap: '12px',
            marginBottom: '16px',
        },
        thumbnail: {
            width: '72px',
            height: '56px',
            borderRadius: '12px',
            background: '{content.hoverBackground}',
        },
        // The badge over the thumbnail. Its own scrim rather than a theme
        // surface: it sits on photography, so it needs to hold against whatever
        // the image happens to be behind it.
        headline: {
            gap: '3px',
            padding: '2px 5px',
            borderRadius: '5px',
            background: 'rgba(0, 0, 0, 0.6)',
            color: '{surface.0}',
            fontSize: '8px',
            fontWeight: '800',
        },
        title: {
            fontSize: '16px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
        },
        run: {
            gap: '6px',
            marginTop: '2px',
            fontFamily: '{font.mono}',
            fontSize: '12px',
            color: '{text.mutedColor}',
        },
        // Weight as well as color, and for BidCard's reason: weight survives
        // greyscale, color blindness and a phone in sunlight, which color alone
        // does not — and the last ten seconds are where that matters.
        clock: {
            urgent: {
                color: '{status.danger}',
                fontWeight: '800',
            },
        },
        marks: {
            gap: '6px',
            marginTop: '4px',
        },
        auctionLights: {
            gap: '3px',
        },
        // `auctionLight`, never `light` — that is one of PrimeVue's color
        // scheme branch keys and would be dropped from the emitted name.
        //
        // FIVE where BidCard has four. White is the absence of a light on a
        // card in a lane, which is scanned; the tray is where money commits, so
        // it paints every code the auction house sent rather than deciding one
        // of them says nothing.
        auctionLight: {
            size: '14px',
            borderRadius: '50%',
            fontSize: '8px',
            fontWeight: '800',
            red: { background: '{auctionLight.red}', color: '{surface.0}' },
            green: { background: '{auctionLight.green}', color: '{surface.0}' },
            yellow: { background: '{auctionLight.yellow}', color: '{surface.950}' },
            blue: { background: '{auctionLight.blue}', color: '{surface.0}' },
            white: { background: '{auctionLight.white}', color: '{surface.950}' },
        },
        mark: {
            padding: '1px 6px',
            borderRadius: '4px',
            background: '{content.hoverBackground}',
            color: '{text.hoverMutedColor}',
            fontSize: '9px',
            fontWeight: '800',
            letterSpacing: '0.04em',
            // The one mark that says nothing about the asset — it is the
            // dealer's own intent — so it reads in the accent rather than in
            // the neutral the condition marks share.
            liked: {
                // 14% before the page ground stopped being pure white. The
                // tint is transparent, so it composites over the ground rather
                // than the card, and the accent reading itself against its own
                // tint fell to 4.46:1 there. 10% is 4.75:1.
                //
                // RESTORED. `bd9914ff` set 10% for the reason above; the Wave 1
                // port (`20c66c3b`) brought this file over wholesale from the
                // fork, which had never had the fix, and put 14% back. The
                // catalog's axe gate measured 4.46:1 on `Every Mark` in light
                // again — the same figure, to the digit.
                background: 'color-mix(in srgb, var(--p-primary-color) 10%, transparent)',
                color: '{primary.color}',
            },
        },

        // ── The money ───────────────────────────────────────────────────
        money: {
            gap: '6px',
            marginBottom: '16px',
            label: {
                fontSize: '11px',
                color: '{text.mutedColor}',
            },
        },
        price: {
            fontFamily: '{font.mono}',
            fontSize: '22px',
            fontWeight: '800',
            color: '{primary.color}',
            // `warn` is a stake the dealer has, `danger` is against them. Mint
            // stays the no-position reading, which is also the bid control's
            // color and why `success` is unusable on this surface.
            warnColor: '{status.warning}',
            dangerColor: '{status.danger}',
            // An asset that has not opened has words here instead of a figure,
            // so they take the muted weight rather than the price's.
            unset: {
                fontSize: '15px',
                color: '{text.mutedColor}',
            },
            // A settled asset's figure is reference, not a stake, so it drops
            // to muted whatever the severity was while the sale ran.
            final: {
                color: '{text.mutedColor}',
            },
        },
        target: {
            gap: '4px',
            fontSize: '11px',
            color: '{text.mutedColor}',
        },

        // ── The receipt ─────────────────────────────────────────────────
        // A settled tray, at the panel's own height so the sheet does not
        // resize between a running asset and a finished one.
        receipt: {
            gap: '8px',
            borderRadius: '16px',
            background: '{content.hoverBackground}',
            color: '{text.color}',
            fontSize: '15px',
            fontWeight: '700',
            // The tints are LIGHT on purpose, and the number is an accessibility
            // one rather than a taste one: these tones are their own text color
            // here, so every percent of tint darkens the background the words
            // sit on and costs contrast. Warning at 14% reads 4.15:1 against
            // itself; at 6% it is 5.3:1, and the tone is still unmistakable.
            warn: {
                background: 'color-mix(in srgb, var(--p-status-warning) 6%, transparent)',
                color: '{status.warning}',
            },
            danger: {
                background: 'color-mix(in srgb, var(--p-status-danger) 8%, transparent)',
                color: '{status.danger}',
            },
            // The settled tray: muted ink on a plain line, never a warn or a
            // danger tint. The sentence still names Won or Sold; the tone says
            // the sale is over.
            final: {
                color: '{text.mutedColor}',
                borderColor: '{content.borderColor}',
            },
        },

        // ── The tabs ────────────────────────────────────────────────────
        tabs: {
            gap: '4px',
            padding: '4px',
            background: '{content.hoverBackground}',
            borderRadius: '12px',
            marginBottom: '14px',
        },
        tab: {
            height: '38px',
            gap: '6px',
            borderRadius: '9px',
            fontWeight: '700',
            fontSize: '14px',
            color: '{text.hoverMutedColor}',
            // A RAISED PILL rather than a mint fill, which is Apple's segmented
            // control and is also what this component's own rule asks for: mint
            // is the bid control's color, and a mint tab competes with the only
            // thing in the tray that costs money. It is the accessible reading
            // too — 14px bold white on the light scheme's primary is 4.31:1,
            // under the 4.5 a label this size needs, while the bid button
            // clears the same pairing because 20px bold counts as large text.
            selected: {
                background: '{content.background}',
                color: '{text.color}',
            },
            disabledOpacity: '0.4',
            lock: {
                fontSize: '12px',
            },
        },

        // ── The panel ───────────────────────────────────────────────────
        panel: {
            height: '144px',
            gap: '8px',
        },
        increment: {
            label: {
                height: '16px',
                fontSize: '11px',
                color: '{text.mutedColor}',
                letterSpacing: '0.08em',
                // The sale just moved this asset's increment, and the row says
                // so where the "tap to change" hint sits. Full ink and weight
                // rather than the accent: an 11px line in the light scheme's
                // primary is 4.31:1, under the 4.5 it owes, and weight is what
                // survives greyscale and a phone in sunlight anyway.
                changed: {
                    color: '{text.color}',
                    fontWeight: '700',
                },
            },
        },
        // 11px bold, so it needs the full 4.5:1 and cannot take the accent —
        // the light scheme's primary on white is 4.31:1. Weight and position
        // are what make it read as a control.
        typeToggle: {
            color: '{text.color}',
            fontWeight: '700',
        },
        chips: {
            gap: '8px',
            height: '48px',
        },
        chip: {
            borderRadius: '14px',
            fontFamily: '{font.mono}',
            background: '{content.hoverBackground}',
            color: '{text.color}',
            borderColor: '{content.borderColor}',
            fontWeight: '700',
            fontSize: '14px',
            // Selected inverts: text-color fill, surface-color ink. The 8% tint
            // it replaced read as barely different from its neighbors. Still
            // not mint — mint stays on the bid control.
            selected: {
                background: '{text.color}',
                color: '{content.background}',
                borderColor: '{text.color}',
            },
            // The ladder the sale just changed. On the BORDER, which is a UI
            // boundary at 3:1, and never on the ink for the reason the label's
            // own `changed` records. It stacks under `selected`, so the chosen
            // multiple still reads as chosen.
            changed: {
                borderColor: '{primary.color}',
            },
        },
        // The numeric field takes the chips' slot, so it takes their height
        // too — that identity is what keeps the OS keyboard affordable where a
        // drawn keypad cost 198px and broke the parity rule every time it
        // opened.
        input: {
            borderRadius: '14px',
            borderColor: '{content.borderColor}',
            background: '{content.hoverBackground}',
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '20px',
            fontWeight: '800',
            // A ceiling under the floor is ignored by the engine and fires
            // nothing. It reads as an error DURING entry rather than after
            // submit, which is the one failure in this tray that used to be
            // completely silent.
            below: {
                borderColor: '{status.danger}',
                color: '{status.danger}',
            },
        },
        below: {
            inset: '8px',
            fontSize: '11px',
            fontWeight: '700',
        },

        // ── The controls ────────────────────────────────────────────────
        control: {
            height: '64px',
        },
        bid: {
            borderRadius: '16px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontWeight: '800',
            fontSize: '20px',
            gap: '10px',
        },
        // The words that REPLACE the slide. There is no greyed control in this
        // tray: the proxy sits in the other panel, so nothing moves when the
        // live control goes away, and a disabled button would be a target a
        // thumb can still find.
        unavailable: {
            gap: '4px',
            borderRadius: '16px',
            background: '{content.hoverBackground}',
            color: '{text.hoverMutedColor}',
            fontSize: '14px',
            fontWeight: '700',
        },
        hint: {
            fontSize: '11px',
            fontWeight: '600',
            // The hint is 11px, so it needs the 4.5:1 of normal text rather
            // than the 3:1 of large. `{text.mutedColor}` is 4.49:1 on the
            // tray's own `{content.hoverBackground}` in dark; the hover step
            // is 6.15:1.
            //
            // RESTORED. `9965ab27` (#167) set the hover step for that reason;
            // the Wave 1 port (`20c66c3b`) brought this file over wholesale
            // from the fork, which had never had the fix, and put the muted
            // step back. The catalog's axe gate measured 4.48:1 on `Winning`
            // in dark again.
            color: '{text.hoverMutedColor}',
        },
        steppers: {
            gap: '10px',
        },
        step: {
            width: '64px',
            borderRadius: '16px',
            background: '{content.hoverBackground}',
            color: '{text.color}',
            borderColor: '{content.borderColor}',
            fontSize: '26px',
            fontWeight: '700',
            // Disabled AT the floor rather than clamping silently, so a tap
            // does not land on a no-op.
            disabledOpacity: '0.4',
        },
        // One control, three readings: set, update, cancel. The tone is what
        // separates them, and `confirming` is the only solid danger in the
        // component — the resting cancel stays quiet, because the destructive
        // intent becomes explicit only once it is one tap away.
        primary: {
            borderRadius: '16px',
            fontWeight: '800',
            fontSize: '18px',
            gap: '2px',
            // `infoContrast` rather than a hand-picked ink: the theme states
            // the pairing for this fill per scheme, and choosing the dark
            // surface by hand read at 3.26:1 on the light scheme's blue.
            commit: {
                background: '{status.info}',
                color: '{status.infoContrast}',
            },
            // Same rule as the receipt above: the danger tone is the TEXT here,
            // so the tint behind it has to stay light enough to read against.
            // 16% put the resting cancel at 4.36:1.
            danger: {
                background: 'color-mix(in srgb, var(--p-status-danger) 8%, transparent)',
                color: '{status.danger}',
            },
            confirming: {
                background: '{status.danger}',
                color: '{surface.0}',
            },
        },
    },

    // BudgetMeter — .budget-meter
    //
    // From COMPONENTS.md and the Dashboard screen, which agree on the sizes: the
    // limit in mono 17/800 under a 10.5/800 eyebrow, a 6px bar with a 3px
    // corner, a 12px footer in the tertiary ink. The two gaps are measured off
    // the screen, since COMPONENTS.md gives none. The padding is COMPONENTS.md's
    // `14px 16px`; the screen draws 12 all round, which is an open delta under
    // MBP-09.
    //
    // The fill is the brand accent while there is room and the status hues past
    // 90% and 100% of the limit, all scheme-aware references. The track is the
    // raised ground, which is why hover firms up the hairline to the field
    // border rather than lifting the card onto that same ground.
    budgetMeter: {
        gap: '6px',
        padding: '14px 16px',
        background: '{content.background}',
        borderWidth: '0.5px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.card}',
        fontFamily: '{font.body}',
        hover: {
            borderColor: '{formField.borderColor}',
        },
        focus: {
            outlineColor: '{primary.color}',
        },
        summary: {
            gap: '2px',
        },
        label: {
            color: '{text.mutedColor}',
            fontSize: '10.5px',
            fontWeight: '800',
            letterSpacing: '0.08em',
        },
        amount: {
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '17px',
            fontWeight: '800',
        },
        chevron: {
            color: '{text.mutedColor}',
            fontSize: '16px',
        },
        bar: {
            borderRadius: '3px',
            background: '{content.hoverBackground}',
        },
        fill: {
            background: '{primary.color}',
            warning: {
                background: '{status.warning}',
            },
            danger: {
                background: '{status.danger}',
            },
        },
        footer: {
            color: '{text.mutedColor}',
            fontSize: '12px',
        },
    },

    // ConfettiOverlay — .confetti-overlay
    confettiOverlay: {
        piece: {
            borderRadius: '1px',
            round: {
                borderRadius: '50%',
            },
            tint0: {
                background: '{status.success}',
            },
            tint1: {
                background: '{status.info}',
            },
            tint2: {
                background: '{status.warning}',
            },
            tint3: {
                background: 'color-mix(in srgb, var(--p-status-danger) 70%, var(--p-surface-0))',
            },
            tint4: {
                background: '{text.color}',
            },
        },
    },

    // ConfirmSheet — .confirm-sheet (the in-app "are you sure?")
    confirmSheet: {
        message: {
            color: '{text.mutedColor}',
            fontSize: '14px',
            lineHeight: '1.55',
        },
        // A failed action's verdict, inside the still-open sheet.
        error: {
            color: '{status.danger}',
            fontSize: '14px',
            lineHeight: '1.55',
        },
        actions: {
            gap: '10px',
        },
        btn: {
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: '600',
            primary: {
                background: '{primary.color}',
                color: '{primary.contrastColor}',
            },
            // The most permanent tap confirm reads red — a real semantic status,
            // not a raw ramp. Contrast follows the danger hue per scheme.
            danger: {
                background: '{status.danger}',
                color: '{status.dangerContrast}',
            },
            ghost: {
                background: 'transparent',
                color: '{text.color}',
                borderWidth: '1px',
                borderColor: '{content.borderColor}',
            },
        },
    },

    // DiscussedAssetBanner — .discussed-asset-banner
    //
    // The Discussing card over a thread, as the Chat Surfaces board draws it:
    // one card on the card ground with a hairline and the token map's 14px
    // field radius, its eyebrow inside it, the asset on a 56 x 42 photo at the
    // map's 8px thumbnail radius, and, with an offer, the offer's state at the
    // eyebrow's end, the Ask / MMR line and a View offer footer. The state takes
    // OfferCard's pill tones; `offer.success.color` is scheme-split — see
    // componentTokensByColorScheme below.
    discussedAssetBanner: {
        background: '{content.background}',
        borderWidth: '0.5px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.row}',
        head: {
            gap: '8px',
            padding: '8px 12px 0',
        },
        label: {
            fontSize: '10.5px',
            letterSpacing: '0.12em',
            fontWeight: '700',
            color: '{text.mutedColor}',
        },
        offer: {
            fontSize: '11px',
            fontWeight: '700',
            info: {
                color: '{status.info}',
            },
            warning: {
                color: '{status.warning}',
            },
            danger: {
                color: '{status.danger}',
            },
            neutral: {
                color: '{text.mutedColor}',
            },
        },
        offerAmount: {
            fontFamily: '{font.mono}',
        },
        row: {
            gap: '10px',
            padding: '8px 12px 10px',
        },
        media: {
            borderRadius: '8px',
            background: '{content.hoverBackground}',
        },
        mediaFallback: {
            background: 'radial-gradient(120% 120% at 50% 20%, var(--p-content-hover-background), var(--surface-ground))',
        },
        title: {
            fontSize: '14px',
            fontWeight: '700',
            color: '{text.color}',
        },
        meta: {
            fontSize: '12px',
            color: '{text.hoverMutedColor}',
            gap: '5px',
        },
        dot: {
            color: '{text.mutedColor}',
        },
        figures: {
            fontSize: '12px',
            color: '{text.hoverMutedColor}',
        },
        figure: {
            fontFamily: '{font.mono}',
            fontWeight: '700',
            color: '{text.color}',
            mmr: {
                color: '{status.info}',
            },
        },
        view: {
            background: 'transparent',
            color: '{primary.color}',
            fontWeight: '600',
            fontSize: '14px',
            padding: '4px 8px',
        },
        footer: {
            gap: '6px',
            borderTopWidth: '0.5px',
            borderTopColor: '{content.borderColor}',
            background: 'transparent',
            color: '{primary.color}',
            fontSize: '13px',
            fontWeight: '700',
            hover: {
                background: 'color-mix(in srgb, var(--p-primary-color) 8%, transparent)',
            },
            focus: {
                outlineColor: '{primary.color}',
            },
        },
        footerIcon: {
            fontSize: '11px',
        },
    },

    // EventChipRow — .event-chip-row
    //
    // The Sale Viewer's event filter, from COMPONENTS.md (option 6a) and the
    // handoff's EventChipRow frame: a strip of chips 8px apart, 20px in from
    // either edge, with 6px above and below that is the room each chip's 44px hit
    // area needs inside the scrolling strip rather than anything the design draws.
    //
    // Each chip is COMPONENTS.md's 32px pill at `0 12px`, with the token map's
    // 999px pill corner, its words at 12.5/700. At rest it is the card ground
    // with the field border's 0.5px outline and the secondary ink; chosen, it is
    // the brand accent with the accent's own ink, and its outline takes the
    // accent too, so choosing a chip never changes its size. Hover lifts a chip
    // at rest one step, to the raised ground. The accent and its ink clear
    // 4.5:1 in both schemes (9.07:1 dark, 6.47:1 light), so nothing here is
    // scheme-split.
    //
    // A live event's dot is `{status.danger}`, 6px round, the token map's locked
    // dot for a live signal. It is the dot alone, not the live badge's plate, so
    // LiveLaneCard owns the named `liveBadge` block; this row keeps the danger
    // dot alone (not the plate). The count after the label is
    // mono.
    eventChipRow: {
        gap: '8px',
        padding: '6px 20px',
        chip: {
            padding: '0 12px',
            borderWidth: '0.5px',
            borderColor: '{formField.borderColor}',
            borderRadius: '999px',
            background: '{content.background}',
            color: '{text.hoverMutedColor}',
            fontSize: '12.5px',
            fontWeight: '700',
            hover: {
                background: '{content.hoverBackground}',
            },
            focus: {
                outlineColor: '{primary.color}',
            },
            selected: {
                background: '{primary.color}',
                borderColor: '{primary.color}',
                color: '{primary.contrastColor}',
            },
        },
        chipContent: {
            gap: '6px',
        },
        count: {
            fontFamily: '{font.mono}',
        },
    },

    // FilterRow — .filter-row
    filterRow: {
        borderBottomWidth: '0.5px',
        borderBottomColor: '{content.borderColor}',
        button: {
            gap: '12px',
            padding: '14px 16px',
            background: 'transparent',
            active: {
                background: '{content.hoverBackground}',
            },
        },
        main: {
            gap: '2px',
        },
        label: {
            fontSize: '15px',
            fontWeight: '600',
            color: '{text.color}',
        },
        sublabel: {
            fontSize: '12px',
            color: '{text.mutedColor}',
        },
        value: {
            gap: '6px',
        },
        count: {
            fontSize: '15px',
            fontWeight: '600',
            // `color` is scheme-split — see componentTokensByColorScheme below.
        },
        any: {
            fontSize: '14px',
            color: '{text.mutedColor}',
            set: {
                color: '{text.hoverMutedColor}',
            },
        },
        chevron: {
            fontSize: '12px',
            color: '{text.mutedColor}',
        },
        chips: {
            gap: '8px',
            padding: '0 16px 12px',
        },
        chip: {
            gap: '6px',
            // Six pixels of vertical padding came off when the remove control
            // grew from 18 to 24, so the chip — and the strip's height with it —
            // is exactly where it was.
            padding: '3px 5px 3px 12px',
            borderRadius: '999px',
            // `background` is scheme-split — see componentTokensByColorScheme
            // below. The INK is not: `{primary.contrastColor}` is white on the
            // darker green (6.47:1) and the near-black on the mint in dark
            // (9.07:1), so the semantic reference is right in both schemes.
            color: '{primary.contrastColor}',
            fontSize: '13px',
            fontWeight: '600',
        },
        chipX: {
            borderRadius: '12px',
            background: 'color-mix(in srgb, var(--p-primary-contrast-color) 18%, transparent)',
            color: '{primary.contrastColor}',
            icon: {
                fontSize: '10px',
            },
        },
        auctionLight: {
            borderRadius: '50%',
            fontSize: '9px',
            fontWeight: '700',
            color: '{surface.0}',
            red: {
                background: '{auctionLight.red}',
            },
            green: {
                background: '{auctionLight.green}',
            },
            yellow: {
                background: '{auctionLight.yellow}',
                color: '{surface.950}',
            },
            blue: {
                background: '{auctionLight.blue}',
            },
            white: {
                background: '{auctionLight.white}',
                color: '{surface.950}',
            },
        },
    },

    // HaltBanner — .halt-banner
    haltBanner: {
        gap: '10px',
        reason: {
            color: '{text.color}',
        },
        banner: {
            padding: '12px 14px',
            borderRadius: '16px',
            fontSize: '13px',
            lineHeight: '1.35',
            icon: {
                fontSize: '16px',
            },
            paused: {
                background: 'color-mix(in srgb, var(--p-status-warning) 14%, transparent)',
                borderWidth: '1px',
                borderColor: 'color-mix(in srgb, var(--p-status-warning) 45%, transparent)',
                color: '{status.warning}',
            },
            finished: {
                background: 'color-mix(in srgb, var(--p-status-danger) 12%, transparent)',
                borderWidth: '1px',
                borderColor: 'color-mix(in srgb, var(--p-status-danger) 40%, transparent)',
                color: '{status.danger}',
            },
        },
        stacked: {
            padding: '10px 12px',
            borderRadius: '12px',
            icon: {
                fontSize: '15px',
            },
            line: {
                fontSize: '12px',
                color: '{text.mutedColor}',
            },
            reason: {
                fontSize: '12px',
            },
        },
        label: {
            stacked: {
                fontSize: '11px',
                fontWeight: '800',
                letterSpacing: '0.1em',
            },
        },
        chip: {
            letterSpacing: '0.04em',
        },
    },

    // MessageBubble — .message-bubble
    messageBubble: {
        padding: '11px 14px',
        borderRadius: '18px',
        fontSize: '15px',
        lineHeight: '1.4',
        me: {
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontWeight: '500',
            assetCard: {
                background: 'color-mix(in srgb, var(--p-primary-contrast-color) 18%, transparent)',
            },
            assetCardTitle: {
                color: '{primary.contrastColor}',
            },
            assetCardPrice: {
                color: '{primary.contrastColor}',
            },
            // "You" is never a role to identify, so the viewer's own author
            // line steps back to the muted tone whatever role it carries.
            //
            // The SEMANTIC muted color rather than a raw ramp step: `{surface.400}`
            // was one hex per scheme with nothing saying it meant "muted", and in
            // both schemes it landed under the contrast floor for 11px bold text
            // — 2.62:1 on paper, and failing in dark as well.
            author: {
                color: '{text.mutedColor}',
            },
            // The offer card on the brand fill takes the fill's own ink at full
            // strength. The design fades it to 70% and 75%, which measures
            // about 4.4:1 on the mint for the 11px label, and under 4.5:1 for
            // both on light's darker green.
            offerLabel: {
                color: '{primary.contrastColor}',
            },
            offerSub: {
                color: '{primary.contrastColor}',
            },
        },
        them: {
            background: '{content.hoverBackground}',
            color: '{text.color}',
        },
        // The offer card — `kind: 'offer'`. The bubble's fill and corners are
        // the text bubble's; its 240px width and the 40px action height are
        // literals in the SFC, since the contract does not tokenize either.
        // The seller's label is the warning hue, as everywhere in the offer
        // flow; an accepted card's is the brand accent.
        offer: {
            padding: '0',
            fontWeight: '400',
        },
        offerBody: {
            padding: '12px 14px 10px',
        },
        offerLabel: {
            fontSize: '11px',
            fontWeight: '800',
            letterSpacing: '0.08em',
            // `color` is scheme-split — see componentTokensByColorScheme below.
            accepted: {
                color: '{primary.color}',
            },
        },
        offerAmount: {
            fontFamily: '{font.mono}',
            fontSize: '26px',
            fontWeight: '800',
        },
        offerSub: {
            fontSize: '12.5px',
            lineHeight: '1.35',
            color: '{text.hoverMutedColor}',
        },
        offerActions: {
            gap: '6px',
            padding: '0 10px 12px',
        },
        // 10px is the token map's locked radius for a small button inside a
        // card. Counter is the neutral secondary button, `{surface.700}` by
        // the map's handoff deviation.
        offerAction: {
            borderRadius: '10px',
            fontFamily: '{font.body}',
            fontSize: '13.5px',
            fontWeight: '700',
            focus: {
                outlineColor: '{primary.color}',
            },
            accept: {
                background: '{primary.color}',
                color: '{primary.contrastColor}',
                hover: {
                    background: '{primary.hoverColor}',
                },
            },
            // `background` and its hover are scheme-split — see
            // componentTokensByColorScheme below.
            counter: {
                color: '{text.color}',
            },
        },
        // Multi-party only. A 1:1 thread never draws the author line and never
        // groups runs, so nothing below applies on the mobile surface.
        groupStart: {
            marginTop: '6px',
        },
        author: {
            padding: '0 0 2px 8px',
            fontSize: '11px',
            fontWeight: '700',
            color: '{text.color}',
            rep: {
                color: '{status.info}',
            },
            auctioneer: {
                color: '{status.warning}',
            },
        },
        // The role in words, beside the name. It steps back to the muted tone
        // and to normal weight so the name keeps the emphasis and the tint —
        // this is the channel that carries the role to a screen reader and to
        // anyone reading the lane in grayscale, not a second decoration.
        authorRole: {
            fontWeight: '400',
            color: '{text.mutedColor}',
        },
        // The `them`-side tint per role. `buyer` and `watcher` declare none:
        // they are separate roles to the sale, not to the eye, so they take the
        // neutral `them` fill.
        role: {
            rep: {
                background: 'color-mix(in srgb, var(--p-status-info) 14%, transparent)',
                color: '{text.color}',
            },
            auctioneer: {
                background: 'color-mix(in srgb, var(--p-status-warning) 14%, transparent)',
                color: '{text.color}',
            },
            clerk: {
                background: '{primary.color}',
                color: '{primary.contrastColor}',
            },
        },
        vehicle: {
            padding: '4px',
        },
        assetCard: {
            borderRadius: '14px',
            background: '{content.background}',
        },
        assetCardMedia: {
            background: '{content.hoverBackground}',
        },
        assetCardMediaFallback: {
            background: 'linear-gradient(135deg, var(--p-content-hover-background), var(--surface-ground))',
        },
        assetCardChip: {
            padding: '4px 8px',
            borderRadius: '6px',
            background: '{status.warning}',
            color: '{status.warningContrast}',
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.06em',
        },
        assetCardBody: {
            padding: '10px 12px',
            gap: '2px',
        },
        assetCardTitle: {
            fontSize: '13px',
            fontWeight: '700',
            color: '{text.color}',
        },
        assetCardPrice: {
            fontSize: '13px',
            fontWeight: '700',
            color: '{primary.color}',
        },
        foot: {
            gap: '4px',
            fontSize: '11px',
            color: '{text.mutedColor}',
        },
        receipt: {
            fontSize: '13px',
            color: '{primary.color}',
        },
    },

    // MessageComposer — .message-composer
    messageComposer: {
        borderTopWidth: '0.5px',
        borderTopColor: '{content.borderColor}',
        background: 'color-mix(in srgb, var(--surface-ground) 96%, transparent)',
        gap: '8px',
        // The row's own padding. The bottom value is a FLOOR: the component
        // raises it to `env(safe-area-inset-bottom)` where a home indicator
        // needs clearing, because a safe-area inset is a device fact rather
        // than a brand decision and the theme has nothing to say about it.
        paddingTop: '12px',
        paddingInline: '16px',
        paddingBottom: '16px',
        row: {
            gap: '10px',
        },
        attach: {
            borderRadius: '22px',
            background: '{content.hoverBackground}',
            color: '{text.hoverMutedColor}',
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
            icon: {
                fontSize: '20px',
            },
        },
        input: {
            background: '{content.hoverBackground}',
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
            borderRadius: '22px',
            padding: '0 16px',
            color: '{text.color}',
            fontSize: '15px',
            placeholder: {
                color: '{text.mutedColor}',
            },
            focus: {
                borderColor: '{primary.color}',
            },
        },
        send: {
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            icon: {
                fontSize: '19px',
            },
        },
    },

    // NoticeBanner — .notice-banner
    //
    // An app-wide notice set inline at the top of a page, from COMPONENTS.md and
    // the Dashboard app-wide notice screen, which agree on the banner: the token
    // map's 14px notice card corner (`{radius.row}`), `10px 12px` padding, a 10px
    // gap, the megaphone at 15px, and the text at 13/1.35 in the primary ink with
    // its key phrase at 700.
    //
    // The tone is the token map's tinted status fill, at the handoff's 10% for
    // the ground and 35% for the hairline, as a `color-mix()` of the status
    // token, with the status token itself for the megaphone: info at rest,
    // warning for maintenance under way. The text keeps the primary ink on both
    // tints, full opacity on a tinted ground.
    //
    // The close button is COMPONENTS.md's 32px button, a circle (the locked
    // 999px literal) with its 12px glyph in the tertiary ink the screen paints,
    // where the handoff's prototype paints the secondary. The screen itself draws
    // a bare glyph flush with the padding rather than the button, which is an
    // open delta under MBP-11. Its hover ground and ink and its focus ring in the
    // accent are authored, since the design draws none.
    noticeBanner: {
        gap: '10px',
        padding: '10px 12px',
        borderWidth: '1px',
        borderRadius: '{radius.row}',
        icon: {
            fontSize: '15px',
        },
        text: {
            color: '{text.color}',
            fontSize: '13px',
            lineHeight: '1.35',
        },
        emphasis: {
            fontWeight: '700',
        },
        dismiss: {
            borderRadius: '999px',
            color: '{text.mutedColor}',
            icon: {
                fontSize: '12px',
            },
            hover: {
                background: 'color-mix(in srgb, var(--p-text-color) 8%, transparent)',
                color: '{text.color}',
            },
            focus: {
                outlineColor: '{primary.color}',
            },
        },
        info: {
            background: 'color-mix(in srgb, var(--p-status-info) 10%, transparent)',
            borderColor: 'color-mix(in srgb, var(--p-status-info) 35%, transparent)',
            icon: {
                color: '{status.info}',
            },
        },
        warning: {
            background: 'color-mix(in srgb, var(--p-status-warning) 10%, transparent)',
            borderColor: 'color-mix(in srgb, var(--p-status-warning) 35%, transparent)',
            icon: {
                color: '{status.warning}',
            },
        },
    },

    // NotificationBanner — .notification-banner
    notificationBanner: {
        background: 'color-mix(in srgb, var(--p-content-background) 92%, transparent)',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '14px',
        boxShadow: '0 8px 24px color-mix(in srgb, var(--p-text-color) 22%, transparent)',
        // The banner's own padding, on the control that fills the banner rather
        // than on the container — padding on the container would be a band of
        // visible banner that pressed nothing. Split per side because the
        // trailing edge has to clear the dismiss control's 44px box, which
        // reaches 46 in from the banner's edge.
        open: {
            gap: '12px',
            paddingTop: '12px',
            paddingRight: '47px',
            paddingBottom: '12px',
            paddingLeft: '14px',
        },
        body: {
            gap: '2px',
        },
        title: {
            fontSize: '14px',
            fontWeight: '600',
            color: '{text.color}',
        },
        preview: {
            fontSize: '13px',
            color: '{text.mutedColor}',
        },
        dismiss: {
            borderRadius: '14px',
            color: '{text.mutedColor}',
            fontSize: '12px',
        },
        // One ring for both controls: they are two halves of one banner, and a
        // keyboard moving between them should not see the treatment change.
        focusVisible: {
            outlineWidth: '2px',
            outlineColor: '{primary.color}',
        },
    },

    // OfferComposer — .offer-composer
    //
    // From the handoff's OfferComposer frame and COMPONENTS.md. The field, the
    // chips and the note all sit on the raised ground with the 14px row radius;
    // what marks a thing live is the brand accent on its border, and a chosen
    // chip adds the selected tint. The delta against MMR is the info hue, which
    // is what MMR reads as everywhere in the flow.
    //
    // Named by the contract's derivation from the selectors rather than by the
    // handoff's list: its `field.focusBorderColor` is `field.filled.borderColor`
    // here, its `chip.selectedBackground` is `chip.selected.background`. Heights
    // (the 64px field, the 48 and 52px chips, the 48px note) are not themable
    // properties under the contract, so they stay literals in the SFC.
    // OfferCard — .offer-card
    //
    // From the handoff's OfferCard frame and COMPONENTS.md, with the My Offers
    // screens for the states the frame does not draw. The card is the preset's
    // card; the thumb is the raised ground with an 11px corner, which
    // COMPONENTS.md and the frame both ask for at 58×44 — a component-owned
    // literal, as the token map's thumbnail row has it for 52×40.
    //
    // The pill is the token map's tinted status fill: the tone at 14% for the
    // ground and the tone itself for the ink, as a `color-mix()` of the status
    // token. Its ink is scheme-split for warning and success — see
    // componentTokensByColorScheme below. Neutral (withdrawn, expired) is the
    // raised ground with the secondary ink.
    //
    // The actions are the locked small in-card button: 40px tall, a 10px corner
    // owned here. Counter is the raised ground with a hairline, as the frame
    // paints it; its hover is scheme-split below. Withdraw is a link in the
    // danger hue, per DESIGN_SYSTEM §7's rule that destructive actions are links.
    offerCard: {
        gap: '10px',
        padding: '14px',
        background: '{content.background}',
        borderWidth: '0.5px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.card}',
        focus: {
            outlineColor: '{primary.color}',
        },
        head: {
            gap: '12px',
        },
        thumb: {
            borderRadius: '11px',
            background: '{content.hoverBackground}',
            color: '{text.mutedColor}',
            icon: {
                fontSize: '16px',
            },
        },
        title: {
            color: '{text.color}',
            fontFamily: '{font.body}',
            fontSize: '15px',
            fontWeight: '700',
        },
        sub: {
            gap: '6px',
            color: '{text.mutedColor}',
            fontSize: '11.5px',
        },
        numbers: {
            gap: '8px',
        },
        label: {
            color: '{text.mutedColor}',
            fontSize: '11px',
        },
        amount: {
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '17px',
            fontWeight: '800',
        },
        theirs: {
            color: '{text.hoverMutedColor}',
            fontSize: '12px',
            fontWeight: '600',
            countered: {
                color: '{status.warning}',
            },
        },
        actions: {
            gap: '8px',
        },
        awaiting: {
            gap: '6px',
            color: '{status.info}',
            fontSize: '13px',
            fontWeight: '600',
        },
        action: {
            borderRadius: '10px',
            fontFamily: '{font.body}',
            fontSize: '13.5px',
            fontWeight: '700',
            primary: {
                background: '{primary.color}',
                color: '{primary.contrastColor}',
                hover: {
                    background: '{primary.hoverColor}',
                },
            },
            // `secondary.hover.background` is scheme-split — see
            // componentTokensByColorScheme below.
            secondary: {
                background: '{content.hoverBackground}',
                borderColor: '{content.borderColor}',
                color: '{text.color}',
            },
        },
        link: {
            color: '{status.danger}',
        },
    },

    offerComposer: {
        gap: '8px',
        field: {
            gap: '6px',
            padding: '0 16px',
            borderRadius: '{radius.row}',
            background: '{content.hoverBackground}',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            filled: {
                borderColor: '{primary.color}',
            },
        },
        currency: {
            color: '{text.mutedColor}',
            fontSize: '20px',
            fontWeight: '700',
        },
        amount: {
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '26px',
            fontWeight: '800',
        },
        delta: {
            color: '{status.info}',
            fontSize: '12px',
            fontWeight: '600',
        },
        chips: {
            gap: '8px',
        },
        chip: {
            gap: '1px',
            borderRadius: '{radius.row}',
            background: '{content.hoverBackground}',
            borderWidth: '1px',
            borderColor: '{content.borderColor}',
            color: '{text.color}',
            focus: {
                outlineColor: '{primary.color}',
            },
            selected: {
                background: '{highlight.background}',
                borderColor: '{primary.color}',
            },
            label: {
                color: '{text.hoverMutedColor}',
                fontSize: '10.5px',
                fontWeight: '700',
            },
            amount: {
                fontFamily: '{font.mono}',
                fontSize: '12.5px',
                fontWeight: '700',
            },
        },
        note: {
            gap: '10px',
            padding: '0 14px',
            borderRadius: '{radius.row}',
            background: '{content.hoverBackground}',
            borderWidth: '1px',
            borderColor: '{content.borderColor}',
            focus: {
                borderColor: '{primary.color}',
            },
            icon: {
                color: '{text.mutedColor}',
                fontSize: '14px',
            },
            input: {
                color: '{text.color}',
                fontFamily: '{font.body}',
                fontSize: '14px',
                placeholder: {
                    color: '{text.mutedColor}',
                },
            },
            count: {
                color: '{text.mutedColor}',
                fontSize: '11px',
            },
        },
    },

    // OfferGapCard — .offer-gap-card
    //
    // From the handoff's OfferGap frame and COMPONENTS.md. The buyer's number is
    // the brand accent and the other side's is the warning hue — the seller's
    // color everywhere in the offer flow — so both point at the semantic tier
    // and follow the scheme. The bar's two fills are those same two hues at the
    // frame's 40% rather than new tokens: the token map's rule for tinted
    // status fills is a `color-mix()` of the status token.
    //
    // MIXED INTO THE RAISED GROUND, NOT INTO TRANSPARENT. The frame lays both
    // segments side by side over a `#1f1f1f` base — `{content.hoverBackground}`
    // — and measured off the screen PNG the fill is exactly the mint at 40% over
    // it, the track exactly the warning at 40% over it. Mixed into transparent,
    // the fill composites over the warning track beneath it and comes out olive.
    offerGapCard: {
        gap: '10px',
        background: '{content.background}',
        borderWidth: '0.5px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.card}',
        padding: '12px 14px',
        label: {
            color: '{text.mutedColor}',
            fontSize: '11px',
        },
        value: {
            fontFamily: '{font.mono}',
            fontSize: '20px',
            fontWeight: '800',
        },
        yours: {
            color: '{primary.color}',
        },
        theirs: {
            color: '{status.warning}',
        },
        apart: {
            color: '{text.color}',
            fontSize: '15px',
            fontWeight: '700',
        },
        bar: {
            height: '8px',
            borderRadius: '4px',
            trackBackground: 'color-mix(in srgb, var(--p-status-warning) 40%, var(--p-content-hover-background))',
            fillBackground: 'color-mix(in srgb, var(--p-primary-color) 40%, var(--p-content-hover-background))',
        },
    },

    // OfferHistoryList — .offer-history-list
    //
    // From the handoff's OfferTimeline frame and the Make Offer screens, which
    // agree with each other where COMPONENTS.md does not: an uppercase eyebrow
    // header, and one line per move with the amount and the time at its end.
    // The list group is one step deeper than the sheet it sits in, with the
    // Josh-locked 16px list group radius as a component-owned literal
    // (design-token-map.md §4). Its `background` is scheme-split — see
    // componentTokensByColorScheme below.
    //
    // Colour by who moved: the buyer is the brand accent on the dot and plain
    // ink on the name, the seller the warning hue on both, as everywhere in the
    // offer flow. A move a later number replaced, and the lane closing, fall to
    // the tertiary ink. The Edit chip is the selected tint with the accent at
    // the frame's 40% for its border, a `color-mix()` of the token as the token
    // map's rule for tinted fills has it.
    offerHistoryList: {
        // `background` is scheme-split — see componentTokensByColorScheme below.
        borderWidth: '0.5px',
        borderColor: '{content.borderColor}',
        borderRadius: '16px',
        padding: '6px 14px',
        header: {
            padding: '6px 0 4px',
        },
        title: {
            color: '{text.mutedColor}',
            fontSize: '11px',
            fontWeight: '800',
            letterSpacing: '0.1em',
        },
        count: {
            color: '{text.mutedColor}',
            fontSize: '11.5px',
        },
        row: {
            padding: '11px 0',
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
        },
        line: {
            gap: '12px',
        },
        dot: {
            borderRadius: '999px',
            buyer: {
                background: '{primary.color}',
            },
            seller: {
                background: '{status.warning}',
            },
            system: {
                borderWidth: '1.5px',
                borderColor: '{text.mutedColor}',
            },
            superseded: {
                background: '{text.mutedColor}',
            },
        },
        who: {
            fontSize: '14px',
            fontWeight: '600',
            buyer: {
                color: '{text.color}',
            },
            seller: {
                color: '{status.warning}',
            },
            system: {
                color: '{text.mutedColor}',
            },
            superseded: {
                color: '{text.mutedColor}',
            },
            editing: {
                color: '{text.color}',
            },
        },
        whenSub: {
            color: '{text.mutedColor}',
            fontSize: '11.5px',
        },
        note: {
            color: '{text.hoverMutedColor}',
            fontSize: '12.5px',
        },
        amount: {
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '15px',
            fontWeight: '700',
            superseded: {
                color: '{text.mutedColor}',
            },
        },
        when: {
            color: '{text.mutedColor}',
            fontSize: '11.5px',
        },
        edit: {
            gap: '5px',
            padding: '0 10px',
            borderRadius: '999px',
            background: '{highlight.background}',
            borderWidth: '1px',
            borderColor: 'color-mix(in srgb, var(--p-primary-color) 40%, transparent)',
            color: '{primary.color}',
            fontFamily: '{font.body}',
            fontSize: '12.5px',
            fontWeight: '700',
            hover: {
                borderColor: '{primary.color}',
            },
            focus: {
                outlineColor: '{primary.color}',
            },
            icon: {
                fontSize: '11px',
            },
        },
        editing: {
            gap: '10px',
        },
        cancel: {
            color: '{text.hoverMutedColor}',
            fontFamily: '{font.body}',
            fontSize: '13px',
            fontWeight: '600',
            focus: {
                outlineColor: '{primary.color}',
            },
        },
        update: {
            gap: '8px',
            borderRadius: '{border.radius.xl}',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontFamily: '{font.body}',
            fontSize: '15px',
            fontWeight: '700',
            hover: {
                background: '{primary.hoverColor}',
            },
            focus: {
                outlineColor: '{primary.color}',
            },
            disabled: {
                background: '{primary.color}',
            },
        },
    },

    // PageHeader — .page-header
    pageHeader: {
        background: 'var(--surface-ground)',
        color: '{text.color}',
        fontFamily: '{font.body}',
        actions: {
            gap: '8px',
        },
        // An icon button from the `actions` prop: the 36px disc Messages drew by
        // hand, filled with the brand while it is pressed.
        action: {
            size: '36px',
            borderRadius: '999px',
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
            background: '{content.background}',
            color: '{text.hoverMutedColor}',
            icon: {
                fontSize: '17px',
            },
            pressed: {
                background: '{primary.color}',
                color: '{primary.contrastColor}',
            },
            focus: {
                outlineColor: '{primary.color}',
            },
        },
        // The title row — 12px between the title and the actions at its end.
        titleRow: {
            columnGap: '12px',
        },
        title: {
            fontFamily: '{font.display}',
            fontWeight: '{display.weight}',
            letterSpacing: '{display.tracking}',
            // 24px rather than iOS's 34px large title: a sale or page name has
            // to read in full on a phone, and the title switcher is one line.
            fontSize: '24px',
            lineHeight: '1.1',
            color: '{text.color}',
            // The title as a control (Live's sale switcher): the words, then a
            // muted chevron that says the title opens something.
            button: {
                gap: '10px',
                chevron: {
                    fontSize: '14px',
                    color: '{text.mutedColor}',
                },
            },
        },
        subtitle: {
            fontSize: '13px',
            color: '{text.hoverMutedColor}',
            lineHeight: '1.4',
        },
    },

    // PlanningWorksheet — .planning-worksheet
    planningWorksheet: {
        background: '{content.background}',
        borderWidth: '0.5px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.card}',
        fontFamily: '{font.body}',
        color: '{text.color}',
        header: {
            padding: '20px 18px 4px',
        },
        kicker: {
            fontFamily: '{font.display}',
            fontSize: '11px',
            fontWeight: '600',
            letterSpacing: '0.14em',
            color: '{primary.color}',
        },
        hint: {
            fontSize: '13px',
            lineHeight: '1.45',
            color: '{text.hoverMutedColor}',
            strong: {
                color: '{text.color}',
                fontWeight: '600',
            },
        },
        moneyRow: {
            gap: '12px',
            padding: '14px 0',
            borderBottomWidth: '0.5px',
            borderBottomColor: '{content.borderColor}',
        },
        rowLabel: {
            fontSize: '15px',
            fontWeight: '600',
            color: '{text.color}',
        },
        rowHint: {
            fontSize: '12px',
            color: '{text.mutedColor}',
        },
        amount: {
            gap: '2px',
            // `filled`, not `empty`: both of these are read under a
            // `:not(.is-empty)` selector, so they paint the prefix of an amount
            // that HAS a figure in it. They were named for the state they do not
            // apply in, which is invisible from here and misleading from the
            // token list.
            amountPrefix: {
                accent: {
                    filled: {
                        color: '{primary.color}',
                    },
                },
            },
            sm: {
                amountPrefix: {
                    fontSize: '15px',
                    fontWeight: '400',
                    filled: {
                        color: '{text.hoverMutedColor}',
                    },
                },
                amountInput: {
                    fontSize: '15px',
                    fontWeight: '600',
                },
            },
        },
        amountPrefix: {
            fontFamily: '{font.mono}',
            fontSize: '18px',
            fontWeight: '600',
            color: '{text.mutedColor}',
        },
        amountInput: {
            background: 'transparent',
            fontFamily: '{font.mono}',
            fontSize: '18px',
            fontWeight: '700',
            color: '{text.color}',
            accent: {
                color: '{primary.color}',
            },
            placeholder: {
                color: '{text.mutedColor}',
            },
        },
        section: {
            padding: '16px 18px 4px',
        },
        sectionLabel: {
            fontFamily: '{font.display}',
            fontSize: '11px',
            fontWeight: '600',
            letterSpacing: '0.1em',
            color: '{text.mutedColor}',
        },
        sectionSum: {
            fontFamily: '{font.mono}',
            fontSize: '14px',
            fontWeight: '700',
            color: '{text.color}',
            empty: {
                color: '{text.mutedColor}',
            },
        },
        costRow: {
            gap: '10px',
            padding: '11px 0',
            borderBottomWidth: '0.5px',
            borderBottomColor: '{content.borderColor}',
        },
        remove: {
            background: 'transparent',
            color: '{text.mutedColor}',
        },
        costName: {
            background: 'transparent',
            color: '{text.color}',
            fontFamily: '{font.body}',
            fontSize: '15px',
            placeholder: {
                color: '{text.mutedColor}',
            },
        },
        add: {
            paddingTop: '12px',
        },
        addButton: {
            gap: '7px',
            background: 'transparent',
            color: '{primary.color}',
            fontFamily: '{font.body}',
            fontSize: '14px',
            fontWeight: '600',
        },
        addChip: {
            borderRadius: '10px',
            background: '{highlight.background}',
            color: '{primary.color}',
        },
        presets: {
            gap: '7px',
        },
        preset: {
            padding: '8px 13px',
            borderRadius: '999px',
            background: '{content.hoverBackground}',
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
            color: '{text.color}',
            fontFamily: '{font.body}',
            fontSize: '13px',
            fontWeight: '600',
            custom: {
                background: 'transparent',
                borderWidth: '1px',
                borderColor: '{formField.borderColor}',
                color: '{text.hoverMutedColor}',
            },
        },
        notes: {
            background: 'var(--surface-ground)',
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
            borderRadius: '14px',
            padding: '12px 14px',
            color: '{text.color}',
            fontFamily: '{font.body}',
            fontSize: '14px',
            lineHeight: '1.5',
            placeholder: {
                color: '{text.mutedColor}',
            },
            focus: {
                borderColor: '{primary.color}',
            },
        },
        summary: {
            padding: '4px 16px',
            background: 'var(--surface-ground)',
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
            borderRadius: '14px',
        },
        summaryRow: {
            padding: '11px 0',
            borderBottomWidth: '0.5px',
            borderBottomColor: '{content.borderColor}',
            fontSize: '14px',
            color: '{text.hoverMutedColor}',
            dd: {
                fontFamily: '{font.mono}',
                color: '{text.color}',
            },
            empty: {
                color: '{text.mutedColor}',
            },
            total: {
                padding: '12px 0 4px',
                fontSize: '16px',
                dt: {
                    color: '{text.color}',
                    fontWeight: '700',
                },
                dd: {
                    fontWeight: '700',
                },
            },
            margin: {
                padding: '4px 0 12px',
                dt: {
                    fontSize: '13px',
                    color: '{text.mutedColor}',
                },
                dd: {
                    fontSize: '13px',
                    fontWeight: '700',
                },
                under: {
                    color: '{primary.color}',
                },
                over: {
                    color: '{status.danger}',
                },
            },
        },
        saveWrap: {
            padding: '16px 18px 20px',
        },
        // The message above the control when the parent reports the save did not
        // land. Danger rather than the accent: it is the one line in the form
        // that says money was not kept.
        saveError: {
            color: '{status.danger}',
            fontSize: '13px',
            fontWeight: '600',
        },
        save: {
            gap: '8px',
            borderRadius: '15px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontFamily: '{font.body}',
            fontWeight: '700',
            fontSize: '16px',
            // Nothing to send, or a send already in flight. Distinct from
            // `saved` below, which is an outcome rather than an absence and
            // carries a border and a check to say so.
            disabled: {
                background: '{content.hoverBackground}',
                color: '{text.mutedColor}',
            },
            saved: {
                background: '{content.hoverBackground}',
                color: '{text.hoverMutedColor}',
                borderWidth: '0.5px',
                borderColor: '{content.borderColor}',
            },
        },
    },

    // PurchaseCard — .purchase-card
    //
    // One of the buyer's purchases on My Purchases, from COMPONENTS.md (option
    // 3a) and the handoff's PurchaseCard frame, with the My Purchases screen for
    // the If-Sale variant the frame does not draw. The card is OfferCard's: the
    // preset's card with the 0.5px hairline and the 18px corner, 14px in and 10px
    // between its rows. The thumb is the raised ground with an 11px corner, which
    // COMPONENTS.md and the frame both ask for at 84×56 — a component-owned
    // literal, as OfferCard's is at 58×44.
    //
    // The price is mono 15/700 in the primary ink, with the word after it and
    // the sale · city line in the tertiary. The MMR is the info hue, as it is
    // everywhere in the flow. The hint beside the chip is the tertiary ink at
    // 11px, DESIGN_SYSTEM §2's hint, since the frame does not draw one.
    //
    // The actions are PrimeVue Buttons painted as the locked small in-card
    // button, as OfferCard's native ones are: a 10px corner owned here (the 40px
    // height is the SFC's), the label at 12.5/700 with the frame's -0.01em
    // tracking and its icon at 15px. Checkout is the brand accent; Invoice,
    // Arbitration and View offer are the raised ground with a hairline, whose
    // hover is scheme-split — see componentTokensByColorScheme below.
    purchaseCard: {
        gap: '10px',
        padding: '14px',
        background: '{content.background}',
        borderWidth: '0.5px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.card}',
        focus: {
            outlineColor: '{primary.color}',
        },
        head: {
            gap: '12px',
        },
        thumb: {
            borderRadius: '11px',
            background: '{content.hoverBackground}',
            color: '{text.mutedColor}',
            icon: {
                fontSize: '16px',
            },
        },
        heading: {
            gap: '6px',
        },
        title: {
            color: '{text.color}',
            fontSize: '15px',
            fontWeight: '700',
        },
        where: {
            color: '{text.mutedColor}',
            fontSize: '11.5px',
        },
        numbers: {
            gap: '6px',
        },
        price: {
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '15px',
            fontWeight: '700',
        },
        priceLabel: {
            color: '{text.mutedColor}',
            fontSize: '11px',
        },
        mmr: {
            color: '{status.info}',
            fontSize: '11px',
        },
        mmrAmount: {
            fontFamily: '{font.mono}',
            fontWeight: '700',
        },
        status: {
            gap: '6px',
        },
        hint: {
            color: '{text.mutedColor}',
            fontSize: '11px',
        },
        actions: {
            gap: '8px',
        },
        action: {
            padding: '0 8px',
            gap: '5px',
            borderRadius: '10px',
            fontSize: '12.5px',
            letterSpacing: '-0.01em',
            icon: {
                fontSize: '15px',
            },
            label: {
                fontWeight: '700',
            },
            primary: {
                background: '{primary.color}',
                color: '{primary.contrastColor}',
                hover: {
                    background: '{primary.hoverColor}',
                },
            },
            // `secondary.hover.background` is scheme-split — see
            // componentTokensByColorScheme below.
            secondary: {
                background: '{content.hoverBackground}',
                borderColor: '{content.borderColor}',
                color: '{text.color}',
            },
        },
    },

    // PurchaseStatusChip — .purchase-status-chip
    //
    // Where a purchase stands on its My Purchases card, from COMPONENTS.md and
    // MBP-13: a pill 20 tall (the height is the SFC's, not a themable property)
    // with `0 7px` padding, the token map's 999px pill corner, and its word at
    // 10.5/800. `won` reads "Purchased" on the success tint and `if` reads
    // "If-Sale" on the warning tint: the tone at 16% as a `color-mix()` of the
    // status token, AttentionItem's plate recipe, with the tone itself for the
    // word. The ink is scheme-split — see componentTokensByColorScheme below.
    //
    // The Proposed Components frame draws this pill as OfferCard's is, 11/700 at
    // `3px 8px`, with Purchased in the brand mint at 16% and If-Sale at 14%, and
    // the My Purchases screen follows the frame. Those are open deltas under
    // MBP-13. In dark the mint and the success hue are the same `#3ee0a4`.
    purchaseStatusChip: {
        padding: '0 7px',
        borderRadius: '999px',
        fontSize: '10.5px',
        fontWeight: '800',
        // `success.color` and `warning.color` are scheme-split — see
        // componentTokensByColorScheme below.
        success: {
            background: 'color-mix(in srgb, var(--p-status-success) 16%, transparent)',
        },
        warning: {
            background: 'color-mix(in srgb, var(--p-status-warning) 16%, transparent)',
        },
    },

    // RangeSliderScreen — .range-slider-screen
    rangeSliderScreen: {
        background: 'var(--surface-ground)',
        color: '{text.color}',
        fontFamily: '{font.body}',
        nav: {
            gap: '8px',
        },
        back: {
            gap: '2px',
            background: 'transparent',
            color: '{primary.color}',
            fontSize: '16px',
            fontWeight: '500',
            padding: '4px',
            icon: {
                fontSize: '13px',
            },
        },
        title: {
            fontSize: '17px',
            fontWeight: '600',
        },
        clear: {
            background: 'transparent',
            color: '{primary.color}',
            fontSize: '15px',
            fontWeight: '500',
            padding: '4px',
        },
        readout: {
            fontSize: '28px',
            fontWeight: '700',
            padding: '28px 16px 24px',
            color: '{text.color}',
        },
        inputs: {
            gap: '12px',
            padding: '0 24px 8px',
        },
        input: {
            gap: '4px',
            fontSize: '12px',
            color: '{text.mutedColor}',
            field: {
                padding: '12px 14px',
                borderRadius: '12px',
                borderWidth: '1px',
                borderColor: '{content.borderColor}',
                background: '{content.hoverBackground}',
                color: '{text.color}',
                fontSize: '16px',
            },
            focus: {
                field: {
                    borderColor: '{primary.color}',
                },
            },
        },
        dash: {
            paddingBottom: '14px',
            color: '{text.mutedColor}',
        },
        track: {
            background: '{content.hoverBackground}',
            borderRadius: '2px',
        },
        fill: {
            background: '{primary.color}',
            borderRadius: '2px',
        },
        thumb: {
            borderRadius: '13px',
            background: '{surface.0}',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
            // The keyboard focus ring, which carries the resting shadow with it
            // — a focused thumb is still a thumb sitting above the track.
            focusVisible: {
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 0 4px color-mix(in srgb, var(--p-primary-color) 35%, transparent)',
            },
        },
        presets: {
            background: '{content.background}',
            borderRadius: '{radius.row}',
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
        },
        preset: {
            padding: '12px 16px',
            background: 'transparent',
            borderBottomWidth: '0.5px',
            borderBottomColor: '{content.borderColor}',
            fontSize: '16px',
            color: '{text.color}',
            active: {
                background: '{content.hoverBackground}',
            },
            on: {
                color: '{primary.color}',
                fontWeight: '600',
            },
            icon: {
                color: '{primary.color}',
                fontSize: '14px',
            },
        },
        note: {
            padding: '14px 20px',
            fontSize: '12px',
            color: '{text.mutedColor}',
        },
        apply: {
            background: 'linear-gradient(180deg, transparent, var(--surface-ground) 30%)',
        },
        applyButton: {
            borderRadius: '14px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontWeight: '700',
            fontSize: '16px',
        },
    },

    // ScopeSegment — .scope-segment (Find a sale Live/Today/Week/All)
    conversationRow: {
        gap: '13px',
        color: '{text.color}',
        focusRing: '{primary.color}',
        md: {
            padding: '15px',
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
            borderRadius: 'var(--p-radius-card)',
            background: '{content.background}',
        },
        sm: {
            gap: '12px',
            padding: '10px 16px 10px 14px',
            name: { fontWeight: '700' },
            time: { fontFamily: '{font.mono}', fontSize: '11px' },
            preview: { fontSize: '12.5px' },
        },
        body: { gap: '4px' },
        head: { gap: '8px' },
        name: { fontSize: '15px', fontWeight: '600', letterSpacing: '-0.01em' },
        about: { fontSize: '13.5px', color: '{text.hoverMutedColor}' },
        time: {
            fontSize: '13px',
            fontWeight: '600',
            color: '{text.mutedColor}',
            unread: { color: '{primary.color}' },
        },
        subline: { fontSize: '13px', color: '{text.mutedColor}' },
        preview: {
            fontSize: '14px',
            color: '{text.hoverMutedColor}',
            unread: { fontWeight: '600', color: '{text.color}' },
        },
        typing: { color: '{primary.color}' },
        count: {
            padding: '0 7px',
            borderRadius: '11px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontSize: '12px',
            fontWeight: '700',
        },
        dot: { background: '{primary.color}' },
    },
    dismissibleChip: {
        gap: '6px',
        minHeight: '36px',
        padding: '7px 10px 7px 13px',
        borderWidth: '0.5px',
        borderRadius: '999px',
        fontSize: '13px',
        fontWeight: '700',
        focusRing: '{primary.color}',
        label: { maxWidth: '200px' },
        remove: { size: '18px', borderRadius: '999px', icon: { fontSize: '10px' } },
        neutral: {
            borderColor: '{content.borderColor}',
            background: '{content.hoverBackground}',
            color: '{text.color}',
            remove: {
                background: 'color-mix(in srgb, var(--p-text-color) 8%, transparent)',
                color: '{text.hoverMutedColor}',
            },
        },
        accent: {
            borderColor: 'transparent',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            remove: {
                background: 'color-mix(in srgb, var(--p-primary-contrast-color) 18%, transparent)',
                color: '{primary.contrastColor}',
            },
        },
    },
    toggleRow: {
        gap: '12px',
        padding: '14px 16px',
        body: { gap: '2px' },
        label: { fontSize: '15px', fontWeight: '600', color: '{text.color}' },
        sub: { fontSize: '13px', color: '{text.mutedColor}' },
    },
    swipeRevealRow: {
        borderRadius: '16px',
        focusRing: '{primary.color}',
        menu: {
            gap: '12px',
            padding: '0 14px',
        },
        action: { gap: '6px' },
        circle: { borderRadius: '999px', icon: { fontSize: '18px' } },
        label: { fontSize: '11px', fontWeight: '700' },
        info: {
            color: '{status.info}',
            circle: {
                background: 'color-mix(in srgb, var(--p-status-info) 22%, transparent)',
                color: '{status.info}',
            },
        },
        // The saved heart is the one danger action: a white heart on the same
        // red plate saved carries everywhere else in the app.
        danger: {
            color: '{status.danger}',
            circle: { background: '{saved.background}', color: '{saved.color}' },
        },
        neutral: {
            color: '{text.hoverMutedColor}',
            circle: { background: '{content.hoverBackground}', color: '{text.color}' },
        },
    },
    scopeSegment: {
        gap: '8px',
        pill: {
            height: '36px',
            padding: '0 12px',
            borderWidth: '0.5px',
            borderColor: 'color-mix(in srgb, var(--p-text-color) 14%, transparent)',
            borderRadius: '999px',
            background: '{content.background}',
            color: '{text.hoverMutedColor}',
            fontSize: '13px',
            fontWeight: '700',
            selected: {
                borderColor: 'transparent',
                background: '{primary.color}',
                color: '{primary.contrastColor}',
            },
        },
        count: {
            minWidth: '18px',
            height: '18px',
            padding: '0 5px',
            borderRadius: '999px',
            background: 'color-mix(in srgb, var(--p-text-color) 16%, transparent)',
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '11px',
            fontWeight: '800',
            selected: {
                background: '{primary.contrastColor}',
                color: '{primary.color}',
            },
            // An unread tally (Messages): the brand fill, so the count reads as
            // something waiting rather than a census.
            accent: {
                background: '{primary.color}',
                color: '{primary.contrastColor}',
            },
        },
    },

    // DayStrip — .day-strip (Find a sale week strip)
    dayStrip: {
        gap: '8px',
        // Square: as wide as it is tall.
        cell: {
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            background: '{content.hoverBackground}',
            color: '{text.hoverMutedColor}',
            selected: {
                background: '{primary.color}',
                color: '{primary.contrastColor}',
            },
        },
        weekday: {
            fontSize: '10.5px',
            fontWeight: '600',
            letterSpacing: '0.04em',
        },
        date: {
            fontFamily: '{font.mono}',
            fontSize: '15px',
            fontWeight: '700',
        },
        dot: {
            size: '4px',
            background: '{primary.color}',
            selected: {
                background: '{primary.contrastColor}',
            },
        },
    },

    // SaleCard — .sale-card
    saleCard: {
        background: '{content.background}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '16px',
        padding: '14px',
        color: '{text.color}',
        live: {
            // SoT 9b / Find-a-sale: live frame is mint, not the danger-tint
            // that read as a red card border on dark.
            borderColor: 'color-mix(in srgb, var(--p-primary-color) 55%, transparent)',
        },
        selected: {
            borderColor: '{primary.color}',
            background: '{highlight.background}',
        },
        // The stretched open button's keyboard focus ring, drawn round the card.
        open: {
            focusRing: {
                width: '2px',
                color: '{primary.color}',
            },
        },
        head: {
            gap: '13px',
        },
        tile: {
            borderRadius: '12px',
            background: '{content.hoverBackground}',
            color: '{text.hoverMutedColor}',
            live: {
                background: '{highlight.background}',
                color: '{primary.color}',
            },
            icon: {
                fontSize: '20px',
                lineHeight: '1',
            },
        },
        name: {
            fontSize: '16px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
            color: '{text.color}',
        },
        chips: {
            gap: '6px 8px',
        },
        type: {
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.06em',
            padding: '4px 9px',
            borderRadius: '999px',
            // SOLID FILLS WITH THE CONTRAST INK, not a 16% tint of the hue
            // under ink of that same hue. The tint these replace could not
            // clear the 4.5:1 axe holds 10px bold text to — measured 4.36 for
            // simulcast and 4.01 for timed on a white card, 3.90 and 3.62 on
            // the attended card's highlight ground — and SaleCard's promotion
            // to wave 2 turns that from a report into a failure.
            //
            // The preset already made this exact call once, for the same
            // reason: `components.tag.colorScheme` in index.mjs flips Aura's
            // 16% translucent tint to a solid fill with the contrast color on
            // top, because auction state has to carry at a glance on a busy
            // card. A sale's auction type is that, on the same card.
            simulcast: {
                background: '{status.danger}',
                color: '{status.dangerContrast}',
            },
            automated: {
                background: '{status.info}',
                color: '{status.infoContrast}',
            },
            timed: {
                background: '{status.warning}',
                color: '{status.warningContrast}',
            },
            // The chip an auction type the card does not recognize falls to.
            // Named for what it is rather than for a type, so that no real
            // auction type shares the card's shrug.
            unrecognized: {
                background: '{content.hoverBackground}',
                color: '{text.hoverMutedColor}',
            },
        },
        status: {
            fontSize: '13px',
            color: '{text.hoverMutedColor}',
            live: {
                // Live status line ink matches the mint frame (SoT 9b).
                color: '{primary.color}',
            },
        },
        liked: {
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.04em',
            padding: '4px 9px',
            borderRadius: '999px',
            // Solid amber plate with contrast ink — quieter than the loud
            // type chips, matching SoT's ★ liked treatment.
            background: '{status.warning}',
            color: '{status.warningContrast}',
            icon: {
                fontSize: '9px',
            },
        },
    },

    // SaleStatusBanner — .sale-status-banner
    saleStatusBanner: {
        gap: '10px',
        // THE WORDS ARE NEUTRAL. A tint of a status color under that same status
        // color as ink cannot clear the 4.5:1 contrast floor — the paused
        // pairing measured 4.15:1 in light — so the label and the inline line
        // read the ordinary text ink, and the state is carried by the fill, the
        // border and the glyph instead. The reason line already did this.
        color: '{text.color}',
        reason: {
            color: '{text.color}',
        },
        // The state groups sit one level up from the arrangements because BOTH
        // blocks read them. They were declared under `banner` while the stacked
        // block read them anyway, which the token gate cannot see: it checks
        // that a component reads its own block, not the path inside it.
        // 6% rather than 14%: warning ink on a warning tint fails AA at 14%
        // (4.15:1); at 6% the fill is still unmistakable and clears 4.5:1 when
        // label ink is status.warning. Org SaleStatusBanner keeps neutral text
        // ink on the banner — this tint still carries the state.
        paused: {
            background: 'color-mix(in srgb, var(--p-status-warning) 6%, transparent)',
            borderWidth: '1px',
            borderColor: 'color-mix(in srgb, var(--p-status-warning) 45%, transparent)',
            icon: {
                color: '{status.warning}',
            },
        },
        finished: {
            background: 'color-mix(in srgb, var(--p-status-danger) 12%, transparent)',
            borderWidth: '1px',
            borderColor: 'color-mix(in srgb, var(--p-status-danger) 40%, transparent)',
            icon: {
                color: '{status.danger}',
            },
        },
        banner: {
            padding: '12px 14px',
            borderRadius: '16px',
            fontSize: '13px',
            lineHeight: '1.35',
            icon: {
                fontSize: '16px',
            },
        },
        stacked: {
            padding: '10px 12px',
            borderRadius: '12px',
            icon: {
                fontSize: '15px',
            },
            line: {
                // The muted ink measured 4.00:1 on the paused tint and 3.99:1 on
                // the finished one, so the secondary line steps up one tone —
                // still visibly secondary, and clear of the floor in both
                // schemes.
                fontSize: '12px',
                color: '{text.hoverMutedColor}',
            },
            reason: {
                fontSize: '12px',
            },
        },
        label: {
            stacked: {
                fontSize: '11px',
                fontWeight: '800',
                letterSpacing: '0.1em',
            },
        },
        chip: {
            letterSpacing: '0.04em',
        },
    },

    // SaleDeskNoticesSheet — .sale-desk-notices-sheet
    //
    // The sale desk's notices, from COMPONENTS.md, whose numbers are the Chat
    // Prototype's sheet. Sheet draws the panel, so what is here is inside it.
    //
    // The head is 12px apart: a 40px flag plate with a 12px corner, a
    // component-owned literal (the token map's locked 10px is for 32 to 36px
    // plates), taking SaleDeskStrip's danger tint at 14% with the flag in the hue
    // at 18px; the title at 20/700 with the design system's -0.01em sheet title
    // tracking, in the sheet's ink; the sale in the secondary ink at 13px; and
    // the 40px bell, the token map's 999px circle, on the raised ground with the
    // primary ink and a 16px glyph. A muted bell takes the plate's recipe. Under
    // the pointer the rest ground mixes 8% of the ink into itself, which lifts it
    // in dark and deepens it in light without a scheme split, and the muted one
    // deepens its tint to 22%.
    //
    // The list group is OfferHistoryList's: a step deeper than the sheet, inside
    // the hairline, with the token map's locked 16px list group corner. The
    // boards draw it at 18. Its `background` is scheme-split — see
    // componentTokensByColorScheme below. Keyboard focus rings it in the accent,
    // as it rings the bell, 2px inside its edge. A notice is `14px 16px` with 8px
    // between its lines and a hairline under it; the tags are 6px apart and 8px
    // from the time. A tag is 20px tall at `0 7px` with a 6px corner, mono at
    // 10.5/800, on the raised ground in the secondary ink, and the hot lane's is
    // the yellow auction light with `{surface.950}`, the ink every yellow plate
    // in the library takes. The time is 11px in the tertiary ink, the notice
    // 15/1.4 in the sheet's.
    //
    // The foot is the tertiary ink at 12.5px with an 11px clock 6px before it,
    // 12px under the list. Its sides and its foot are Sheet's body padding.
    saleDeskNoticesSheet: {
        head: {
            gap: '12px',
        },
        plate: {
            borderRadius: '12px',
            background: 'color-mix(in srgb, var(--p-status-danger) 14%, transparent)',
            color: '{status.danger}',
        },
        icon: {
            fontSize: '18px',
        },
        title: {
            fontSize: '20px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
        },
        subtitle: {
            fontSize: '13px',
            color: '{text.hoverMutedColor}',
        },
        mute: {
            borderRadius: '999px',
            background: '{content.hoverBackground}',
            color: '{text.color}',
            icon: {
                fontSize: '16px',
            },
            hover: {
                background: 'color-mix(in srgb, var(--p-text-color) 8%, var(--p-content-hover-background))',
            },
            focus: {
                outlineColor: '{primary.color}',
            },
            muted: {
                background: 'color-mix(in srgb, var(--p-status-danger) 14%, transparent)',
                color: '{status.danger}',
                hover: {
                    background: 'color-mix(in srgb, var(--p-status-danger) 22%, transparent)',
                },
            },
        },
        list: {
            // `background` is scheme-split — see componentTokensByColorScheme below.
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
            borderRadius: '16px',
            focus: {
                outlineColor: '{primary.color}',
            },
        },
        notice: {
            gap: '8px',
            padding: '14px 16px',
            borderBottomWidth: '0.5px',
            borderBottomColor: '{content.borderColor}',
        },
        meta: {
            gap: '8px',
        },
        tags: {
            gap: '6px',
        },
        tag: {
            padding: '0 7px',
            borderRadius: '6px',
            background: '{content.hoverBackground}',
            color: '{text.hoverMutedColor}',
            fontFamily: '{font.mono}',
            fontSize: '10.5px',
            fontWeight: '800',
            hot: {
                background: '{auctionLight.yellow}',
                color: '{surface.950}',
            },
        },
        time: {
            fontSize: '11px',
            color: '{text.mutedColor}',
        },
        text: {
            fontSize: '15px',
            lineHeight: '1.4',
        },
        footer: {
            gap: '6px',
            padding: '12px 0 0',
            fontSize: '12.5px',
            color: '{text.mutedColor}',
            icon: {
                fontSize: '11px',
            },
        },
    },

    // SaleDeskStrip — .sale-desk-strip
    //
    // The sale desk's latest notice, from COMPONENTS.md, whose numbers are the
    // Watch prototype's strip to the pixel: `9px 12px` inside a 1px hairline in
    // the danger hue at 35%, the token map's 14px notice card corner
    // (`{radius.row}`), and a 10px gap. The ground is the card at 82% so the
    // Watch stream shows through it; on the Sale Viewer's page ground it reads
    // as the card. Hover lifts it to the raised ground at the same 82%.
    //
    // The flag plate is 28px with an 8px corner, a component-owned literal: the
    // token map's locked 10px is for 32 to 36px plates. It takes the danger hue
    // at 14%, AttentionItem's plate recipe, with the flag in the hue itself at
    // 12px. The eyebrow is the hue at 10.5/800 with 0.08em tracking, the notice
    // 13px in the primary ink, and the chevron 12px in the tertiary.
    //
    // The unread badge is the hue with its own ink, `{status.dangerContrast}`,
    // where the prototype paints `#0a0a0a`: the design system's ink on danger is
    // this token's `#2a0808`. The count is mono at 11/800, `0 6px` inside the
    // token map's 999px pill corner, which on the 22px badge draws the
    // prototype's 11px ends.
    //
    // Nothing here is scheme-split: on the page's ground the eyebrow, the flag,
    // the badge's ink on its hue and the chevron all clear their floors in both
    // schemes (the eyebrow 6.13:1 in dark, 5.62:1 in light). Over the Watch
    // stream the glass lets the picture through, and what holds the eyebrow
    // there is the Watch page's scrim, as the handoff's Watch draws one.
    //
    // The plate is the danger tint and not the live badge's `#232a2f` plate, so
    // LiveLaneCard owns the named `liveBadge` block; this strip keeps the danger
    // tint plate of its own rather than that badge.
    saleDeskStrip: {
        gap: '10px',
        padding: '9px 12px',
        borderWidth: '1px',
        borderColor: 'color-mix(in srgb, var(--p-status-danger) 35%, transparent)',
        borderRadius: '{radius.row}',
        background: 'color-mix(in srgb, var(--p-content-background) 82%, transparent)',
        color: '{text.color}',
        hover: {
            background: 'color-mix(in srgb, var(--p-content-hover-background) 82%, transparent)',
        },
        focus: {
            outlineColor: '{primary.color}',
        },
        plate: {
            borderRadius: '8px',
            background: 'color-mix(in srgb, var(--p-status-danger) 14%, transparent)',
            color: '{status.danger}',
        },
        icon: {
            fontSize: '12px',
        },
        eyebrow: {
            fontSize: '10.5px',
            fontWeight: '800',
            letterSpacing: '0.08em',
            color: '{status.danger}',
        },
        text: {
            fontSize: '13px',
        },
        badge: {
            padding: '0 6px',
            borderRadius: '999px',
            background: '{status.danger}',
            color: '{status.dangerContrast}',
            fontFamily: '{font.mono}',
            fontSize: '11px',
            fontWeight: '800',
        },
        chevron: {
            fontSize: '12px',
            color: '{text.mutedColor}',
        },
    },

    // SearchField — .search-field
    searchField: {
        icon: {
            color: '{text.mutedColor}',
            fontSize: '15px',
        },
        field: {
            padding: '0 38px',
            borderWidth: '1px',
            borderColor: '{content.borderColor}',
            borderRadius: '999px',
            background: '{content.hoverBackground}',
            color: '{text.color}',
            fontSize: '15px',
            placeholder: {
                color: '{text.mutedColor}',
            },
            focus: {
                borderColor: '{primary.color}',
            },
        },
        clear: {
            background: 'transparent',
            color: '{text.mutedColor}',
            fontSize: '16px',
        },
    },

    // SelectionListScreen — .selection-list-screen
    selectionListScreen: {
        background: 'var(--surface-ground)',
        color: '{text.color}',
        fontFamily: '{font.body}',
        nav: {
            gap: '8px',
        },
        back: {
            gap: '2px',
            background: 'transparent',
            color: '{primary.color}',
            fontSize: '16px',
            fontWeight: '500',
            padding: '4px',
            icon: {
                fontSize: '13px',
            },
        },
        title: {
            fontSize: '17px',
            fontWeight: '600',
        },
        clear: {
            background: 'transparent',
            color: '{primary.color}',
            fontSize: '15px',
            fontWeight: '500',
            padding: '4px',
            disabled: {
                color: '{text.mutedColor}',
            },
        },
        search: {
            color: '{text.mutedColor}',
            fontSize: '14px',
        },
        searchField: {
            background: '{content.hoverBackground}',
            borderWidth: '0.5px',
            borderColor: '{content.borderColor}',
            borderRadius: '22px',
            padding: '0 40px',
            color: '{text.color}',
            fontSize: '15px',
            focus: {
                borderColor: '{primary.color}',
            },
        },
        searchClear: {
            borderRadius: '12px',
            background: 'transparent',
            color: '{text.mutedColor}',
            icon: {
                fontSize: '12px',
            },
        },
        chips: {
            gap: '8px',
            padding: '4px 16px 10px',
        },
        chip: {
            gap: '6px',
            padding: '7px 12px',
            borderRadius: '999px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontSize: '13px',
            fontWeight: '600',
            icon: {
                fontSize: '11px',
            },
        },
        list: {
            paddingBottom: '100px',
        },
        section: {
            padding: '10px 16px 4px',
            fontSize: '12px',
            fontWeight: '600',
            color: '{text.mutedColor}',
            letterSpacing: '0.04em',
            background: 'var(--surface-ground)',
        },
        row: {
            padding: '12px 16px',
            background: 'transparent',
            borderBottomWidth: '0.5px',
            borderBottomColor: '{content.borderColor}',
            color: '{text.color}',
            active: {
                background: '{content.background}',
            },
        },
        // One span, one block. `on` used to sit under `row` as well, so the same
        // element read `--p-selection-list-screen-row-row-label-on-color` beside
        // `--p-selection-list-screen-row-label-font-size`.
        rowLabel: {
            gap: '10px',
            fontSize: '16px',
            on: {
                color: '{primary.color}',
                fontWeight: '600',
            },
        },
        check: {
            borderRadius: '12px',
            borderWidth: '1.5px',
            borderColor: '{formField.borderColor}',
            on: {
                background: '{primary.color}',
                borderColor: '{primary.color}',
                color: '{primary.contrastColor}',
            },
            icon: {
                fontSize: '12px',
            },
        },
        empty: {
            padding: '32px 16px',
            color: '{text.mutedColor}',
            fontSize: '14px',
        },
        note: {
            padding: '12px 20px',
            fontSize: '12px',
            color: '{text.mutedColor}',
        },
        auctionLight: {
            borderRadius: '50%',
            fontSize: '10px',
            fontWeight: '700',
            color: '{surface.0}',
            red: {
                background: '{auctionLight.red}',
            },
            green: {
                background: '{auctionLight.green}',
            },
            yellow: {
                background: '{auctionLight.yellow}',
                color: '{surface.950}',
            },
            blue: {
                background: '{auctionLight.blue}',
            },
            white: {
                background: '{auctionLight.white}',
                color: '{surface.950}',
            },
        },
        apply: {
            background: 'linear-gradient(180deg, transparent, var(--surface-ground) 30%)',
        },
        applyButton: {
            borderRadius: '14px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontWeight: '700',
            fontSize: '16px',
        },
    },

    // Sheet — .sheet
    //
    // The list tokens left with the list: `actionList` above carries the rows,
    // and a token block is keyed by component name, so the move was a rename
    // rather than a re-tier. What is left is the panel itself — the surface it
    // paints, the grabber, the header and the body's padding.
    sheet: {
        background: 'var(--surface-ground)',
        color: '{text.color}',
        fontFamily: '{font.body}',
        borderTopWidth: '1px',
        borderTopColor: '{content.borderColor}',
        boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.5)',
        grabber: {
            borderRadius: '3px',
            background: '{formField.borderColor}',
        },
        head: {
            padding: '12px 20px 8px',
        },
        title: {
            fontFamily: '{font.display}',
            fontSize: '18px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
            color: '{text.color}',
        },
        close: {
            borderRadius: '16px',
            background: 'transparent',
            color: '{text.color}',
            icon: {
                fontSize: '16px',
            },
        },
        body: {
            padding: '8px 20px 16px',
        },
    },

    // SlideToConfirm — .slide-to-confirm
    slideToConfirm: {
        borderRadius: '18px',
        background: '{content.hoverBackground}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        fill: {
            background: 'color-mix(in srgb, var(--p-primary-color) 18%, transparent)',
        },
        label: {
            gap: '8px',
            fontSize: '17px',
            fontWeight: '700',
            color: '{text.color}',
            paddingLeft: '66px',
        },
        thumb: {
            borderRadius: '14px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.4)',
            fontSize: '20px',
        },
        // The destructive skin — the same semantic danger status ConfirmSheet's
        // danger button reads (`{status.danger}` / `{status.dangerContrast}`),
        // for the most permanent slide there is (closing an account). Only the
        // colors move; the geometry above is unchanged, so the modifier overrides
        // just the fill, label and thumb paint.
        destructive: {
            fill: {
                background: 'color-mix(in srgb, var(--p-status-danger) 18%, transparent)',
            },
            label: {
                color: '{status.danger}',
            },
            thumb: {
                background: '{status.danger}',
                color: '{status.dangerContrast}',
            },
        },
    },

    // StatusPanel — .status-panel
    statusPanel: {
        gap: '10px',
        padding: '40px 32px',
        icon: {
            borderRadius: '999px',
            background: '{content.hoverBackground}',
            fontSize: '30px',
            color: '{primary.color}',
        },
        title: {
            fontSize: '20px',
            fontWeight: '700',
            color: '{text.color}',
        },
        body: {
            fontSize: '14px',
            lineHeight: '1.5',
            color: '{text.mutedColor}',
        },
        callToAction: {
            padding: '11px 22px',
            borderRadius: '999px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontSize: '15px',
            fontWeight: '600',
        },
    },

    // TotalsCard — .totals-card
    //
    // From COMPONENTS.md and the handoff's TotalsCard frame, which agree on the
    // card: the 18px card corner (`{radius.card}`), `4px 16px` padding, the
    // hairline, rows with `10px 0` padding and a 12px gap, the label at 14 in
    // the secondary ink, and the amount in mono at 14/600 in the primary ink.
    // The total row is COMPONENTS.md's: the label at 700 in the primary ink and
    // the amount at 15/800 in the brand accent. The frame sets it as a plain
    // row at 14/600 with only the amount in the accent, which is an open delta
    // under MBP-12.
    //
    // The tones are for the pricing card on asset detail (MBP-20): MMR in the
    // info hue, the buyer's own number in the brand accent while it leads and
    // in the danger hue once outbid. `success` points at `{primary.color}`, not
    // `{status.success}`, because the asset detail screen paints the leading
    // figure in the design's brand mint (`#49d29b`), which the token map maps
    // to `{primary.color}`, as OfferGapCard paints the buyer's number. The two
    // are the same mint in dark; in light the accent is the one that clears
    // 4.5:1 on white at 14px.
    totalsCard: {
        padding: '4px 16px',
        background: '{content.background}',
        borderWidth: '0.5px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.card}',
        row: {
            gap: '12px',
            padding: '10px 0',
            borderBottomWidth: '0.5px',
            borderBottomColor: '{content.borderColor}',
            total: {
                label: {
                    color: '{text.color}',
                    fontWeight: '700',
                },
                amount: {
                    color: '{primary.color}',
                    fontSize: '15px',
                    fontWeight: '800',
                },
            },
        },
        label: {
            color: '{text.hoverMutedColor}',
            fontSize: '14px',
        },
        amount: {
            color: '{text.color}',
            fontFamily: '{font.mono}',
            fontSize: '14px',
            fontWeight: '600',
            info: {
                color: '{status.info}',
            },
            success: {
                color: '{primary.color}',
            },
            danger: {
                color: '{status.danger}',
            },
        },
    },

    // AssetBidTile — .asset-bid-tile
    //
    // The 62px square at the trailing edge of an asset row. Drawn inline on the
    // Bid Stack page before the row became one component, where the four tints
    // below were rgba() literals of the mint, the amber, the blue and the red.
    // Each is now a mix of the semantic token it was approximating, so the tile
    // follows the scheme and a brand change moves it.
    assetBidTile: {
        borderRadius: '13px',
        fontWeight: '700',
        // The bid shape: one centered word on the brand fill.
        bid: {
            fontSize: '15px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
        },
        // A ceiling rather than a bid: an outline in the proxy blue.
        proxy: {
            background: 'transparent',
            color: '{status.info}',
            borderWidth: '1px',
            borderColor: 'color-mix(in srgb, var(--p-status-info) 50%, transparent)',
        },
        // The result shape: a glyph over a word.
        result: {
            gap: '3px',
            fontSize: '11px',
            icon: {
                fontSize: '18px',
            },
        },
        winning: {
            background: 'color-mix(in srgb, var(--p-status-warning) 16%, transparent)',
            color: '{status.warning}',
        },
        // 12% rather than the Bid Stack page's 18%: the Won tile lands on a card
        // that is ALSO washed gold, and the two tints together put `{status.warning}`
        // at 4.2:1 in light, under the 4.5:1 floor an 11px bold word is held to.
        // The catalog's axe gate is what measured it.
        won: {
            background: 'color-mix(in srgb, var(--p-status-warning) 12%, transparent)',
            color: '{status.warning}',
        },
        lost: {
            background: 'color-mix(in srgb, var(--p-status-danger) 14%, transparent)',
            color: '{status.danger}',
        },
        // The lot did not sell. The one result that is not a verdict on the
        // dealer — nobody took it from them — so it is the one that takes no
        // tone: muted ink, and a neutral wash of that same ink for a ground.
        // NOT the clerk console's `{outcome.noSale}` yellow, which is a hue
        // tuned for a decision an auctioneer makes rather than a face a dealer
        // reads. The wash itself is a scheme split below.
        noSale: {
            color: '{text.mutedColor}',
        },
    },

    // AssetCard — .asset-card
    assetCard: {
        background: '{content.background}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        // The compact row's trailing accessory (Liked's remove ×, and the bid
        // tile on a row the dealer is bidding on).
        accessory: {
            gap: '6px',
        },
        expanded: {
            borderRadius: '{radius.card}',
            media: {
                background: 'linear-gradient(135deg, var(--p-content-hover-background), var(--surface-ground))',
            },
        },
        compact: {
            gap: '11px',
            borderRadius: '13px',
            padding: '8px',
            media: {
                borderRadius: '9px',
                background: 'radial-gradient(120% 120% at 50% 18%, var(--p-content-hover-background), var(--surface-ground))',
            },
            body: {
                gap: '3px',
            },
            title: {
                fontSize: '14px',
            },
            meta: {
                fontSize: '11.5px',
                color: '{text.mutedColor}',
            },
            sale: {
                padding: '3px 6px',
                borderRadius: '6px',
                fontSize: '9.5px',
                fontWeight: '700',
                letterSpacing: '0.04em',
                gap: '3px',
                icon: {
                    fontSize: '8px',
                },
            },
        },
        sale: {
            padding: '6px 10px',
            borderRadius: '999px',
            background: 'rgba(0, 0, 0, 0.55)',
            color: '{surface.0}',
            fontSize: '12px',
            fontWeight: '600',
            gap: '5px',
            // The LIVE state has no block of its own here on purpose: it reads
            // the shared `liveBadge` trio, which is the one live-now look the
            // whole app draws (see the note on that key in index.mjs). It used
            // to be a deep-mint plate this component owned, which is how a
            // results card came to look nothing like the Home Live Now card
            // beside it — the drift the trio exists to stop (QA S3).
        },
        countdown: {
            icon: {
                fontSize: '11px',
            },
            urgent: {
                background: '{status.danger}',
                color: '{status.dangerContrast}',
            },
        },
        heart: {
            borderRadius: '18px',
            background: 'rgba(0, 0, 0, 0.55)',
            color: '{surface.0}',
            icon: {
                fontSize: '16px',
            },
            // Saved: a white heart on a red plate, as everywhere in the app.
            fav: {
                background: '{saved.background}',
                color: '{saved.color}',
            },
        },
        save: {
            borderRadius: '999px',
            background: '{content.hoverBackground}',
            color: '{text.hoverMutedColor}',
            icon: {
                fontSize: '15px',
            },
        },
        body: {
            padding: '12px 14px 14px',
        },
        title: {
            fontSize: '16px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
            color: '{text.color}',
        },
        meta: {
            fontSize: '13px',
            color: '{text.hoverMutedColor}',
        },
        // The runtime status line — line 2 where the dealer is bidding on the
        // asset, in place of the spec strip. 11.5px rather than the Bid Stack
        // page's 11px, because it now lands in the compact sub-line's slot and
        // the two have to read as one line rather than two sizes.
        status: {
            gap: '5px',
            fontSize: '11.5px',
            fontWeight: '700',
            letterSpacing: '0.06em',
            live: {
                color: '{primary.color}',
            },
            upcoming: {
                color: '{text.mutedColor}',
            },
            muted: {
                color: '{text.mutedColor}',
            },
            won: {
                color: '{status.warning}',
            },
            lost: {
                color: '{status.danger}',
            },
        },
        // The dealer's own proxy ceiling, beside the current bid. Blue, which is
        // the proxy language on every other surface.
        proxyPill: {
            gap: '4px',
            padding: '2px 7px',
            borderRadius: '999px',
            // 10% rather than the Bid Stack page's 14%: an armed ceiling also
            // washes the whole card blue, and the two tints together put
            // `{status.info}` at 4.48:1 in light — two hundredths under the
            // floor. The catalog's axe gate is what measured it.
            background: 'color-mix(in srgb, var(--p-status-info) 10%, transparent)',
            color: '{status.info}',
            fontSize: '10.5px',
            fontWeight: '700',
            icon: {
                fontSize: '10.5px',
            },
        },
        // ── The outline states ───────────────────────────────────────────
        // One outline per row state, and each one its own colour. Ported from
        // the Bid Stack page's own card, where each was an rgba() literal of the
        // mint or the amber — and where winning and outbid were the SAME amber,
        // told apart by how soft the glow was.
        //
        // Every outline is the same geometry: the 1px border above, carrying the
        // colour, plus an INSET shadow for the extra weight. Inset, so no state
        // paints outside the border box — the outer rings these replace made a
        // reordering list reflow around a halo and let the search feed's top
        // edge clip the first row's ring. No state changes the border WIDTH
        // either, so the row measures 82px in all of them.
        //
        // Weight is the on-block signal and colour is the standing's, which is
        // what makes the precedence rule below expressible: 2px inset says
        // RUNNING, 1px says the dealer has a standing on it, and the colour says
        // which standing. Light and dark need no override: every value resolves
        // through a scheme-aware reference, so the two move in lockstep.
        block: {
            borderColor: '{primary.color}',
            boxShadow: 'inset 0 0 0 2px var(--p-primary-color)',
        },
        // Gold, which is the colour Won wears: both are the dealer holding the
        // asset, one of them not yet finally.
        winning: {
            borderColor: '{status.warning}',
            boxShadow: 'inset 0 0 0 1px var(--p-status-warning)',
        },
        // Red, and NOT the winning gold: these are the two states a dealer has
        // to tell apart at a glance in a scrolling list, and the only reliable
        // difference between them is the hue.
        outbid: {
            borderColor: '{status.danger}',
            boxShadow: 'inset 0 0 0 1px var(--p-status-danger)',
        },
        // On the block AND the dealer has a standing: the standing's colour (the
        // border colour the single-state rule already set) at the on-block
        // weight. The row says both facts with one outline rather than dropping
        // the more urgent of them.
        blockWinning: {
            boxShadow: 'inset 0 0 0 2px var(--p-status-warning)',
        },
        blockOutbid: {
            boxShadow: 'inset 0 0 0 2px var(--p-status-danger)',
        },
        proxy: {
            background: 'color-mix(in srgb, var(--p-status-info) 10%, transparent)',
            borderColor: 'color-mix(in srgb, var(--p-status-info) 30%, transparent)',
        },
        // A row that has run recedes by PHOTO and FRAME, never by the words. One
        // opacity for the thumbnail, one softened border, and no opacity on the
        // row itself — so the title, the status, the bid figure, the proxy pill
        // and the bid tile all keep the contrast they were measured at. Both
        // values resolve from scheme-aware references, so light and dark recede
        // by the same amount without a per-scheme override.
        resolved: {
            borderColor: 'color-mix(in srgb, var(--p-content-border-color) 55%, transparent)',
            media: {
                opacity: '0.55',
            },
        },
        // 10% rather than the Bid Stack page's 14%, for the reason the Won tile
        // stepped down to 12%: the wash and the tile stack, and the word on the
        // tile has to stay readable on both.
        won: {
            background: 'color-mix(in srgb, var(--p-status-warning) 10%, transparent)',
            borderColor: 'color-mix(in srgb, var(--p-status-warning) 40%, transparent)',
        },
        price: {
            gap: '7px',
        },
        priceFigure: {
            fontFamily: '{font.mono}',
            fontSize: '14px',
            fontWeight: '600',
        },
        // What the figure IS, set beside it. Muted and small, so it labels the
        // number without competing with it — a dollar amount nobody can name is
        // the defect this closes (QA S8).
        priceLabel: {
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.06em',
            color: '{text.mutedColor}',
        },
        // The compact row's "Contact for price" prompt. Text color rather than
        // the mint accent, which is what the expanded band's own call to action
        // already uses — there the mint is carried by the glyph beside it, and a
        // glyph is not text that has to clear a contrast floor. `{primary.color}`
        // at 11.5px is 4.31:1 on light paper, under the 4.5:1 AA floor.
        priceContact: {
            fontSize: '11.5px',
            fontWeight: '600',
            color: '{text.color}',
        },
        priceTarget: {
            gap: '3px',
            fontSize: '11.5px',
            fontWeight: '700',
            color: '{status.warning}',
        },
        band: {
            gap: '10px',
            padding: '12px 14px',
            borderTopWidth: '0.5px',
            borderTopColor: '{content.borderColor}',
            dealer: {
                background: 'color-mix(in srgb, var(--p-primary-color) 6%, transparent)',
            },
        },
        // The wholesale figure on the expanded band. Text color, which is what
        // the compact layout's `priceFigure` has always inherited — the two
        // layouts are one contract, and the band was the one that disagreed. The
        // mint wash behind it is what says "dealer"; the figure is data. It also
        // has to move: `{primary.color}` on that wash is 4.01:1 in light, under
        // the 4.5:1 AA floor at 18px.
        bandMoney: {
            gap: '8px',
        },
        // "Buy now" reads as part of the price, not a caption over it: the
        // figure's own size and weight, in the dealer green. `color` is
        // scheme-split — see componentTokensByColorScheme below.
        bandPriceLabel: {
            fontSize: '18px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
        },
        bandPrice: {
            fontSize: '18px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
            color: '{text.color}',
        },
        bandTarget: {
            gap: '5px',
            fontSize: '12px',
            fontWeight: '700',
            color: '{status.warning}',
        },
        bandCallToAction: {
            gap: '7px',
            fontSize: '14px',
            fontWeight: '600',
            color: '{text.color}',
        },
        bandIcon: {
            fontSize: '15px',
            color: '{primary.color}',
        },
        bandChevron: {
            fontSize: '14px',
            color: '{text.mutedColor}',
        },
    },

    // PageLayout — .page-layout
    pageLayout: {
        background: 'var(--surface-ground)',
        content: {
            color: '{text.color}',
        },
    },

    // TabBar — .tab-bar
    tabBar: {
        background: '{content.hoverBackground}',
        borderTopWidth: '1px',
        borderTopColor: '{content.borderColor}',
        button: {
            borderRadius: '999px',
            background: 'transparent',
            color: 'color-mix(in srgb, var(--p-text-color) 72%, transparent)',
            fontFamily: '{font.body}',
            active: {
                color: '{primary.color}',
            },
        },
        badge: {
            padding: '0 4px',
            borderRadius: '999px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontSize: '10px',
            fontWeight: '700',
            lineHeight: '1',
            borderWidth: '2px',
            borderColor: '{content.hoverBackground}',
        },
        icon: {
            icon: {
                fontSize: '22px',
                lineHeight: '1',
            },
        },
        // The centre slot draws no disc and no ring — the emphasis is the
        // glyph alone — so the only paint it asks for is the glyph's.
        floatingActionButton: {
            // Larger than an ordinary slot's 22px: the slot is the dock's one
            // emphasized target, and its glyph carries the emphasis alone —
            // there is no label beneath it to share the work.
            glyph: {
                fontSize: '25px',
                lineHeight: '1',
                // The seed the rainbow rotates away from, so the animation has
                // one saturated hue to start at. A semantic token would be
                // whatever the scheme made it, and the rotation needs a fixed
                // point.
                color: '#34d399',
            },
        },
    },

    // ─────────────────────────────────────────────────────────────────────
    // The clerk console — `@hds/auto-auction-com-components-desktop`
    // ─────────────────────────────────────────────────────────────────────
    //
    // Lifted out of the legacy-clerk application, where every one of these
    // values was a raw color or a reference into a parallel `--fg-*` /
    // `--bg-*` / `--border-*` vocabulary that only that app declared. Reading
    // it here is what the mapping settled on, and it is worth stating once
    // because it recurs in every block below:
    //
    //   --bg-app / --bg-section     →  {content.background}, or
    //                                  {formField.background} for an input well
    //   --bg-overlay / --bg-hover   →  {content.hoverBackground}
    //   --bg-selected               →  {highlight.background}
    //   --border-subtle             →  {content.borderColor}
    //   --border-default            →  {formField.borderColor}
    //   --fg-primary                →  {text.color}
    //   --fg-secondary              →  {text.mutedColor}
    //   --fg-tertiary               →  {surface.400}
    //   --info-*/--warn-*/--danger-*→  {status.info/warning/danger}
    //   the mint anchor             →  {primary.color}
    //
    // `{surface.400}` for the tertiary tone is not a primitive leaking in:
    // `surface` is defined per color scheme in this preset, so it flips with
    // light and dark exactly as a semantic token does, and it is the only step
    // that reads as "present but recessive" against BOTH backgrounds.
    //
    // Two hues the semantic tier genuinely could not express — No Sale yellow
    // and Offer teal — plus the selling rep's orange, live in
    // `extend.colorScheme.*.outcome` in index.mjs. They are referenced as
    // `{outcome.*}` throughout.

    // ActionGrid — .action-grid
    actionGrid: {
        gap: '10px',
        pair: {
            gap: '8px',
        },
    },

    // BidPadCell — .bid-pad-cell
    bidPadCell: {
        borderRadius: '8px',
        padding: '0 6px',
        fontFamily: '{font.mono}',
        fontSize: '22px',
        letterSpacing: '-0.01em',
        reference: {
            borderWidth: '2px',
            fontWeight: '800',
        },
        // The standing bid, in the brand accent. A reference mark, not a target.
        current: {
            background: '{highlight.background}',
            color: '{primary.color}',
            borderColor: '{primary.color}',
            boxShadow: '0 0 0 4px color-mix(in srgb, var(--p-primary-color) 18%, transparent)',
        },
        // What the auctioneer is asking, in the drop-the-ask blue.
        asking: {
            background: 'color-mix(in srgb, var(--p-status-info) 14%, transparent)',
            color: '{status.info}',
            borderColor: '{status.info}',
            boxShadow: '0 0 0 4px color-mix(in srgb, var(--p-status-info) 18%, transparent)',
        },
        tag: {
            fontFamily: '{font.body}',
            fontSize: '8px',
            fontWeight: '800',
            letterSpacing: '0.1em',
        },
        tappable: {
            background: '{content.background}',
            color: '{text.color}',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            fontWeight: '700',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
            primary: {
                hover: {
                    background: '{highlight.background}',
                    borderColor: '{primary.color}',
                },
            },
            info: {
                hover: {
                    background: 'color-mix(in srgb, var(--p-status-info) 14%, transparent)',
                    borderColor: '{status.info}',
                },
            },
        },
    },

    // BidHistoryList — .bid-history-list
    bidHistoryList: {
        background: '{content.background}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '10px',
        head: {
            padding: '8px 14px',
            borderBottomWidth: '1px',
            borderBottomColor: '{content.borderColor}',
            background: '{content.hoverBackground}',
        },
        title: {
            fontSize: '10px',
            fontWeight: '800',
            letterSpacing: '0.1em',
            color: '{text.mutedColor}',
        },
        count: {
            fontFamily: '{font.mono}',
            fontSize: '11px',
            color: '{surface.400}',
        },
        empty: {
            padding: '16px 14px',
            fontSize: '12px',
            color: '{surface.400}',
        },
        row: {
            gap: '10px',
            padding: '7px 14px',
            borderBottomWidth: '1px',
            borderBottomColor: '{content.borderColor}',
            newest: {
                background: '{highlight.background}',
            },
            system: {
                background: '{content.hoverBackground}',
            },
            settled: {
                background: '{highlight.background}',
            },
        },
        name: {
            fontSize: '12px',
            fontWeight: '700',
            color: '{text.color}',
        },
        meta: {
            fontFamily: '{font.mono}',
            fontSize: '10px',
            color: '{surface.400}',
        },
        amount: {
            fontFamily: '{font.mono}',
            fontSize: '14px',
            fontWeight: '800',
            color: '{primary.color}',
            online: {
                color: '{status.info}',
            },
            rejected: {
                color: '{surface.400}',
                fontWeight: '700',
            },
        },
        glyph: {
            borderRadius: '6px',
            background: '{content.background}',
            borderWidth: '1px',
            borderColor: '{content.borderColor}',
            color: '{surface.400}',
            fontSize: '12px',
            settled: {
                color: '{primary.color}',
                borderColor: '{primary.color}',
            },
        },
        systemLabel: {
            fontSize: '10px',
            fontWeight: '800',
            letterSpacing: '0.08em',
            color: '{text.mutedColor}',
        },
        qualifier: {
            fontSize: '9px',
            fontWeight: '700',
            letterSpacing: '0.06em',
            color: '{surface.400}',
        },
        rejectedTag: {
            fontSize: '9px',
            fontWeight: '800',
            letterSpacing: '0.06em',
            color: '{status.danger}',
        },
        figure: {
            fontFamily: '{font.mono}',
            fontSize: '13px',
            fontWeight: '700',
            color: '{text.mutedColor}',
        },
    },

    // ChatPanel — .chat-panel
    chatPanel: {
        background: '{content.background}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '10px',
        watermarkBadge: {
            padding: '8px 18px',
            borderRadius: '999px',
            background: '{content.hoverBackground}',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.28)',
            fontSize: '12px',
            fontWeight: '800',
            letterSpacing: '0.12em',
            color: '{text.mutedColor}',
        },
        tabs: {
            padding: '6px',
            borderBottomWidth: '1px',
            borderBottomColor: '{content.borderColor}',
            gap: '4px',
        },
        tab: {
            padding: '6px 8px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '500',
            lineHeight: '1.1',
            color: '{text.mutedColor}',
            top: {
                gap: '5px',
            },
            count: {
                fontFamily: '{font.mono}',
                fontSize: '9px',
                fontWeight: '700',
                lineHeight: '1.2',
                padding: '1px 5px',
                borderRadius: '8px',
                background: '{content.hoverBackground}',
                color: '{surface.400}',
            },
            close: {
                color: '{surface.400}',
                fontSize: '9px',
            },
            subtitle: {
                fontSize: '9px',
                fontWeight: '500',
                color: '{surface.400}',
            },
            active: {
                background: '{content.hoverBackground}',
                color: '{text.color}',
                fontWeight: '700',
                count: {
                    background: '{primary.color}',
                    color: '{primary.contrastColor}',
                },
            },
        },
        newConversation: {
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            borderRadius: '8px',
            color: '{text.mutedColor}',
        },
        roster: {
            head: {
                padding: '4px 8px 6px',
                fontSize: '9px',
                fontWeight: '800',
                letterSpacing: '0.08em',
                color: '{surface.400}',
            },
            empty: {
                padding: '6px 8px',
                fontSize: '12px',
                color: '{surface.400}',
            },
            row: {
                gap: '8px',
                padding: '7px 8px',
                borderRadius: '6px',
                hover: {
                    background: '{content.hoverBackground}',
                },
            },
            badge: {
                fontFamily: '{font.mono}',
                fontSize: '11px',
                fontWeight: '800',
                color: '{text.color}',
            },
            name: {
                fontSize: '12px',
                color: '{text.mutedColor}',
            },
            icon: {
                fontSize: '11px',
                color: '{surface.400}',
            },
        },
        stream: {
            padding: '10px 10px 12px',
            gap: '2px',
        },
        note: {
            padding: '2px 0 8px',
            fontSize: '10px',
            color: '{surface.400}',
        },
    },

    // ClerkBottomBar — .clerk-bottom-bar
    clerkBottomBar: {
        background: '{content.background}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '10px',
        padding: '0 20px',
        divider: {
            background: '{content.borderColor}',
        },
    },

    // ClerkLogo — .clerk-logo
    clerkLogo: {
        fontFamily: '{font.display}',
        fontSize: '2rem',
        fontWeight: '600',
        letterSpacing: '-0.02em',
        lineHeight: '1',
        // "Auto" follows the scheme; "Auction" is the brand and never does.
        auto: {
            color: '{text.color}',
        },
        auction: {
            color: '{primary.color}',
        },
        domain: {
            color: '{surface.400}',
        },
    },

    // ClerkTopBar — .clerk-top-bar
    clerkTopBar: {
        background: '{content.background}',
        borderBottomWidth: '1px',
        borderBottomColor: '{content.borderColor}',
        padding: '0 20px',
        gap: '16px',
        brand: {
            gap: '12px',
        },
        tag: {
            padding: '3px 8px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '700',
            letterSpacing: '0.1em',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
        },
        divider: {
            background: '{content.borderColor}',
        },
        identity: {
            gap: '10px',
        },
        sale: {
            fontSize: '15px',
            fontWeight: '600',
            color: '{text.color}',
        },
        separator: {
            color: '{surface.400}',
        },
        lane: {
            fontSize: '13px',
            fontWeight: '600',
            color: '{text.mutedColor}',
        },
        externalId: {
            fontFamily: '{font.mono}',
            fontSize: '11px',
            color: '{surface.400}',
        },
        right: {
            gap: '10px',
        },
        settings: {
            gap: '8px',
            label: {
                fontSize: '10px',
                fontWeight: '800',
                letterSpacing: '0.1em',
                color: '{text.mutedColor}',
            },
            icon: {
                fontSize: '12px',
            },
        },
    },

    // ConfirmSalePanel — .confirm-sale-panel
    confirmSalePanel: {
        background: '{content.background}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '10px',
        boxShadow: '0 -16px 40px rgba(0, 0, 0, 0.32)',
        inner: {
            gap: '18px',
            padding: '28px 24px',
        },
        title: {
            fontSize: '20px',
            fontWeight: '800',
            color: '{text.color}',
        },
        price: {
            fontFamily: '{font.mono}',
            fontSize: '20px',
            fontWeight: '800',
            color: '{text.mutedColor}',
        },
        body: {
            gap: '8px',
        },
        fieldLabel: {
            fontSize: '10px',
            fontWeight: '800',
            letterSpacing: '0.1em',
            color: '{text.mutedColor}',
        },
        settled: {
            gap: '24px',
            block: {
                gap: '6px',
            },
            price: {
                fontFamily: '{font.mono}',
                fontSize: '56px',
                fontWeight: '800',
                lineHeight: '1',
                letterSpacing: '-0.02em',
                color: '{text.color}',
            },
            badge: {
                fontFamily: '{font.mono}',
                fontSize: '40px',
                fontWeight: '800',
                lineHeight: '1',
                color: '{text.color}',
            },
        },
        pill: {
            padding: '10px 28px',
            borderRadius: '999px',
            fontSize: '22px',
            fontWeight: '800',
            letterSpacing: '0.08em',
        },
        field: {
            padding: '8px 12px',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            borderRadius: '8px',
            background: '{formField.background}',
        },
        input: {
            fontFamily: '{font.mono}',
            fontSize: '26px',
            fontWeight: '800',
            letterSpacing: '-0.01em',
            lineHeight: '1',
            color: '{text.color}',
            padding: '0',
        },
        winner: {
            gap: '4px',
            badge: {
                fontFamily: '{font.mono}',
                fontSize: '26px',
                fontWeight: '800',
                color: '{text.color}',
            },
        },
        override: {
            gap: '7px',
            padding: '8px 14px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '700',
            letterSpacing: '0.04em',
            background: '{content.background}',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            color: '{text.mutedColor}',
        },
        error: {
            fontSize: '12.5px',
            fontWeight: '600',
            color: '{status.danger}',
        },
        footer: {
            gap: '8px',
        },
        action: {
            borderWidth: '1px',
            borderRadius: '10px',
            padding: '10px 14px',
            fontWeight: '800',
            letterSpacing: '0.04em',
        },
        // The four confirm tones, in lockstep with the ActionGrid button that
        // opened the panel. `noSale` doubles as the prose color on the No Sale
        // body copy, which is why the text pair sits under it.
        cancel: {
            background: 'transparent',
            borderColor: '{content.borderColor}',
            color: '{text.mutedColor}',
        },
        sell: {
            background: '{primary.color}',
            borderColor: '{primary.600}',
            color: '{primary.contrastColor}',
        },
        noSale: {
            background: '{outcome.noSale}',
            borderColor: '{outcome.noSale}',
            color: '{outcome.noSaleContrast}',
            fontSize: '14px',
            lineHeight: '1.5',
            text: {
                color: '{text.mutedColor}',
            },
            strong: {
                color: '{text.color}',
            },
        },
        offer: {
            background: '{outcome.offer}',
            borderColor: '{outcome.offer}',
            color: '{outcome.offerContrast}',
        },
        neutral: {
            background: '{content.background}',
            borderColor: '{formField.borderColor}',
            color: '{text.color}',
        },
    },

    // ConnectionPill — .connection-pill
    connectionPill: {
        dot: {
            borderRadius: '999px',
            healthy: {
                background: '{primary.color}',
                boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-primary-color) 20%, transparent)',
            },
            degraded: {
                background: '{status.warning}',
                boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-status-warning) 20%, transparent)',
            },
            offline: {
                background: '{status.danger}',
                boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-status-danger) 20%, transparent)',
            },
        },
    },

    // ControlGroup — .control-group
    controlGroup: {
        gap: '3px',
        padding: '6px 0',
        label: {
            fontSize: '9px',
            fontWeight: '800',
            letterSpacing: '0.12em',
            paddingLeft: '2px',
            neutral: {
                color: '{surface.400}',
            },
            danger: {
                color: '{status.danger}',
            },
        },
        row: {
            gap: '10px',
        },
    },

    // AnchoredDrawer — .anchored-drawer
    //
    // The generic container-anchored drawer (identity effort ruling 2026-09-22):
    // it slides in from an edge of whatever positioned container it sits in and
    // dims only that container. The scrim is pure black at an alpha — the
    // absence of a theme color rather than one of them — so it is a literal
    // here for the same reason Sheet's backdrop is.
    anchoredDrawer: {
        scrim: {
            background: 'rgba(0, 0, 0, 0.45)',
        },
        panel: {
            background: '{content.background}',
            color: '{text.color}',
            borderWidth: '1px',
            borderColor: '{content.borderColor}',
            borderRadius: '14px',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.35)',
        },
        head: {
            padding: '16px 20px 0',
        },
        title: {
            fontFamily: '{font.display}',
            fontSize: '18px',
            fontWeight: '700',
            color: '{text.color}',
        },
        body: {
            padding: '12px 20px 16px',
        },
        foot: {
            padding: '0 20px 20px',
        },
    },

    // ConfirmDrawer — .confirm-drawer
    //
    // The desktop twin of ConfirmSheet, composed on AnchoredDrawer. Same tokens
    // as the sheet's — message, in-drawer error, the three button skins — so a
    // confirm reads the same on both surfaces.
    confirmDrawer: {
        message: {
            color: '{text.mutedColor}',
            fontSize: '14px',
            lineHeight: '1.55',
        },
        // A failed action's verdict, inside the still-open drawer.
        error: {
            color: '{status.danger}',
            fontSize: '14px',
            lineHeight: '1.55',
        },
        actions: {
            gap: '10px',
        },
        btn: {
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            padding: '0 16px',
            primary: {
                background: '{primary.color}',
                color: '{primary.contrastColor}',
            },
            danger: {
                background: '{status.danger}',
                color: '{status.dangerContrast}',
            },
            ghost: {
                background: 'transparent',
                color: '{text.color}',
                borderWidth: '1px',
                borderColor: '{content.borderColor}',
            },
        },
    },

    // CrossEventDrawer — .cross-event-drawer
    crossEventDrawer: {
        background: '{content.background}',
        borderTopWidth: '1px',
        borderTopColor: '{formField.borderColor}',
        borderRadius: '12px 12px 10px 10px',
        padding: '14px',
        gap: '12px',
        boxShadow: '0 -16px 40px rgba(0, 0, 0, 0.32)',
        done: {
            gap: '10px',
            title: {
                fontSize: '13px',
                fontWeight: '800',
                color: '{text.color}',
            },
            subtitle: {
                fontSize: '11px',
                color: '{text.mutedColor}',
            },
        },
        check: {
            borderRadius: '999px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontSize: '15px',
        },
        head: {
            gap: '10px',
            left: {
                gap: '8px',
            },
            icon: {
                borderRadius: '7px',
                background: 'color-mix(in oklab, var(--p-status-warning) 18%, transparent)',
                color: '{status.warning}',
                fontSize: '12px',
            },
            label: {
                fontSize: '11px',
                fontWeight: '800',
                letterSpacing: '0.08em',
                color: '{text.mutedColor}',
            },
        },
        dismiss: {
            borderRadius: '6px',
            color: '{surface.400}',
        },
        summary: {
            gap: '2px 12px',
            padding: '10px 12px',
            borderRadius: '8px',
            background: '{content.hoverBackground}',
            borderWidth: '1px',
            borderColor: '{content.borderColor}',
            run: {
                fontFamily: '{font.mono}',
                fontWeight: '800',
                fontSize: '15px',
                color: '{text.color}',
                letterSpacing: '-0.01em',
                padding: '4px 8px',
                borderRadius: '6px',
                background: '{content.background}',
            },
            title: {
                fontSize: '13px',
                fontWeight: '700',
                color: '{text.color}',
            },
            year: {
                fontWeight: '700',
                color: '{surface.400}',
            },
            meta: {
                fontSize: '11px',
                color: '{text.mutedColor}',
                gap: '6px',
            },
            event: {
                gap: '4px',
                icon: {
                    fontSize: '10px',
                    color: '{status.info}',
                },
            },
            separator: {
                color: '{surface.400}',
            },
            miles: {
                fontFamily: '{font.mono}',
            },
        },
        prompt: {
            fontSize: '12px',
            color: '{text.mutedColor}',
            lineHeight: '1.45',
            strong: {
                color: '{text.color}',
            },
        },
        actions: {
            gap: '8px',
        },
        cancel: {
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            background: '{content.background}',
            color: '{text.color}',
            borderRadius: '8px',
            padding: '8px 14px',
            fontSize: '12px',
            fontWeight: '700',
        },
        move: {
            borderWidth: '1px',
            borderColor: '{primary.600}',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            borderRadius: '8px',
            padding: '8px 14px',
            fontSize: '12px',
            fontWeight: '800',
            gap: '6px',
        },
    },

    // CurrentBidCard — .current-bid-card
    //
    // The console's one gold surface. It is `{outcome.noSale}` rather than a
    // fourth hue: the reference card and the No Sale button are the same yellow
    // there, and the card is a REFERENCE — deliberately not the brand accent,
    // which on this screen means "act".
    currentBidCard: {
        background: 'color-mix(in srgb, var(--p-outcome-no-sale) 12%, transparent)',
        borderWidth: '1.5px',
        borderColor: '{outcome.noSale}',
        borderRadius: '12px',
        padding: '10px 14px',
        gap: '3px',
        label: {
            fontSize: '10px',
            fontWeight: '800',
            letterSpacing: '0.1em',
            color: '{outcome.noSale}',
        },
        amount: {
            fontFamily: '{font.mono}',
            fontSize: '30px',
            fontWeight: '800',
            letterSpacing: '-0.02em',
            lineHeight: '1',
            color: '{outcome.noSale}',
        },
        leader: {
            gap: '6px',
            fontSize: '10px',
            fontWeight: '700',
            color: '{outcome.noSale}',
        },
    },

    // EventHaltedOverlay — .event-halted-overlay
    eventHaltedOverlay: {
        background: 'color-mix(in srgb, var(--p-content-background) 88%, transparent)',
        padding: '24px',
        panel: {
            background: '{content.background}',
            borderWidth: '1px',
            borderColor: 'color-mix(in srgb, var(--p-status-warning) 45%, transparent)',
            borderRadius: '16px',
            padding: '32px',
            gap: '16px',
        },
        icon: {
            borderRadius: '12px',
            background: 'color-mix(in srgb, var(--p-status-warning) 12%, transparent)',
            borderWidth: '1px',
            borderColor: 'color-mix(in srgb, var(--p-status-warning) 40%, transparent)',
            color: '{status.warning}',
            fontSize: '26px',
        },
        // Terminal, so red rather than amber — the panel says the sale is over,
        // not that it is waiting.
        ended: {
            panel: {
                borderColor: 'color-mix(in srgb, var(--p-status-danger) 45%, transparent)',
            },
            icon: {
                background: 'color-mix(in srgb, var(--p-status-danger) 12%, transparent)',
                borderColor: 'color-mix(in srgb, var(--p-status-danger) 40%, transparent)',
                color: '{status.danger}',
            },
        },
        title: {
            fontSize: '22px',
            fontWeight: '800',
            color: '{text.color}',
        },
        text: {
            fontSize: '14px',
            lineHeight: '1.5',
            color: '{text.mutedColor}',
        },
        action: {
            padding: '12px 24px',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: '800',
            gap: '7px',
        },
        leave: {
            background: '{content.hoverBackground}',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            color: '{text.mutedColor}',
        },
        resume: {
            background: '{status.warning}',
            color: '{status.warningContrast}',
        },
    },

    // EventRunningTime — .event-running-time
    eventRunningTime: {
        gap: '5px',
        padding: '0 4px',
        left: {
            gap: '8px',
        },
        right: {
            gap: '10px',
        },
        dot: {
            borderRadius: '999px',
            background: '{primary.color}',
        },
        title: {
            fontSize: '10px',
            fontWeight: '800',
            letterSpacing: '0.1em',
            color: '{text.mutedColor}',
        },
        phase: {
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.04em',
            color: '{primary.color}',
        },
        clock: {
            fontFamily: '{font.mono}',
            fontSize: '14px',
            fontWeight: '800',
            letterSpacing: '-0.02em',
            color: '{text.color}',
        },
        bar: {
            borderRadius: '999px',
            background: '{content.hoverBackground}',
        },
        fill: {
            background: '{primary.color}',
        },
        tick: {
            background: '{formField.borderColor}',
        },
        warn: {
            dot: {
                background: '{status.warning}',
                boxShadow: '0 0 8px var(--p-status-warning)',
            },
            phase: {
                color: '{status.warning}',
            },
            clock: {
                color: '{status.warning}',
            },
            fill: {
                background: '{status.warning}',
            },
        },
        urgent: {
            dot: {
                background: '{status.danger}',
                boxShadow: '0 0 8px var(--p-status-danger)',
            },
            phase: {
                color: '{status.danger}',
            },
            clock: {
                color: '{status.danger}',
            },
            fill: {
                background: '{status.danger}',
            },
        },
    },

    // FloorBidPad — .floor-bid-pad
    floorBidPad: {
        background: '{content.background}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '10px',
        head: {
            padding: '12px',
            borderBottomWidth: '1px',
            borderBottomColor: '{content.borderColor}',
            background: '{content.hoverBackground}',
            gap: '6px',
        },
        label: {
            fontSize: '10px',
            fontWeight: '800',
            letterSpacing: '0.1em',
            color: '{text.mutedColor}',
        },
        eyebrow: {
            fontSize: '10px',
            fontWeight: '600',
            letterSpacing: '0.06em',
            color: '{surface.400}',
        },
        form: {
            gap: '6px',
        },
        field: {
            gap: '8px',
            padding: '8px 12px',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            borderRadius: '8px',
            background: '{formField.background}',
        },
        currency: {
            fontSize: '22px',
            fontWeight: '700',
            color: '{surface.400}',
        },
        input: {
            fontFamily: '{font.mono}',
            fontSize: '26px',
            fontWeight: '800',
            letterSpacing: '-0.01em',
            lineHeight: '1',
            color: '{text.color}',
            padding: '0',
        },
        submit: {
            padding: '0 16px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '800',
            letterSpacing: '0.08em',
            gap: '8px',
            background: '{content.hoverBackground}',
            color: '{surface.400}',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            ready: {
                background: '{primary.color}',
                color: '{primary.contrastColor}',
                borderColor: '{primary.600}',
                boxShadow: '0 8px 24px color-mix(in srgb, var(--p-primary-color) 32%, transparent)',
                shortcut: {
                    background: 'rgba(0, 0, 0, 0.18)',
                },
            },
        },
        shortcut: {
            fontFamily: '{font.mono}',
            fontSize: '9px',
            fontWeight: '700',
            padding: '1px 5px',
            borderRadius: '3px',
            background: '{content.hoverBackground}',
        },
        grid: {
            padding: '10px',
            gap: '6px',
        },
    },

    // IncrementButton — .increment-button
    incrementButton: {
        background: '{content.background}',
        color: '{text.color}',
        borderWidth: '1px',
        borderColor: '{formField.borderColor}',
        borderRadius: '10px',
        padding: '14px 16px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
        hover: {
            background: '{content.hoverBackground}',
        },
        compact: {
            padding: '10px 14px',
            amount: {
                fontSize: '20px',
            },
        },
        amount: {
            fontFamily: '{font.mono}',
            fontWeight: '800',
            letterSpacing: '-0.01em',
            fontSize: '24px',
        },
        hint: {
            fontSize: '9px',
            fontWeight: '800',
            letterSpacing: '0.1em',
            padding: '2px 6px',
            borderRadius: '4px',
            background: '{content.hoverBackground}',
            color: '{text.mutedColor}',
        },
        // The set step: a tinted fill with a heavier ring, so it reads as
        // selected rather than as the recommendation below.
        active: {
            borderWidth: '2px',
            primary: {
                background: '{highlight.background}',
                color: '{primary.color}',
                borderColor: '{primary.color}',
                boxShadow: '0 0 0 4px color-mix(in srgb, var(--p-primary-color) 18%, transparent)',
                hint: {
                    background: '{primary.color}',
                    color: '{primary.contrastColor}',
                },
            },
            info: {
                background: 'color-mix(in srgb, var(--p-status-info) 12%, transparent)',
                color: '{status.info}',
                borderColor: '{status.info}',
                boxShadow: '0 0 0 4px color-mix(in srgb, var(--p-status-info) 20%, transparent)',
                hint: {
                    background: '{status.info}',
                    color: '{status.infoContrast}',
                },
            },
        },
        // The recommendation: a solid fill and a lift, the loudest state.
        suggested: {
            primary: {
                background: '{primary.color}',
                color: '{primary.contrastColor}',
                borderColor: '{primary.600}',
                boxShadow: '0 8px 24px color-mix(in srgb, var(--p-primary-color) 32%, transparent)',
            },
            info: {
                background: '{status.info}',
                color: '{status.infoContrast}',
                borderColor: '{status.info}',
                boxShadow: '0 8px 24px color-mix(in srgb, var(--p-status-info) 30%, transparent)',
            },
        },
    },

    // IncrementRail — .increment-rail
    incrementRail: {
        gap: '6px',
        eyebrow: {
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.08em',
            color: '{surface.400}',
            padding: '0 4px',
        },
        grid: {
            gap: '6px',
        },
    },

    // LightDot — .light-dot
    //
    // The five disclosure hues come straight from `auctionLight`, the brand
    // primitive that exists for exactly this. The ring is the light's own
    // color and the halo a 15% wash of it, so a house re-tinting one light
    // moves all three at once.
    lightDot: {
        borderRadius: '999px',
        borderWidth: '2px',
        fontFamily: '{font.body}',
        fontSize: '14px',
        fontWeight: '800',
        letterSpacing: '0.02em',
        red: {
            background: '{auctionLight.red}',
            color: '{surface.0}',
            borderColor: '{auctionLight.red}',
            boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-auction-light-red) 15%, transparent)',
        },
        green: {
            background: '{auctionLight.green}',
            color: '{surface.0}',
            borderColor: '{auctionLight.green}',
            boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-auction-light-green) 15%, transparent)',
        },
        yellow: {
            background: '{auctionLight.yellow}',
            color: '{surface.950}',
            borderColor: '{auctionLight.yellow}',
            boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-auction-light-yellow) 15%, transparent)',
        },
        blue: {
            background: '{auctionLight.blue}',
            color: '{surface.0}',
            borderColor: '{auctionLight.blue}',
            boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-auction-light-blue) 15%, transparent)',
        },
        white: {
            background: '{auctionLight.white}',
            color: '{surface.950}',
            borderColor: '{auctionLight.white}',
            boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-auction-light-white) 15%, transparent)',
        },
        // Not a light — the absence of an inspection.
        unset: {
            background: '{content.hoverBackground}',
            color: '{surface.400}',
            borderColor: '{formField.borderColor}',
        },
        // The same puck at the scale of a line of type, for the marketplace
        // hero's meta row. A MODIFIER rather than a second component: the
        // console reads a 32px puck across the room and the hero reads one
        // inside a sentence, and two components would be two definitions of what
        // a green light looks like — which is the thing this component was
        // written to collapse in the first place.
        //
        // The drop shadow replaces the ring glow the colors carry at full size.
        // A 3px colored halo around a 15px dot is most of the dot; over a
        // photograph what the puck needs instead is separation from whatever is
        // behind it.
        compact: {
            borderWidth: '1.5px',
            fontSize: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
        },
    },

    // LightsEditor — .lights-editor
    lightsEditor: {
        gap: '8px',
        head: {
            fontSize: '10px',
            fontWeight: '800',
            letterSpacing: '0.1em',
            color: '{text.mutedColor}',
        },
        multiSelect: {
            color: '{surface.400}',
            fontWeight: '600',
            letterSpacing: '0.04em',
        },
        grid: {
            gap: '6px',
        },
        option: {
            gap: '4px',
            padding: '6px 0',
            borderRadius: '8px',
            background: 'transparent',
            borderWidth: '1.5px',
            borderColor: '{formField.borderColor}',
            selected: {
                background: '{content.background}',
                label: {
                    color: '{text.color}',
                },
            },
            label: {
                fontSize: '9px',
                fontWeight: '700',
                letterSpacing: '0.04em',
                color: '{surface.400}',
            },
            r: {
                borderColor: '{auctionLight.red}',
                boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-auction-light-red) 16%, transparent)',
            },
            g: {
                borderColor: '{auctionLight.green}',
                boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-auction-light-green) 16%, transparent)',
            },
            y: {
                borderColor: '{auctionLight.yellow}',
                boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-auction-light-yellow) 16%, transparent)',
            },
            b: {
                borderColor: '{auctionLight.blue}',
                boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-auction-light-blue) 16%, transparent)',
            },
            w: {
                borderColor: '{auctionLight.white}',
                boxShadow: '0 0 0 3px color-mix(in srgb, var(--p-auction-light-white) 16%, transparent)',
            },
        },
        foot: {
            paddingTop: '6px',
            borderTopWidth: '1px',
            borderTopColor: '{content.borderColor}',
            fontSize: '10px',
            color: '{surface.400}',
        },
        done: {
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '700',
            color: '{text.mutedColor}',
        },
    },

    // LightsRow — .lights-row
    lightsRow: {
        pucks: {
            gap: '8px',
        },
        unset: {
            fontSize: '11px',
            color: '{surface.400}',
        },
        edit: {
            borderRadius: '999px',
            color: '{surface.400}',
        },
    },

    // OutcomeButton — .outcome-button
    //
    // Nine tones, each named for the verb. Every lift is the tone's own fill at
    // 28%, written as a `color-mix` over the token rather than a second color,
    // so re-tinting an outcome moves its glow with it.
    outcomeButton: {
        borderRadius: '10px',
        borderWidth: '1px',
        padding: '14px 16px',
        gap: '4px',
        lineHeight: '1.15',
        letterSpacing: '0.04em',
        compact: {
            padding: '10px 14px',
        },
        big: {
            padding: '22px 28px',
            compact: {
                padding: '16px 24px',
            },
            icon: {
                fontSize: '19px',
            },
            label: {
                fontSize: '22px',
            },
            sub: {
                fontSize: '12px',
            },
        },
        icon: {
            fontSize: '13px',
        },
        label: {
            fontSize: '15px',
            fontWeight: '800',
            letterSpacing: '0.06em',
        },
        sub: {
            fontSize: '10px',
            fontWeight: '500',
        },
        hotkey: {
            fontFamily: '{font.mono}',
            fontSize: '10px',
            fontWeight: '700',
        },
        sell: {
            background: '{primary.color}',
            hover: { background: '{primary.600}' },
            color: '{primary.contrastColor}',
            borderColor: '{primary.600}',
            boxShadow: '0 6px 16px color-mix(in srgb, var(--p-primary-color) 28%, transparent)',
        },
        floor: {
            background: '{status.warning}',
            hover: { background: 'color-mix(in srgb, var(--p-status-warning) 88%, black)' },
            color: '{status.warningContrast}',
            borderColor: '{status.warning}',
            boxShadow: '0 6px 16px color-mix(in srgb, var(--p-status-warning) 28%, transparent)',
        },
        noSale: {
            background: '{outcome.noSale}',
            hover: { background: 'color-mix(in srgb, var(--p-outcome-no-sale) 88%, black)' },
            color: '{outcome.noSaleContrast}',
            borderColor: '{outcome.noSale}',
            boxShadow: '0 6px 16px color-mix(in srgb, var(--p-outcome-no-sale) 32%, transparent)',
        },
        offer: {
            background: '{outcome.offer}',
            hover: { background: 'color-mix(in srgb, var(--p-outcome-offer) 88%, black)' },
            color: '{outcome.offerContrast}',
            borderColor: '{outcome.offer}',
            boxShadow: '0 6px 16px color-mix(in srgb, var(--p-outcome-offer) 30%, transparent)',
        },
        info: {
            background: '{status.info}',
            hover: { background: 'color-mix(in srgb, var(--p-status-info) 88%, black)' },
            color: '{status.infoContrast}',
            borderColor: '{status.info}',
            boxShadow: '0 6px 16px color-mix(in srgb, var(--p-status-info) 28%, transparent)',
        },
        danger: {
            background: '{status.danger}',
            hover: { background: 'color-mix(in srgb, var(--p-status-danger) 88%, black)' },
            color: '{status.dangerContrast}',
            borderColor: '{status.danger}',
            boxShadow: '0 6px 16px color-mix(in srgb, var(--p-status-danger) 28%, transparent)',
        },
        neutral: {
            background: '{content.background}',
            hover: { background: '{content.hoverBackground}' },
            color: '{text.color}',
            borderColor: '{formField.borderColor}',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
        },
        ghost: {
            background: 'transparent',
            hover: { background: '{content.hoverBackground}' },
            color: '{text.mutedColor}',
            borderColor: '{content.borderColor}',
        },
        dangerGhost: {
            background: 'transparent',
            hover: { background: 'color-mix(in srgb, var(--p-status-danger) 8%, transparent)' },
            color: '{status.danger}',
            borderColor: 'color-mix(in srgb, var(--p-status-danger) 30%, transparent)',
        },
    },

    // RepOfferCard — .rep-offer-card
    repOfferCard: {
        background: 'color-mix(in srgb, var(--p-outcome-rep) 16%, transparent)',
        borderWidth: '1.5px',
        borderColor: '{outcome.rep}',
        borderRadius: '12px',
        padding: '10px 14px',
        body: {
            gap: '10px',
        },
        watermarkBadge: {
            padding: '6px 14px',
            borderRadius: '999px',
            background: '{content.hoverBackground}',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.28)',
            fontSize: '11px',
            fontWeight: '800',
            letterSpacing: '0.12em',
            color: '{text.mutedColor}',
        },
        eyebrow: {
            fontSize: '9px',
            fontWeight: '800',
            letterSpacing: '0.1em',
            color: '{outcome.rep}',
        },
        name: {
            fontSize: '13px',
            fontWeight: '700',
            color: '{text.color}',
        },
        pill: {
            fontSize: '14px',
            fontWeight: '800',
            letterSpacing: '0.08em',
            color: '{outcome.repContrast}',
            background: '{outcome.rep}',
            borderRadius: '8px',
            padding: '8px 14px',
        },
    },

    // RepPriceStrip — .rep-price-strip
    repPriceStrip: {
        gap: '8px',
        cell: {
            gap: '2px',
            padding: '8px 12px',
            background: '{content.background}',
            borderWidth: '1px',
            borderColor: '{content.borderColor}',
            borderRadius: '10px',
            head: {
                gap: '6px',
            },
        },
        label: {
            fontSize: '9px',
            fontWeight: '800',
            letterSpacing: '0.1em',
            color: '{surface.400}',
        },
        value: {
            fontFamily: '{font.mono}',
            fontSize: '20px',
            fontWeight: '800',
            letterSpacing: '-0.01em',
            lineHeight: '1.1',
            color: '{text.color}',
        },
        // A reserve the rep moved mid-sale, in the selling rep's own orange.
        new: {
            background: 'color-mix(in srgb, var(--p-outcome-rep) 16%, transparent)',
            borderWidth: '1.5px',
            borderColor: '{outcome.rep}',
            boxShadow: '0 0 0 4px color-mix(in srgb, var(--p-outcome-rep) 14%, transparent)',
            label: {
                color: '{outcome.rep}',
            },
            value: {
                color: '{outcome.rep}',
            },
            badge: {
                padding: '1px 6px',
                borderRadius: '999px',
                background: '{outcome.rep}',
                color: '{outcome.repContrast}',
                fontSize: '8px',
                fontWeight: '800',
                letterSpacing: '0.1em',
            },
        },
        clear: {
            color: '{surface.400}',
            fontSize: '11px',
            hover: {
                color: '{status.danger}',
            },
        },
        reset: {
            gap: '4px',
            background: '{content.background}',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            borderRadius: '10px',
            color: '{text.mutedColor}',
            fontSize: '11px',
            fontWeight: '800',
            letterSpacing: '0.08em',
            hover: {
                background: '{content.hoverBackground}',
            },
        },
        confirm: {
            gap: '10px',
            message: {
                gap: '8px',
                fontSize: '13px',
                color: '{text.color}',
                lineHeight: '1.4',
            },
            icon: {
                color: '{status.warning}',
                fontSize: '14px',
            },
            actions: {
                gap: '6px',
            },
            accept: {
                background: '{status.danger}',
                borderColor: '{status.danger}',
                color: '{status.dangerContrast}',
            },
        },
    },

    // OnBlockPanel — .on-block-panel
    onBlockPanel: {
        gap: '12px',
        head: {
            background: '{content.background}',
            borderWidth: '1px',
            borderColor: '{content.borderColor}',
            borderRadius: '10px',
            padding: '12px 14px',
            gap: '10px',
        },
        title: {
            fontSize: '17px',
            fontWeight: '700',
            lineHeight: '1.2',
            color: '{text.color}',
        },
        year: {
            fontWeight: '700',
            color: '{surface.400}',
        },
        body: {
            gap: '12px',
        },
    },

    // RunNumber — .run-number
    runNumber: {
        gap: '6px',
        color: '{text.color}',
        eyebrow: {
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.1em',
            color: '{surface.400}',
        },
        value: {
            fontFamily: '{font.mono}',
            fontWeight: '800',
            fontSize: '26px',
            letterSpacing: '-0.02em',
            lineHeight: '1',
            color: '{text.color}',
        },
    },

    // RunSearchField — .run-search-field
    runSearchField: {
        gap: '8px',
        padding: '4px 8px 4px 12px',
        borderWidth: '1px',
        borderColor: '{formField.borderColor}',
        borderRadius: '8px',
        background: '{formField.background}',
        label: {
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.08em',
            color: '{surface.400}',
        },
        input: {
            fontFamily: '{font.mono}',
            fontWeight: '700',
            fontSize: '14px',
            color: '{text.color}',
            padding: '0',
        },
        submit: {
            borderRadius: '6px',
            background: '{content.hoverBackground}',
            color: '{text.mutedColor}',
        },
    },

    // SourceBadge — .source-badge
    sourceBadge: {
        borderRadius: '6px',
        fontFamily: '{font.mono}',
        fontWeight: '800',
        floor: {
            background: '{status.warning}',
            color: '{status.warningContrast}',
        },
        online: {
            background: '{status.info}',
            color: '{status.infoContrast}',
        },
        prebid: {
            background: '{primary.color}',
            color: '{primary.contrastColor}',
        },
    },

    // StatTile — .stat-tile
    statTile: {
        background: '{content.background}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '10px',
        padding: '12px 14px',
        gap: '4px',
        label: {
            fontSize: '10px',
            fontWeight: '600',
            letterSpacing: '0.08em',
            color: '{surface.400}',
        },
        value: {
            fontFamily: '{font.mono}',
            fontWeight: '700',
            fontSize: '22px',
            letterSpacing: '-0.01em',
            lineHeight: '1.15',
            neutral: { color: '{text.color}' },
            success: { color: '{primary.color}' },
            warning: { color: '{status.warning}' },
            danger: { color: '{status.danger}' },
            info: { color: '{status.info}' },
        },
        sub: {
            fontSize: '11px',
            color: '{text.mutedColor}',
        },
        // Scoreboard order (the phone's Bid Stack Won / Lost / Budget): the
        // figure on top, its word under it in a quiet sentence-case caption.
        valueFirst: {
            gap: '3px',
            padding: '11px 13px',
            borderRadius: '14px',
            label: {
                fontSize: '11px',
                fontWeight: '600',
                letterSpacing: 'normal',
                color: '{text.mutedColor}',
            },
        },
        // The score row that pins under the notch: one baseline, small type.
        condensed: {
            gap: '5px',
            padding: '5px 9px',
            borderRadius: '10px',
            value: { fontSize: '15px' },
            label: { fontSize: '10px' },
        },
    },

    // StatsPanel — .stats-panel
    statsPanel: {
        gap: '8px',
    },

    // StopEventDrawer — .stop-event-drawer
    stopEventDrawer: {
        background: 'var(--surface-ground)',
        mask: {
            background: 'rgba(0, 0, 0, 0.55)',
        },
        inner: {
            gap: '22px',
            padding: '44px 24px',
        },
        head: {
            gap: '12px',
            icon: {
                borderRadius: '10px',
                background: 'color-mix(in srgb, var(--p-status-danger) 12%, transparent)',
                borderWidth: '1px',
                borderColor: 'color-mix(in srgb, var(--p-status-danger) 40%, transparent)',
                color: '{status.danger}',
                fontSize: '18px',
            },
            title: {
                fontSize: '22px',
                fontWeight: '800',
                color: '{text.color}',
            },
        },
        dismiss: {
            borderRadius: '9px',
            background: '{content.background}',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            color: '{text.mutedColor}',
        },
        question: {
            fontSize: '14px',
            color: '{text.mutedColor}',
        },
        choices: {
            gap: '14px',
        },
        choice: {
            gap: '8px',
            padding: '18px',
            borderRadius: '14px',
            background: '{content.background}',
            borderWidth: '2px',
            borderColor: '{formField.borderColor}',
            color: '{text.color}',
            icon: {
                fontSize: '22px',
            },
            title: {
                fontSize: '16px',
                fontWeight: '800',
                color: '{text.color}',
            },
            subtitle: {
                fontSize: '12.5px',
                lineHeight: '1.4',
                color: '{surface.400}',
            },
        },
        // Recoverable, and amber. Terminal, and red. The two are not variants.
        pause: {
            color: '{status.warning}',
            contrastColor: '{status.warningContrast}',
            selected: {
                background: 'color-mix(in srgb, var(--p-status-warning) 12%, transparent)',
            },
        },
        end: {
            color: '{status.danger}',
            contrastColor: '{status.dangerContrast}',
            selected: {
                background: 'color-mix(in srgb, var(--p-status-danger) 12%, transparent)',
            },
        },
        reason: {
            gap: '7px',
            label: {
                fontSize: '11px',
                fontWeight: '700',
                color: '{text.mutedColor}',
            },
            optional: {
                fontWeight: '500',
                color: '{surface.400}',
            },
            echo: {
                padding: '10px 14px',
                borderRadius: '10px',
                background: '{content.background}',
                borderWidth: '1px',
                borderColor: '{content.borderColor}',
                fontSize: '13px',
                color: '{text.mutedColor}',
                label: {
                    fontSize: '10px',
                    fontWeight: '700',
                    letterSpacing: '0.08em',
                    color: '{surface.400}',
                },
            },
        },
        textarea: {
            fontSize: '14px',
            lineHeight: '1.4',
            padding: '10px 14px',
            borderRadius: '10px',
            background: '{content.background}',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            color: '{text.color}',
        },
        warning: {
            fontSize: '14px',
            lineHeight: '1.5',
            color: '{text.mutedColor}',
            strong: {
                color: '{text.color}',
            },
        },
        actions: {
            gap: '12px',
        },
        cancel: {
            padding: '12px 22px',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: '700',
            background: '{content.background}',
            borderWidth: '1px',
            borderColor: '{formField.borderColor}',
            color: '{text.mutedColor}',
        },
        primary: {
            padding: '12px 24px',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: '800',
            gap: '7px',
        },
    },

    // TimeOnBlock — .time-on-block
    timeOnBlock: {
        gap: '6px',
        padding: '8px 12px',
        borderRadius: '10px',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        background: '{content.background}',
        left: {
            gap: '8px',
        },
        right: {
            gap: '10px',
        },
        dot: {
            borderRadius: '999px',
            background: '{primary.color}',
        },
        title: {
            fontSize: '10px',
            fontWeight: '800',
            letterSpacing: '0.1em',
            color: '{text.mutedColor}',
        },
        phase: {
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.04em',
            color: '{primary.color}',
        },
        clock: {
            fontFamily: '{font.mono}',
            fontSize: '20px',
            fontWeight: '800',
            letterSpacing: '-0.02em',
            color: '{text.color}',
        },
        bar: {
            borderRadius: '999px',
            background: '{content.hoverBackground}',
        },
        fill: {
            background: '{primary.color}',
        },
        tick: {
            background: '{formField.borderColor}',
        },
        // Past the target the whole card takes the tone, not just the clock —
        // this one is read from across the block.
        warn: {
            background: 'color-mix(in srgb, var(--p-status-warning) 8%, transparent)',
            borderColor: 'color-mix(in srgb, var(--p-status-warning) 50%, transparent)',
            dot: {
                background: '{status.warning}',
                boxShadow: '0 0 8px var(--p-status-warning)',
            },
            phase: { color: '{status.warning}' },
            clock: { color: '{status.warning}' },
            fill: { background: '{status.warning}' },
        },
        urgent: {
            background: 'color-mix(in srgb, var(--p-status-danger) 10%, transparent)',
            borderColor: 'color-mix(in srgb, var(--p-status-danger) 55%, transparent)',
            dot: {
                background: '{status.danger}',
                boxShadow: '0 0 8px var(--p-status-danger)',
            },
            phase: { color: '{status.danger}' },
            clock: { color: '{status.danger}' },
            fill: { background: '{status.danger}' },
        },
    },

    // UpNextPanel — .up-next-panel
    upNextPanel: {
        background: '{content.background}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '10px',
        head: {
            padding: '8px 12px',
            borderBottomWidth: '1px',
            borderBottomColor: '{content.borderColor}',
            background: '{content.hoverBackground}',
        },
        title: {
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.08em',
            color: '{text.mutedColor}',
        },
        count: {
            fontFamily: '{font.mono}',
            fontSize: '11px',
            color: '{surface.400}',
        },
        empty: {
            padding: '16px 12px',
            fontSize: '12px',
            color: '{surface.400}',
        },
    },

    // UpNextRow — .up-next-row
    upNextRow: {
        gap: '10px',
        padding: '7px 12px',
        borderBottomWidth: '1px',
        borderBottomColor: '{content.borderColor}',
        // Mint wash plus a left rail, the same treatment the set increment gets.
        onBlock: {
            background: '{highlight.background}',
            boxShadow: 'inset 3px 0 0 0 var(--p-primary-color)',
            run: {
                background: 'color-mix(in srgb, var(--p-primary-color) 16%, transparent)',
            },
        },
        run: {
            fontFamily: '{font.mono}',
            fontWeight: '800',
            fontSize: '13px',
            color: '{text.color}',
            letterSpacing: '-0.01em',
            padding: '2px 6px',
            borderRadius: '4px',
            background: '{content.hoverBackground}',
        },
        description: {
            gap: '8px',
        },
        title: {
            fontSize: '12px',
            fontWeight: '500',
            color: '{text.color}',
        },
        year: {
            fontWeight: '700',
            color: '{surface.400}',
        },
        bid: {
            gap: '3px',
            amount: {
                fontFamily: '{font.mono}',
                fontSize: '12px',
                fontWeight: '700',
                color: '{text.color}',
            },
            badge: {
                fontSize: '11px',
                color: '{surface.400}',
            },
        },
        status: {
            empty: {
                fontSize: '11px',
                color: '{surface.400}',
            },
            // Solid fills matching the action that produced each outcome, so
            // the list and the buttons agree at a glance.
            pill: {
                padding: '2px 9px',
                borderRadius: '999px',
                fontSize: '10px',
                fontWeight: '700',
                letterSpacing: '0.04em',
                asking: {
                    background: '{status.info}',
                    color: '{status.infoContrast}',
                },
                selling: {
                    background: '{status.warning}',
                    color: '{status.warningContrast}',
                },
                sold: {
                    background: '{primary.color}',
                    color: '{primary.contrastColor}',
                },
                notSold: {
                    background: '{outcome.noSale}',
                    color: '{outcome.noSaleContrast}',
                },
                offer: {
                    background: '{outcome.offer}',
                    color: '{outcome.offerContrast}',
                },
                unknown: {
                    background: '{content.hoverBackground}',
                    color: '{text.mutedColor}',
                },
            },
        },
    },

    // ValuationInline — .valuation-inline
    valuationInline: {
        padding: '0 4px',
        label: {
            fontSize: '9px',
            fontWeight: '800',
            letterSpacing: '0.06em',
            color: '{surface.400}',
        },
        value: {
            fontFamily: '{font.mono}',
            fontSize: '14px',
            fontWeight: '800',
            letterSpacing: '-0.01em',
            lineHeight: '1.1',
            color: '{text.color}',
        },
        highlight: {
            label: { color: '{primary.color}' },
            value: { color: '{primary.color}' },
        },
    },

    // ValuationStrip — .valuation-strip
    valuationStrip: {
        paddingTop: '8px',
        borderTopWidth: '1px',
        borderTopColor: '{content.borderColor}',
        rule: {
            background: '{content.borderColor}',
        },
    },

    // AssetDetailsCard — .asset-details-card
    assetDetailsCard: {
        background: '{content.background}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '10px',
        padding: '10px 12px',
        gap: '8px',
        year: {
            fontSize: '10px',
            fontWeight: '600',
            letterSpacing: '0.08em',
            color: '{surface.400}',
        },
        title: {
            fontSize: '15px',
            fontWeight: '700',
            lineHeight: '1.25',
            color: '{text.color}',
        },
        grid: {
            gap: '5px 12px',
            fontSize: '12px',
            paddingTop: '6px',
            borderTopWidth: '1px',
            borderTopColor: '{content.borderColor}',
        },
        key: {
            color: '{surface.400}',
            fontWeight: '600',
        },
        value: {
            fontFamily: '{font.mono}',
            fontWeight: '600',
            color: '{text.color}',
            code: {
                letterSpacing: '0.02em',
            },
            strong: {
                fontWeight: '700',
            },
        },
        plain: {
            color: '{text.color}',
        },
        timesRun: {
            fontSize: '10px',
            fontWeight: '700',
            color: '{status.warning}',
        },
        seller: {
            gap: '6px',
            color: '{text.color}',
            badge: {
                fontFamily: '{font.mono}',
                fontSize: '10px',
                color: '{surface.400}',
                padding: '1px 5px',
                borderRadius: '4px',
                background: '{content.hoverBackground}',
            },
        },
    },

    // ── The buyer marketplace's desktop home ───────────────────────────────
    //
    // The chrome and the board a dealer lands on: the top nav, the rail of
    // running sales, the hero, the tracked-asset rows and the two panels down
    // the right.
    //
    // Two things in here are deliberately NOT scheme-aware, and both are the
    // same decision. The top nav and the hero are dark in both schemes, so they
    // read `{surface.900}` / `{surface.950}` — the two ends of the ramp that do
    // not swap between light and dark — and their ink reads `{surface.0}`
    // rather than `{text.color}`. A nav that inverted with the page would put
    // the wordmark on white in light mode, which is not what the brand is.
    //
    // For the same reason their accent is `{primary.400}` rather than
    // `{primary.color}`: light mode promotes the accent to `{primary.800}` so it
    // holds contrast on white, and that shade disappears on near-black.

    // MarketplaceTopNav — .marketplace-top-nav
    marketplaceTopNav: {
        background: '{surface.900}',
        color: '{surface.0}',
        padding: '0 14px',
        gap: '14px',
        brand: {
            padding: '0',
            color: '{surface.0}',
            fontFamily: '{font.display}',
            fontSize: '14px',
            fontWeight: '800',
            letterSpacing: '-0.02em',
            suffix: {
                fontSize: '9px',
                fontWeight: '600',
                color: '{primary.400}',
            },
        },
        links: {
            gap: '2px',
        },
        link: {
            gap: '6px',
            padding: '5px 9px',
            borderRadius: '7px',
            color: '{surface.300}',
            fontSize: '11.5px',
            fontWeight: '600',
            icon: {
                fontSize: '10px',
            },
            hover: {
                background: 'color-mix(in srgb, var(--p-surface-0) 8%, transparent)',
                color: '{surface.0}',
            },
            active: {
                background: 'color-mix(in srgb, var(--p-surface-0) 12%, transparent)',
                color: '{surface.0}',
            },
            // A count, not a dot — see the component's own note.
            badge: {
                padding: '0 4px',
                borderRadius: '7px',
                background: '{status.danger}',
                color: '{surface.0}',
                fontSize: '8.5px',
                fontWeight: '800',
            },
        },
        identity: {
            gap: '6px',
            padding: '4px 9px',
            borderRadius: '999px',
            background: 'color-mix(in srgb, var(--p-surface-0) 10%, transparent)',
            color: '{surface.0}',
            fontSize: '10.5px',
            fontWeight: '700',
            icon: {
                fontSize: '9px',
                color: '{primary.400}',
            },
            label: {
                color: '{surface.300}',
                fontWeight: '500',
            },
            badge: {
                fontFamily: '{font.mono}',
                fontSize: '9.5px',
                color: '{surface.300}',
            },
        },
        avatar: {
            borderRadius: '50%',
            background: '{primary.400}',
            color: '{primary.950}',
            fontSize: '9.5px',
            fontWeight: '800',
            letterSpacing: '0.02em',
        },
    },

    // LiveNowRail — .live-now-rail
    liveNowRail: {
        background: '{content.background}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.card}',
        padding: '10px 12px 12px',
        gap: '9px',
        head: {
            gap: '8px',
        },
        count: {
            color: '{text.mutedColor}',
            fontSize: '10.5px',
        },
        controls: {
            gap: '5px',
        },
        arrow: {
            borderWidth: '1px',
            borderColor: '{content.borderColor}',
            borderRadius: '6px',
            background: '{content.background}',
            color: '{text.mutedColor}',
            fontSize: '9px',
            hover: {
                background: '{content.hoverBackground}',
            },
        },
        track: {
            gap: '9px',
        },
    },

    // LiveSaleCard — .live-sale-card
    liveSaleCard: {
        background: '{content.background}',
        color: '{text.color}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '10px',
        padding: '9px 10px',
        gap: '7px',
        // A live lane earns a left edge, not a filled card; LiveBadge draws the
        // word and the pip that used to sit beside it.
        live: {
            borderLeftWidth: '2px',
            borderLeftColor: '{status.danger}',
        },
        head: {
            gap: '6px',
        },
        name: {
            padding: '0',
            color: '{text.color}',
            fontSize: '12px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
        },
        meta: {
            gap: '6px',
        },
        type: {
            gap: '3px',
            padding: '1px 5px',
            borderRadius: '4px',
            fontSize: '8px',
            fontWeight: '800',
            letterSpacing: '0.04em',
            icon: {
                fontSize: '8px',
            },
            simulcast: {
                background: 'color-mix(in srgb, var(--p-status-success) 15%, transparent)',
                color: '{status.success}',
            },
            automated: {
                background: 'color-mix(in srgb, var(--p-status-warning) 18%, transparent)',
                color: '{status.warning}',
            },
            timed: {
                background: 'color-mix(in srgb, var(--p-status-info) 16%, transparent)',
                color: '{status.info}',
            },
        },
        where: {
            color: '{text.mutedColor}',
            fontSize: '9.5px',
        },
        foot: {
            gap: '8px',
        },
        counts: {
            color: '{text.mutedColor}',
            fontSize: '10px',
        },
        run: {
            fontFamily: '{font.mono}',
            fontWeight: '700',
            color: '{text.color}',
        },
        crossed: {
            fontFamily: '{font.mono}',
            color: '{text.mutedColor}',
        },
        separator: {
            color: '{text.mutedColor}',
        },
        myList: {
            color: '{primary.color}',
            fontWeight: '700',
        },
        join: {
            gap: '4px',
            padding: '4px 10px',
            borderRadius: '6px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontSize: '10.5px',
            fontWeight: '700',
            icon: {
                fontSize: '9px',
            },
            hover: {
                background: '{primary.hoverColor}',
            },
        },
    },

    // FeaturedAssetHero — .featured-asset-hero
    featuredAssetHero: {
        background: '{surface.950}',
        color: '{surface.0}',
        borderRadius: '{radius.card}',
        // `boxShadow` is scheme-split — see componentTokensByColorScheme below.
        scrimBackground:
            'linear-gradient(90deg, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0.55) 38%,'
            + ' rgba(0, 0, 0, 0.05) 70%, transparent 100%)',
        // The six placeholder grounds, shown while the photograph loads and kept
        // if it never does. Keyed to the SLIDE INDEX rather than to the asset, so
        // reordering a programme changes which tone a given asset gets — which is
        // the design's own decision and is only visible pre-load.
        //
        // Literals, and they earn it: a placeholder tone under a photograph is
        // not a role any semantic token names, and the design does not theme them
        // either. Deliberately dark in BOTH schemes, because what sits on them is
        // white type over photography.
        backdrop: {
            toneOne: { background: 'linear-gradient(120deg, #1e3a5f, #0b1a2b)' },
            toneTwo: { background: 'linear-gradient(120deg, #3a2a4d, #15101f)' },
            toneThree: { background: 'linear-gradient(120deg, #1f4035, #0a1a14)' },
            toneFour: { background: 'linear-gradient(120deg, #4a3520, #1c130a)' },
            toneFive: { background: 'linear-gradient(120deg, #2c2f4a, #10111f)' },
            toneSix: { background: 'linear-gradient(120deg, #43283a, #1a0f16)' },
        },
        // What a failed photograph leaves behind: a woven stripe, an oversized
        // car glyph bled off the right, and the wordmark. Not an empty state —
        // the asset is real and everything else about it still reads.
        fallback: {
            background:
                'repeating-linear-gradient(115deg, rgba(255, 255, 255, 0.03) 0 14px,'
                + ' transparent 14px 30px)',
            glyph: {
                fontSize: '190px',
                color: 'rgba(255, 255, 255, 0.07)',
            },
            mark: {
                fontSize: '13px',
                fontWeight: '700',
                letterSpacing: '-0.01em',
                lead: { color: 'rgba(255, 255, 255, 0.34)' },
                tail: { color: 'rgba(56, 216, 152, 0.55)' },
            },
        },
        // The cut toggle, as one pill track with the selected item filled. The
        // track's own fill and blur are what let it sit on a photograph at the
        // top edge, where the scrim has not finished darkening.
        categories: {
            gap: '0',
            padding: '4px',
            borderRadius: '999px',
            borderWidth: '1px',
            borderColor: 'rgba(255, 255, 255, 0.18)',
            background: 'rgba(0, 0, 0, 0.42)',
        },
        // Hard white on hard near-black in BOTH schemes, and one of the two
        // places a token-only audit flags this component. It is correct: the
        // control sits on a photograph, not on a surface, so a scheme-aware
        // token would be answering the wrong question.
        category: {
            padding: '6px 15px',
            borderRadius: '999px',
            background: 'transparent',
            color: 'rgba(255, 255, 255, 0.78)',
            fontSize: '12.5px',
            fontWeight: '700',
            hover: { color: '{surface.0}' },
            active: {
                background: '{surface.0}',
                color: '#111111',
            },
        },
        body: { gap: '11px' },
        stack: { gap: '6px' },
        // Two treatments, and which one shows says which programme put the asset
        // in front of the dealer: the house's own selection is a solid mint
        // plate, a machine recommendation is glass over the photograph. Told
        // apart at a glance rather than by reading the word.
        badge: {
            gap: '6px',
            padding: '5px 11px',
            borderRadius: '999px',
            borderWidth: '1px',
            borderColor: 'transparent',
            background: '{primary.500}',
            color: '{primary.950}',
            fontSize: '11px',
            fontWeight: '800',
            letterSpacing: '0.06em',
            icon: { fontSize: '10px' },
            suggestion: {
                borderColor: 'rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.16)',
                color: '{surface.0}',
            },
        },
        title: {
            fontFamily: '{font.display}',
            fontSize: '29px',
            fontWeight: '800',
            letterSpacing: '{display.tracking}',
            lineHeight: '1.06',
            color: '{surface.0}',
        },
        meta: {
            gap: '11px',
            color: 'rgba(255, 255, 255, 0.82)',
            fontSize: '13px',
            // The row color at half strength, written out rather than left as an
            // `opacity` rule: opacity is not a paint a theme addresses, and a
            // separator that dims with its own token is one fewer thing to
            // discover in the cascade.
            separator: { color: 'rgba(255, 255, 255, 0.41)' },
        },
        price: {
            fontFamily: '{font.mono}',
            fontSize: '13px',
            fontWeight: '800',
            color: '{surface.0}',
        },
        actions: { gap: '10px' },
        // `background` and `hover.background` are scheme-split — see
        // componentTokensByColorScheme below, and the note there for why the
        // bright mint belongs here rather than in `primary.color`.
        research: {
            gap: '8px',
            padding: '9px 17px',
            borderRadius: '{borderRadius.md}',
            borderWidth: '1px',
            borderColor: 'transparent',
            color: '{primary.950}',
            fontSize: '14px',
            fontWeight: '800',
            // Relative on purpose: the glyph tracks the label rather than being
            // a second size to keep in step with it.
            icon: { fontSize: '0.85em' },
        },
        track: {
            gap: '8px',
            padding: '10px 14px',
            borderWidth: '1px',
            borderColor: 'rgba(255, 255, 255, 0.28)',
            borderRadius: '{borderRadius.md}',
            background: 'transparent',
            color: 'rgba(255, 255, 255, 0.86)',
            fontSize: '12.5px',
            fontWeight: '600',
            icon: { fontSize: '0.85em' },
            hover: {
                borderColor: 'rgba(255, 255, 255, 0.4)',
                background: 'rgba(255, 255, 255, 0.08)',
                color: '{surface.0}',
            },
        },
        // The rail carries no plate of its own. Every control on it is legible
        // because IT is filled, which is what keeps a bare counter readable over
        // a bright photograph without a box around the whole cluster.
        pager: { gap: '10px' },
        step: {
            borderRadius: '999px',
            borderWidth: '1px',
            borderColor: 'rgba(255, 255, 255, 0.28)',
            background: 'rgba(0, 0, 0, 0.42)',
            color: '{surface.0}',
            fontSize: '10px',
            hover: {
                borderColor: 'rgba(255, 255, 255, 0.4)',
                background: 'rgba(0, 0, 0, 0.62)',
            },
        },
        position: {
            fontFamily: '{font.mono}',
            fontSize: '11px',
            fontWeight: '700',
            letterSpacing: '0.06em',
            color: 'rgba(255, 255, 255, 0.7)',
        },
        dots: { gap: '4px' },
        // The lighter 400 step in both schemes, where the primary button is 500
        // in light. Deliberate rather than a drift: the tick is over
        // photography and the button is on a fill, so they are answering
        // different contrast questions.
        dot: {
            borderRadius: '2px',
            background: 'rgba(255, 255, 255, 0.38)',
            active: { background: '{primary.400}' },
        },
        // ONE focus treatment for every control on the hero, which is why it
        // hangs off the component rather than off each of them: what the ring
        // has to survive is a photograph, and that is the same problem for a
        // pill, a button and a 3px tick. Two rings, white then dark, so one of
        // them always has something to sit against — on the selected white pill
        // the inner ring disappears and the outer one carries it, and over a
        // dark photograph it is the other way round.
        focus: {
            boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.9), 0 0 0 4px rgba(0, 0, 0, 0.55)',
        },
    },

    // TrackedAssetRow — .tracked-asset-row
    trackedAssetRow: {
        background: '{content.background}',
        color: '{text.color}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.row}',
        padding: '10px 12px',
        gap: '11px',
        compactPadding: '5px 12px',
        hover: {
            background: '{content.hoverBackground}',
        },
        thumbnail: {
            borderRadius: '7px',
            background: '{content.hoverBackground}',
            color: '{text.mutedColor}',
            fontSize: '14px',
        },
        run: {
            padding: '0 3px',
            borderRadius: '3px',
            background: 'rgba(0, 0, 0, 0.72)',
            color: '{surface.0}',
            fontFamily: '{font.mono}',
            fontSize: '8px',
            fontWeight: '700',
        },
        identity: {
            gap: '2px',
        },
        name: {
            fontSize: '13px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
        },
        vin: {
            fontFamily: '{font.mono}',
            fontSize: '9.5px',
            color: '{text.mutedColor}',
        },
        // `auctionLight`, never `light` — see the hero's note above.
        auctionLight: {
            borderRadius: '50%',
            boxShadow: '0 0 0 0.5px rgba(0, 0, 0, 0.4) inset',
            red: { background: '{auctionLight.red}' },
            green: { background: '{auctionLight.green}' },
            yellow: { background: '{auctionLight.yellow}' },
            blue: { background: '{auctionLight.blue}' },
            white: { background: '{auctionLight.white}' },
        },
        state: {
            padding: '2px 7px',
            borderRadius: '5px',
            fontSize: '8.5px',
            fontWeight: '800',
            letterSpacing: '0.05em',
            winning: {
                background: 'color-mix(in srgb, var(--p-status-success) 16%, transparent)',
                color: '{status.success}',
            },
            outbid: {
                background: 'color-mix(in srgb, var(--p-status-danger) 15%, transparent)',
                color: '{status.danger}',
            },
            attending: {
                background: 'color-mix(in srgb, var(--p-status-info) 16%, transparent)',
                color: '{status.info}',
            },
        },
        money: {
            gap: '1px',
        },
        bid: {
            fontFamily: '{font.mono}',
            fontSize: '13px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
        },
        market: {
            fontFamily: '{font.mono}',
            fontSize: '9px',
            color: '{text.mutedColor}',
        },
        chevron: {
            fontSize: '10px',
            color: '{text.mutedColor}',
        },
    },

    // MarketplacePanel — .marketplace-panel
    marketplacePanel: {
        background: '{content.background}',
        color: '{text.color}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.card}',
        head: {
            gap: '8px',
            padding: '8px 12px',
            borderBottomWidth: '1px',
            borderBottomColor: '{content.borderColor}',
            background: '{content.hoverBackground}',
        },
        title: {
            fontSize: '9.5px',
            fontWeight: '800',
            letterSpacing: '0.07em',
            color: '{text.color}',
        },
        link: {
            padding: '0',
            color: '{text.mutedColor}',
            fontSize: '10px',
            fontWeight: '600',
            hover: {
                color: '{primary.color}',
            },
        },
    },

    // AttentionPanel — .attention-panel
    attentionPanel: {
        // The tinted band the won assets sit in.
        won: {
            padding: '8px 12px 6px',
            background: 'color-mix(in srgb, var(--p-status-success) 10%, transparent)',
            borderBottomWidth: '1px',
            borderBottomColor: '{content.borderColor}',
            head: {
                gap: '8px',
            },
            title: {
                fontSize: '10.5px',
                fontWeight: '800',
                color: '{status.success}',
            },
            row: {
                gap: '9px',
                padding: '5px 0',
            },
            identity: {
                gap: '1px',
            },
            name: {
                fontSize: '11px',
                fontWeight: '600',
                color: '{text.color}',
            },
            amount: {
                fontFamily: '{font.mono}',
                fontSize: '9.5px',
                color: '{text.mutedColor}',
            },
        },
        due: {
            fontFamily: '{font.mono}',
            fontSize: '9.5px',
            fontWeight: '700',
            color: '{text.mutedColor}',
        },
        thumbnail: {
            borderRadius: '5px',
            background: '{content.hoverBackground}',
            color: '{text.mutedColor}',
            fontSize: '12px',
        },
        checkout: {
            padding: '4px 10px',
            borderRadius: '6px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontSize: '10px',
            fontWeight: '700',
            hover: {
                background: '{primary.hoverColor}',
            },
        },
        more: {
            gap: '4px',
            padding: '3px 0 0',
            color: '{status.success}',
            fontSize: '9.5px',
            fontWeight: '700',
            icon: {
                fontSize: '8px',
            },
        },
        signal: {
            gap: '9px',
            padding: '9px 12px',
            background: 'transparent',
            color: '{text.color}',
            borderTopWidth: '1px',
            borderTopColor: '{content.borderColor}',
            hover: {
                background: '{content.hoverBackground}',
            },
            icon: {
                fontSize: '12px',
                danger: { color: '{status.danger}' },
                success: { color: '{status.success}' },
                info: { color: '{status.info}' },
            },
            text: {
                gap: '1px',
            },
            label: {
                fontSize: '11.5px',
                fontWeight: '700',
            },
            caption: {
                fontSize: '9.5px',
                color: '{text.mutedColor}',
            },
            count: {
                fontFamily: '{font.mono}',
                fontSize: '12px',
                fontWeight: '700',
            },
            chevron: {
                fontSize: '10px',
                color: '{text.mutedColor}',
            },
        },
    },

    // OpenOffersPanel — .open-offers-panel
    openOffersPanel: {
        empty: {
            padding: '22px 0',
            color: '{text.mutedColor}',
            fontSize: '11px',
        },
        row: {
            gap: '9px',
            padding: '8px 12px',
            background: 'transparent',
            color: '{text.color}',
            borderTopWidth: '1px',
            borderTopColor: '{content.borderColor}',
            hover: {
                background: '{content.hoverBackground}',
            },
        },
        identity: {
            gap: '2px',
        },
        name: {
            fontSize: '11.5px',
            fontWeight: '700',
        },
        figures: {
            gap: '5px',
            fontFamily: '{font.mono}',
            fontSize: '9.5px',
        },
        offer: {
            color: '{text.color}',
            fontWeight: '700',
        },
        separator: {
            color: '{text.mutedColor}',
        },
        ask: {
            color: '{text.mutedColor}',
        },
        state: {
            padding: '2px 7px',
            borderRadius: '5px',
            fontSize: '8.5px',
            fontWeight: '800',
            letterSpacing: '0.05em',
            countered: {
                background: 'color-mix(in srgb, var(--p-status-warning) 18%, transparent)',
                color: '{status.warning}',
            },
            pending: {
                background: 'color-mix(in srgb, var(--p-status-info) 16%, transparent)',
                color: '{status.info}',
            },
        },
        chevron: {
            fontSize: '10px',
            color: '{text.mutedColor}',
        },
    },

    mobileAppBanner: {
        gap: '10px',
        padding: '10px 12px',
        borderRadius: '{radius.card}',
        background: '{primary.400}',
        glyph: {
            borderRadius: '8px',
            background: 'color-mix(in srgb, var(--p-primary-950) 12%, transparent)',
            color: '{primary.950}',
            fontSize: '14px',
        },
        copy: {
            gap: '1px',
        },
        message: {
            color: '{primary.950}',
            fontSize: '13px',
            fontWeight: '800',
            lineHeight: '1.25',
        },
        // The second line qualifies the offer, so it is the same ink at less
        // weight rather than a second colour — there is no muted token that
        // holds up on a mint fill.
        caption: {
            color: 'color-mix(in srgb, var(--p-primary-950) 78%, transparent)',
            fontSize: '11px',
            lineHeight: '1.35',
        },
        cta: {
            padding: '7px 13px',
            borderRadius: '999px',
            // Bright green + contrast (white in light) — design home-mobile CTA,
            // not the inverted dark+mint pill that read heavy on dark chrome.
            background: '{primary.color}',
            color: '{primary.contrast.color}',
            fontSize: '11.5px',
            fontWeight: '700',
        },
    },

    sponsorBanner: {
        background: '{content.hoverBackground}',
        color: '{text.color}',
        borderRadius: '{radius.card}',
        padding: '16px 20px',
        gap: '16px',
        hatchBackground:
            'repeating-linear-gradient(135deg, color-mix(in srgb, var(--p-text-color) 5%, transparent), '
            + 'color-mix(in srgb, var(--p-text-color) 5%, transparent) 9px, transparent 9px, transparent 18px)',
        copy: {
            gap: '6px',
        },
        sponsor: {
            color: '{text.mutedColor}',
            fontSize: '9px',
            fontWeight: '800',
            letterSpacing: '0.1em',
        },
        headline: {
            color: '{text.color}',
            fontFamily: '{font.display}',
            fontSize: '17px',
            fontWeight: '{display.weight}',
            letterSpacing: '{display.tracking}',
        },
        cta: {
            padding: '7px 13px',
            borderRadius: '7px',
            background: '{primary.color}',
            color: '{primary.contrastColor}',
            fontSize: '11.5px',
            fontWeight: '700',
        },
        slotSize: {
            fontFamily: '{font.mono}',
            fontSize: '10px',
            color: '{text.mutedColor}',
        },
    },

    trackedSaleRow: {
        background: '{content.background}',
        color: '{text.color}',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        borderRadius: '{radius.row}',
        padding: '10px 12px',
        gap: '11px',
        compactPadding: '5px 12px',
        hover: {
            background: '{content.hoverBackground}',
        },
        glyph: {
            borderRadius: '8px',
            background: '{content.hoverBackground}',
            color: '{text.mutedColor}',
            fontSize: '13px',
        },
        identity: {
            gap: '2px',
        },
        name: {
            fontSize: '13px',
            fontWeight: '700',
            letterSpacing: '-0.01em',
        },
        where: {
            fontSize: '10px',
            color: '{text.mutedColor}',
        },
        type: {
            gap: '3px',
            padding: '1px 5px',
            borderRadius: '4px',
            fontSize: '8px',
            fontWeight: '800',
            letterSpacing: '0.04em',
            icon: {
                fontSize: '8px',
            },
            simulcast: {
                background: 'color-mix(in srgb, var(--p-status-success) 15%, transparent)',
                color: '{status.success}',
            },
            automated: {
                background: 'color-mix(in srgb, var(--p-status-warning) 18%, transparent)',
                color: '{status.warning}',
            },
            timed: {
                background: 'color-mix(in srgb, var(--p-status-info) 16%, transparent)',
                color: '{status.info}',
            },
        },
        when: {
            fontFamily: '{font.mono}',
            fontSize: '9.5px',
            fontWeight: '700',
            color: '{text.mutedColor}',
        },
        counts: {
            fontSize: '10px',
            color: '{text.mutedColor}',
        },
        separator: {
            color: '{text.mutedColor}',
        },
        myList: {
            color: '{primary.color}',
            fontWeight: '700',
        },
        chevron: {
            fontSize: '10px',
            color: '{text.mutedColor}',
        },
    },
};

export default componentTokens;

/**
 * The component tokens that genuinely differ per color scheme.
 *
 * Spread into `extend.colorScheme.{light,dark}` by index.mjs, which is the only
 * place PrimeVue honors a scheme split — see the header above for what happens
 * when one is nested inside a block instead. The variable names are the same as
 * the flat block's would be, because `colorScheme`, `light` and `dark` leave no
 * trace in a variable name: `--p-filter-row-count-color`, declared twice, once
 * per scheme selector.
 *
 * Keyed by component here rather than written out in index.mjs so a component's
 * tokens stay in the file named for them, and so the block above can point at
 * this one by name.
 *
 * BOTH SCHEMES MUST BE WRITTEN. A token declared in one and not the other
 * inherits whatever the other scheme's cascade leaves behind, which is a defect
 * the Design Tokens page shows side by side.
 */
export const componentTokensByColorScheme = {
    // FilterRow's accent — a local patch over a theme-level shortfall rather
    // than a component decision.
    //
    // `{primary.color}` resolves to `{primary.700}` (#1c8a62) in light, which
    // measures 4.31:1 on white: under the 4.5:1 axe holds the 15px count to, and
    // under it again the other way round as a chip fill beneath white ink. So
    // the reference is scheme-aware and still wrong in one scheme, which is the
    // one case the rule above does not cover. `{primary.800}` (#166b4d) measures
    // 6.47:1 in both roles.
    //
    // MessageBubble's seller label is the same shortfall on the warning hue:
    // light's `{status.warning}` (#b45309) measures 4.32:1 on the `them` bubble
    // at 11px, so light mixes it a step toward the text ink. Dark keeps the
    // warning itself, which is the design. Its Counter button is
    // OfferHistoryList's case: `{surface.700}` is the handoff's neutral button
    // in dark and the same dark grey as the text ink in light, which left the
    // label unreadable. Light steps it to `{surface.200}`.
    //
    // DELETE THIS WHEN THE THEME IS FIXED. The real repair is moving light's
    // `primary.color` down a step in index.mjs — which every component painting
    // the accent on white shares, and which is more than one line, since
    // `hoverColor` and `activeColor` sit under it and must step down with it.
    //
    // OfferHistoryList's ground, the other kind: a step deeper than the card it
    // sits in, which no semantic token names. The surface ramp is not a
    // scheme-aware ladder — light's `surface.950` is the same near-black as
    // dark's — so `{surface.950}` alone painted a black list on a white sheet.
    // Dark takes the frame's `#0a0a0a` (`{surface.950}`, the token map's page
    // ground); light takes `{surface.50}`, one step off the white card.
    // SaleDeskNoticesSheet's list group is the same ground in the same kind of
    // sheet, and takes the same split.
    //
    // OfferStatePill's ink is MessageBubble's seller-label case twice over: on
    // its own 14% tint in light, `{status.warning}` and `{status.success}` fall
    // under 4.5:1 at 11px, so light mixes both 70% toward the text ink. Dark
    // keeps the tones themselves, which is the design. OfferCard's Counter
    // hover takes the same scheme split as MessageBubble's Counter fill.
    //
    // AttentionItem's money ink is the same shortfall on the card's own ground:
    // light's `{status.success}` measures 4.32:1 on white, under the 4.5:1 its
    // 10.5px eyebrow needs, so light mixes it 70% toward the text ink, OfferCard's
    // recipe. The other three tones clear 5:1 in light. Dark keeps the tone.
    //
    // PurchaseStatusChip's ink is OfferStatePill's case at 16%: on its own tint
    // over a white card, light's `{status.success}` measures 3.53:1 and
    // `{status.warning}` 4.03:1, under the 4.5:1 its 10.5px word needs. Light
    // mixes both 70% toward the text ink, for 4.78:1 and 5.33:1. Dark keeps the
    // tones, at 7.65:1 and 7.41:1 on their tints over the card.
    //
    // PurchaseCard's secondary hover is OfferCard's Counter hover: a step up the
    // ramp from the raised ground in dark, and a step down it in light, where
    // `{surface.700}` is the text ink itself.
    //
    // DiscussedAssetBanner's accepted state is AttentionItem's money case: its
    // 11px words sit on the white card, where light's `{status.success}` measures
    // 4.31:1, so light takes the same 70% mix. Its other tones clear 4.5:1 on
    // white in light. Dark keeps the tone.
    //
    // AssetBidTile's no-sale wash is a different kind again, and not a
    // shortfall. The won and lost tiles mix a 14% wash out of their own
    // `{status.*}` ink, and the no-sale tile does the same out of
    // `{text.mutedColor}` — except that dark's muted ink is itself half
    // transparent (`rgba(235, 235, 245, 0.50)`), so a 14% mix of it lands at 7%
    // and the tile loses its edge against the card. Dark takes 28% to reach the
    // step light's 14% reaches. The INK stays flat, in the block above:
    // `{text.mutedColor}` is already scheme-aware, which is what the rule at the
    // top of this block is for.
    light: {
        // The "Buy now" label sits on the band's mint wash at 18px bold, which is
        // not large text, so it needs 4.5:1. `{primary.color}` there is 4.01:1 in
        // light (see `bandPrice` above); the filter row's darker step clears it.
        assetBidTile: {
            noSale: { background: 'color-mix(in srgb, var(--p-text-muted-color) 14%, transparent)' },
        },
        assetCard: {
            bandPriceLabel: { color: '{primary.800}' },
        },
        attentionItem: {
            money: { color: 'color-mix(in srgb, var(--p-status-success) 70%, var(--p-text-color))' },
        },
        discussedAssetBanner: {
            offer: { success: { color: 'color-mix(in srgb, var(--p-status-success) 70%, var(--p-text-color))' } },
        },
        filterRow: {
            count: { color: '{primary.800}' },
            chip: { background: '{primary.800}' },
        },
        // The hero's mint FILL, which index.mjs says in as many words belongs
        // in a component override rather than in `primary.color`, and its
        // shadow — the preset has no shadow scale, and inventing a brand-wide
        // one while building a single hero would be deciding it sideways.
        featuredAssetHero: {
            boxShadow: '0 4px 8px rgba(15, 23, 42, 0.06), 0 12px 24px rgba(15, 23, 42, 0.10)',
            research: {
                background: '{primary.500}',
                hover: { background: '{primary.600}' },
            },
        },
        offerHistoryList: { background: '{surface.50}' },
        offerCard: {
            action: { secondary: { hover: { background: '{surface.200}' } } },
        },
        offerStatePill: {
            warning: { color: 'color-mix(in srgb, var(--p-status-warning) 70%, var(--p-text-color))' },
            success: { color: 'color-mix(in srgb, var(--p-status-success) 70%, var(--p-text-color))' },
        },
        messageBubble: {
            offerLabel: { color: 'color-mix(in srgb, var(--p-status-warning) 80%, var(--p-text-color))' },
            offerAction: {
                counter: { background: '{surface.200}', hover: { background: '{surface.300}' } },
            },
        },
        purchaseCard: {
            action: { secondary: { hover: { background: '{surface.200}' } } },
        },
        purchaseStatusChip: {
            success: { color: 'color-mix(in srgb, var(--p-status-success) 70%, var(--p-text-color))' },
            warning: { color: 'color-mix(in srgb, var(--p-status-warning) 70%, var(--p-text-color))' },
        },
        saleDeskNoticesSheet: { list: { background: '{surface.50}' } },
    },
    dark: {
        assetBidTile: {
            noSale: { background: 'color-mix(in srgb, var(--p-text-muted-color) 28%, transparent)' },
        },
        assetCard: {
            bandPriceLabel: { color: '{primary.color}' },
        },
        attentionItem: {
            money: { color: '{status.success}' },
        },
        discussedAssetBanner: {
            offer: { success: { color: '{status.success}' } },
        },
        filterRow: {
            count: { color: '{primary.color}' },
            chip: { background: '{primary.color}' },
        },
        featuredAssetHero: {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.45), 0 12px 24px rgba(0, 0, 0, 0.55)',
            research: {
                background: '{primary.400}',
                hover: { background: '{primary.300}' },
            },
        },
        offerHistoryList: { background: '{surface.950}' },
        offerCard: {
            action: { secondary: { hover: { background: '{surface.700}' } } },
        },
        offerStatePill: {
            warning: { color: '{status.warning}' },
            success: { color: '{status.success}' },
        },
        messageBubble: {
            offerLabel: { color: '{status.warning}' },
            offerAction: {
                counter: { background: '{surface.700}', hover: { background: '{surface.600}' } },
            },
        },
        purchaseCard: {
            action: { secondary: { hover: { background: '{surface.700}' } } },
        },
        purchaseStatusChip: {
            success: { color: '{status.success}' },
            warning: { color: '{status.warning}' },
        },
        saleDeskNoticesSheet: { list: { background: '{surface.950}' } },
    },
};
